import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'country-flag-icons/react/3x2';
import { 
  Camera, Upload, Save, Share2, Phone, Mail, Globe, 
  Linkedin, Twitter, Instagram, Github, Edit3, Eye, 
  X, Check, User, MapPin, Briefcase, Lock, LogIn, AlertCircle, 
  Plus, Trash2, ArrowLeft, Users, ExternalLink, RefreshCw,
  Download, FileText, Calendar, Video, Music, ShoppingCart, 
  Link as LinkIcon, Youtube, Facebook, MessageCircle, Sun, Moon,
  ChevronUp, ChevronDown, GripVertical, Settings,
  Rss, DollarSign, GalleryHorizontal, Hash, BarChart2,
  UserPlus, ClipboardCopy,
  Store, Tag, CreditCard, WalletCards, Package, Gem, Crown, Star, Award, Trophy,
  Headphones, Gamepad2, Palette, Pipette,
  Bot, Cpu, MousePointer2, Search, Zap,
  Map, Navigation, Home, Building2,
  Coffee, Utensils, Dumbbell, Car, Plane, Heart, Shirt, ShoppingBag,
  BookOpen, GraduationCap, Ticket, Bell, Send,
  AlignLeft
} from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove } from '@dnd-kit/sortable';
import HeroOverlay from './components/HeroOverlay';
import BlockVideo from './blocks/BlockVideo';
import BlockMediaList from './blocks/BlockMediaList';

const API_ENDPOINT = '/api';
const APP_VERSION = require('../package.json').version; // Automatically read from package.json
const GITHUB_URL = 'https://github.com/MrCrin/swiish';
const { resolveCardBg, isHexColor, parseVideoUrl, hexToRgba, FONT_PAIRS, resolveFontPair } = require('./lib/cardStyle');

// Try to read branch info from active-branch.json (generated at build time)
let GIT_BRANCH = null;
try {
  const branchInfo = require('./active-branch.json');
  GIT_BRANCH = branchInfo.branch;
} catch (e) {
  // active-branch.json doesn't exist yet (first run before build)
  GIT_BRANCH = null;
}
const swiishTheme         = require('./theme/swiish');
const minimalTheme        = require('./theme/minimal');
const corporateNavyTheme  = require('./theme/corporate-navy');
const corporateSlateTheme = require('./theme/corporate-slate');
const corporateCharcoalTheme = require('./theme/corporate-charcoal');
const corporateWarmTheme  = require('./theme/corporate-warm');
const praiaTropicalTheme  = require('./theme/praia-tropical');
const praiaSunsetTheme    = require('./theme/praia-sunset');
const praiaBrisaTheme     = require('./theme/praia-brisa');
const anrvaInfoTheme      = require('./theme/anrva-info');
const pousadaTheme        = require('./theme/pousada');
const imoveisTheme        = require('./theme/imoveis');
const comercialTheme      = require('./theme/comercial');
const obraCancaoTheme     = require('./theme/obra-cancao');
import BackgroundEffect from './BackgroundEffect';
const THEME_FILES = {
  swiish:               swiishTheme,
  minimal:              minimalTheme,
  'corporate-navy':     corporateNavyTheme,
  'corporate-slate':    corporateSlateTheme,
  'corporate-charcoal': corporateCharcoalTheme,
  'corporate-warm':     corporateWarmTheme,
  'praia-tropical':     praiaTropicalTheme,
  'praia-sunset':       praiaSunsetTheme,
  'praia-brisa':        praiaBrisaTheme,
  'anrva-info':         anrvaInfoTheme,
  pousada:              pousadaTheme,
  imoveis:              imoveisTheme,
  comercial:            comercialTheme,
  'obra-cancao':        obraCancaoTheme,
};
// Mutable runtime themes — starts with built-ins, extended with custom uploaded themes
const RUNTIME_THEMES = { ...THEME_FILES };

// Metadata shown in the per-card theme picker (built-in entries)
const BUILTIN_THEME_META = [
  { id: 'swiish',               displayName: 'ANRVA Padrão',     swatchColors: ['#0d1b2e', '#0891b2', '#f5f5f4'],   group: 'geral',        presetBg: { type: 'custom', url: '/theme-bgs/swiish.jpg' } },
  { id: 'minimal',              displayName: 'Minimal',           swatchColors: ['#1c1917', '#ffffff', '#a3a3a3'],   group: 'geral',        presetBg: { type: 'solid',  value: '#ffffff' } },
  { id: 'corporate-navy',       displayName: 'Navy Corporativo',  swatchColors: ['#0d1b36', '#c9a84c', '#f4f1e8'],   group: 'corporativo',  presetBg: { type: 'custom', url: '/theme-bgs/corporate-navy.jpg' } },
  { id: 'corporate-slate',      displayName: 'Slate Tech',        swatchColors: ['#0f172a', '#2563eb', '#f1f5f9'],   group: 'corporativo',  presetBg: { type: 'custom', url: '/theme-bgs/corporate-slate.jpg' } },
  { id: 'corporate-charcoal',   displayName: 'Charcoal Finance',  swatchColors: ['#111110', '#34d399', '#fafaf9'],   group: 'corporativo',  presetBg: { type: 'custom', url: '/theme-bgs/corporate-charcoal.jpg' } },
  { id: 'corporate-warm',       displayName: 'Warm Profissional', swatchColors: ['#1c0a0a', '#881337', '#fdf8f6'],   group: 'corporativo',  presetBg: { type: 'custom', url: '/theme-bgs/corporate-warm.jpg' } },
  { id: 'praia-tropical',       displayName: 'Tropical',          swatchColors: ['#0a2020', '#22d3ee', '#fdf6e3'],   group: 'praia',        presetBg: { type: 'custom', url: '/theme-bgs/praia-tropical.jpg' } },
  { id: 'praia-sunset',         displayName: 'Sunset',            swatchColors: ['#180800', '#fb923c', '#fff8f0'],   group: 'praia',        presetBg: { type: 'custom', url: '/theme-bgs/praia-sunset.jpg' } },
  { id: 'praia-brisa',          displayName: 'Brisa do Mar',      swatchColors: ['#071a2e', '#38bdf8', '#f0f9ff'],   group: 'praia',        presetBg: { type: 'custom', url: '/theme-bgs/praia-brisa.jpg' } },
  { id: 'anrva-info',           displayName: 'ANRVA Info',        swatchColors: ['#04090f', '#e85d04', '#f0f8ff'],   group: 'anrva',        presetBg: { type: 'custom', url: '/theme-bgs/anrva-info.jpg' } },
  { id: 'pousada',              displayName: 'Pousada / Hotel',   swatchColors: ['#1a1208', '#a07850', '#fdf6ee'],   group: 'hospitalidade', presetBg: { type: 'custom', url: '/theme-bgs/pousada.jpg' } },
  { id: 'imoveis',              displayName: 'Imobiliária',       swatchColors: ['#0d1f12', '#2d6a4f', '#f4f9f5'],   group: 'negocios',     presetBg: { type: 'custom', url: '/theme-bgs/imoveis.jpg' } },
  { id: 'comercial',            displayName: 'Comércio',          swatchColors: ['#1a0d00', '#c0392b', '#fff8f5'],   group: 'negocios',     presetBg: { type: 'custom', url: '/theme-bgs/comercial.jpg' } },
  { id: 'obra-cancao',          displayName: 'Obra e Canção',    swatchColors: ['#191410', '#e0a45c', '#f3ecdf'],   group: 'criativo',     presetBg: { type: 'gradient', from: '#191410', to: '#2a1c10', angle: 160 } },
];

const THEME_PRESETS = {
  swiish: [
    { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-cyan-600 bg-cyan-50 border-indigo-100 hover:bg-cyan-100", text: "text-cyan-600" },
    { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
    { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
    { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
    { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
  ],
  minimal: [
    { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
    { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
    { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
  ]
};

// Define applyThemeCssVars outside component to ensure it's always available
// This function sets all CSS custom properties from the selected theme file
const applyThemeCssVars = (variant, fontPairOverride) => {
  const root = document.documentElement;
  const theme = RUNTIME_THEMES[variant] || THEME_FILES[variant] || swiishTheme;
  const colors = theme.colors || {};
  
  // Helper to convert camelCase to kebab-case
  const toKebabCase = (str) => str.replace(/([A-Z])/g, (match) => '-' + match).toLowerCase();
  
  // Set all color CSS variables from theme
  Object.keys(colors).forEach((colorKey) => {
    const colorValue = colors[colorKey];
    if (colorValue && typeof colorValue === 'object' && colorValue.light !== undefined) {
      const cssVarName = `--color-${toKebabCase(colorKey)}`;
      root.style.setProperty(`${cssVarName}-light`, colorValue.light);
      root.style.setProperty(`${cssVarName}-dark`, colorValue.dark);
    }
  });
  
  // Set texture variables
  const textures = theme.textures?.main || {};
  root.style.setProperty('--texture-main-light', textures.light ? `url(${textures.light})` : 'none');
  root.style.setProperty('--texture-main-dark', textures.dark ? `url(${textures.dark})` : 'none');
  root.style.setProperty('--texture-main-size', textures.size || '540px 540px');
  root.style.setProperty('--texture-main-blend-light', textures.blendLight || 'multiply');
  root.style.setProperty('--texture-main-blend-dark', textures.blendDark || 'overlay');
  root.style.setProperty('--texture-main-opacity-light', textures.opacityLight ?? 0.08);
  root.style.setProperty('--texture-main-opacity-dark', textures.opacityDark ?? 0.1);
  // Expose theme effect for BackgroundEffect component
  root.dataset.themeEffect = theme.effect || 'none';

  // Tipografia (M2): override por-cartão vence; senão o par declarado no arquivo de tema; senão nenhum.
  const pairKey = fontPairOverride || (theme.fonts && theme.fonts.pair) || 'system';
  const pair = resolveFontPair(pairKey);
  if (pair.display) root.style.setProperty('--font-display', pair.display);
  else root.style.removeProperty('--font-display');
  if (pair.body) root.style.setProperty('--font-body', pair.body);
  else root.style.removeProperty('--font-body');
};

// --- QR PAYLOAD STORAGE KEYS ---
const QR_STORAGE_KEY = 'swiish:lastQrPayload';

// Build a vCard string for QR code encoding (simplified for reliable scanning)
// When scanned, this will add the contact directly to the phone
const buildQrPayload = (cardUrl, data) => {
  const { personal = {}, contact = {} } = data || {};

  const safe = (v, maxLen = 120) => sanitizeText(v || '').substring(0, maxLen);
  
  const firstName = safe(personal.firstName || '', 40);
  const lastName = safe(personal.lastName || '', 40);
  const fullName = `${firstName} ${lastName}`.trim();
  const company = safe(personal.company || '', 80);
  const email = safe(contact.email || '', 120);
  const phone = safe(contact.phone || '', 50);


  // Build vCard 3.0 format (minimal for QR scanning reliability)
  let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
  
  if (fullName) {
    vcard += `FN:${fullName}\n`;
    vcard += `N:${lastName};${firstName};;;\n`;
  }
  
  if (company) {
    vcard += `ORG:${company}\n`;
  }
  
  if (email) {
    vcard += `EMAIL;TYPE=WORK:${email}\n`;
  }
  
  if (phone) {
    vcard += `TEL;TYPE=CELL:${phone}\n`;
  }
  
  if (cardUrl) {
    vcard += `URL:${cardUrl}\n`;
  }
  
  vcard += 'END:VCARD';

  return vcard;
};

const saveQrPayloadToStorage = (payload) => {
  try {
    if (!payload) return;
    const record = {
      payload,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('Failed to save QR payload', e);
  }
};

const loadQrPayloadFromStorage = () => {
  try {
    const raw = localStorage.getItem(QR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.payload || null;
  } catch (e) {
    console.warn('Failed to load QR payload', e);
    return null;
  }
};

// sanitizeText / sanitizeHTML vivem em ./lib/sanitize (reutilizável entre projetos)
const { sanitizeText, sanitizeHTML } = require('./lib/sanitize');

// ============================================================
// BRAND ICON SVGs (not available in lucide-react)
// ============================================================

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.28 8.28 0 0 0 4.84 1.54V6.78a4.85 4.85 0 0 1-1.07-.09z"/>
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TwitterXIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.056a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

const TwitchIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
  </svg>
);

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const SnapchatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.065.026C9.24.026 6.898 1.2 5.38 3.26c-.963 1.318-1.17 2.659-1.17 3.977 0 .422.032.843.071 1.257l-.318.139c-.388.17-.786.256-1.183.256-.441 0-.834-.1-1.171-.299a.468.468 0 0 0-.238-.065c-.323 0-.598.24-.636.561-.05.405.249.766.626.822.068.01.142.017.217.017.356 0 .725-.092 1.099-.273.198-.097.392-.21.581-.337-.043.578-.025.956-.025.956l-.012.114C2.975 11.81 1.5 12.72 1.5 13.5c0 .453.3.852.9 1.122.466.207 1.052.333 1.748.374.261.51.613 1.32.613 1.32s.132.305-.167.459c-.4.204-1.018.363-1.773.424-.28.022-.624.092-.624.456 0 .308.244.503.586.503.055 0 .114-.006.177-.018.508-.097 1.225-.14 1.818.066.455.158.825.501 1.208.856.714.655 1.526 1.398 3.054 1.398.176 0 .36-.012.553-.037.454-.059.87-.22 1.273-.375.535-.203 1.04-.395 1.635-.395.594 0 1.099.192 1.634.395.403.153.819.316 1.273.375.193.025.377.037.553.037 1.528 0 2.34-.743 3.054-1.398.383-.355.753-.698 1.208-.856.593-.206 1.31-.163 1.818-.066.063.012.122.018.177.018.342 0 .586-.195.586-.503 0-.364-.344-.434-.624-.456-.755-.061-1.373-.22-1.773-.424-.299-.154-.167-.459-.167-.459s.352-.81.613-1.32c.696-.041 1.282-.167 1.748-.374.6-.27.9-.669.9-1.122 0-.78-1.475-1.69-1.921-2.891l-.012-.114s.018-.378-.025-.956c.189.127.383.24.581.337.374.181.743.273 1.099.273.075 0 .149-.007.217-.017.377-.056.676-.417.626-.822-.038-.321-.313-.561-.636-.561a.468.468 0 0 0-.238.065c-.337.199-.73.299-1.171.299-.397 0-.795-.086-1.183-.256l-.318-.139c.039-.414.071-.835.071-1.257 0-1.318-.207-2.659-1.17-3.977C17.166 1.2 14.824.026 12 .026h.065z"/>
  </svg>
);

const MediumIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
  </svg>
);

const BehanceIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.938 4.503c.702 0 1.34.06 1.92.188.577.13 1.07.33 1.485.61.41.28.733.65.96 1.12.224.47.34 1.05.34 1.73 0 .74-.17 1.36-.507 1.86-.338.5-.837.9-1.502 1.22.906.26 1.576.72 2.022 1.37.448.66.665 1.45.665 2.36 0 .75-.13 1.39-.39 1.93-.26.54-.623 1-.1 1.37-.48.37-1.03.64-1.64.81-.61.17-1.26.25-1.95.25H0V4.51h6.938zm-.34 4.63c.55 0 .99-.13 1.32-.39.33-.26.49-.65.49-1.17 0-.27-.05-.5-.14-.68a1.1 1.1 0 00-.38-.44 1.6 1.6 0 00-.56-.23 3.1 3.1 0 00-.69-.07H3.14v2.98h3.46zm.19 4.89c.27 0 .52-.03.76-.09.24-.06.45-.16.63-.3.18-.14.32-.32.43-.55.1-.23.16-.52.16-.85 0-.67-.19-1.16-.57-1.47-.38-.31-.9-.46-1.54-.46H3.14v3.72h3.65zm9.13-8.56h5.55v1.5h-5.55V5.47zm5.63 6.56c0 .96-.2 1.8-.6 2.52-.39.72-.92 1.32-1.58 1.79-.66.47-1.42.81-2.27 1.02-.85.21-1.72.31-2.6.31-1.01 0-1.95-.15-2.82-.45-.87-.3-1.63-.74-2.27-1.31-.64-.57-1.15-1.27-1.51-2.1-.36-.83-.54-1.78-.54-2.85 0-1.03.18-1.95.53-2.76.35-.81.84-1.5 1.47-2.07.63-.57 1.38-.99 2.24-1.29.86-.3 1.79-.45 2.79-.45.93 0 1.77.14 2.52.42.75.28 1.39.68 1.93 1.19.54.51.95 1.13 1.24 1.87.29.74.43 1.55.43 2.44v.72H16.8c.07.78.38 1.37.92 1.79.54.42 1.2.63 1.98.63.59 0 1.09-.12 1.49-.37.4-.25.72-.57.94-.97h3.21zm-3.27-2.41c-.04-.66-.27-1.21-.7-1.64-.43-.43-.99-.65-1.69-.65-.39 0-.73.07-1.01.2-.28.13-.52.3-.71.51-.19.21-.34.44-.44.7-.1.26-.16.53-.18.88h4.73z"/>
  </svg>
);

const DribbbleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.19-7.77l-.066.02c-5.79 2.015-7.86 6.025-8.04 6.4 1.73 1.358 3.92 2.166 6.29 2.166 1.42 0 2.77-.29 4-.816zm-11.62-2.073c.232-.296 3.188-3.825 8.816-5.497.186-.048.377-.094.567-.138-.36-.812-.755-1.646-1.167-2.378C7.234 13 2.8 13.06 2.48 13.06c-.018 0-.18 0-.18-.012zm-.164-1.63h.208c.39 0 4.494-.025 9.137-1.86-.52-1.028-1.07-2.02-1.67-2.98C8.22 8.12 5.4 9.42 5.18 9.53L3.22 13.72zM9.6 4.8c.616 1.004 1.187 2.04 1.72 3.09 2.59-.906 3.674-2.225 3.8-2.38C13.926 4.08 12.9 3.576 11.78 3.276L9.6 4.8zM6.63 6.3c.22-.11 3.1-1.49 6.83-2.2-.2-.453-.417-.896-.646-1.33C9.38 4.12 6.93 6 6.63 6.3zM12 2.4c-.836 0-1.65.12-2.42.34.24.437.47.88.69 1.33C13.28 3.43 14.85 2.4 12 2.4zm1.27 12.4c1.88.625 3.523 1.5 4.9 2.26.065-.18.122-.363.175-.55-.15-.12-.3-.24-.44-.362-1.36-1.098-2.69-2.064-3.9-2.876-.24.52-.48 1.05-.73 1.528z"/>
  </svg>
);

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GoogleMapsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const FigmaIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 5.5A3.5 3.5 0 018.5 2H12v7H8.5A3.5 3.5 0 015 5.5zM12 2h3.5a3.5 3.5 0 110 7H12V2zM12 10.5H8.5a3.5 3.5 0 100 7H12v-7zM12 10.5h3.5a3.5 3.5 0 110 7H12v-7zM8.5 21a3.5 3.5 0 113.5-3.5V21H8.5z"/>
  </svg>
);

const NotionIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"/>
  </svg>
);

const SlackIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
);

const YoutubeShortIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

// ============================================================
// ICONS MAPPING & PICKER
// ============================================================

const ICON_MAP = {
  // Redes Sociais
  whatsapp:   WhatsAppIcon,
  instagram:  InstagramIcon,
  linkedin:   LinkedInIcon,
  github:     Github,
  facebook:   Facebook,
  youtube:    YoutubeShortIcon,
  twitter:    TwitterXIcon,
  tiktok:     TikTokIcon,
  telegram:   TelegramIcon,
  discord:    DiscordIcon,
  spotify:    SpotifyIcon,
  reddit:     RedditIcon,
  twitch:     TwitchIcon,
  pinterest:  PinterestIcon,
  snapchat:   SnapchatIcon,
  medium:     MediumIcon,
  behance:    BehanceIcon,
  dribbble:   DribbbleIcon,
  // Comunicação
  email:      Mail,
  phone:      Phone,
  send:       Send,
  bell:       Bell,
  // Negócios & Loja
  shop:       ShoppingCart,
  bag:        ShoppingBag,
  store:      Store,
  dollar:     DollarSign,
  tag:        Tag,
  credit:     CreditCard,
  wallet:     WalletCards,
  package:    Package,
  briefcase:  Briefcase,
  gem:        Gem,
  crown:      Crown,
  star:       Star,
  award:      Award,
  trophy:     Trophy,
  // Mídia & Criação
  camera:     Camera,
  video:      Video,
  music:      Music,
  headphones: Headphones,
  gamepad:    Gamepad2,
  palette:    Palette,
  figma:      FigmaIcon,
  // Tecnologia & IA
  bot:        Bot,
  cpu:        Cpu,
  cursor:     MousePointer2,
  search:     Search,
  zap:        Zap,
  google:     GoogleIcon,
  apple:      AppleIcon,
  notion:     NotionIcon,
  slack:      SlackIcon,
  // Localização
  location:   MapPin,
  map:        Map,
  navigation: Navigation,
  google_maps:GoogleMapsIcon,
  home:       Home,
  building:   Building2,
  // Estilo de Vida
  coffee:     Coffee,
  utensils:   Utensils,
  dumbbell:   Dumbbell,
  car:        Car,
  plane:      Plane,
  heart:      Heart,
  shirt:      Shirt,
  // Geral
  link:       LinkIcon,
  download:   Download,
  file:       FileText,
  calendar:   Calendar,
  globe:      Globe,
  book:       BookOpen,
  graduation: GraduationCap,
  ticket:     Ticket,
  rss:        Rss,
};

const ICON_CATEGORIES = [
  { label: 'Redes Sociais',    keys: ['whatsapp','instagram','linkedin','github','facebook','youtube','twitter','tiktok','telegram','discord','spotify','reddit','twitch','pinterest','snapchat','medium','behance','dribbble'] },
  { label: 'Comunicação',      keys: ['email','phone','send','bell'] },
  { label: 'Negócios & Loja', keys: ['shop','bag','store','dollar','tag','credit','wallet','package','briefcase','gem','crown','star','award','trophy'] },
  { label: 'Mídia & Criação',  keys: ['camera','video','music','headphones','gamepad','palette','figma'] },
  { label: 'Tecnologia & IA',  keys: ['bot','cpu','cursor','search','zap','google','apple','notion','slack'] },
  { label: 'Localização',      keys: ['location','map','navigation','google_maps','home','building'] },
  { label: 'Estilo de Vida',   keys: ['coffee','utensils','dumbbell','car','plane','heart','shirt'] },
  { label: 'Geral',            keys: ['link','download','file','calendar','globe','book','graduation','ticket','rss'] },
];

function IconPicker({ selected, onChange }) {
  return (
    <div className="max-h-56 overflow-y-auto space-y-2.5 pr-0.5 scrollbar-thin">
      {ICON_CATEGORIES.map(cat => (
        <div key={cat.label}>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-text-muted dark:text-text-muted-dark mb-1.5 select-none">{cat.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {cat.keys.map(iconKey => {
              const Ic = ICON_MAP[iconKey];
              if (!Ic) return null;
              const isActive = selected === iconKey;
              return (
                <button
                  key={iconKey}
                  type="button"
                  onClick={() => onChange(iconKey)}
                  title={iconKey.replace(/_/g, ' ')}
                  className={`p-2 rounded-button border flex-shrink-0 transition-all ${
                    isActive
                      ? 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-300'
                      : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-muted-subtle dark:text-text-muted-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark'
                  }`}
                >
                  <Ic className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// BACKGROUND LIBRARY PRESETS

// ============================================================
// BACKGROUND LIBRARY PRESETS
// ============================================================
const BG_PRESETS = [
  // --- Gradientes modernos ---
  { id: 'gradient-anrva',    category: 'gradient', label: 'ANRVA',      style: { background: 'linear-gradient(135deg, #0d1b2e 0%, #0e7490 100%)' } },
  { id: 'gradient-ocean',    category: 'gradient', label: 'Oceano',     style: { background: 'linear-gradient(135deg, #0077B6 0%, #00B4D8 60%, #90E0EF 100%)' } },
  { id: 'gradient-sunset',   category: 'gradient', label: 'Pôr do Sol', style: { background: 'linear-gradient(135deg, #FF6B35 0%, #F7C59F 60%, #EFEFD0 100%)' } },
  { id: 'gradient-cosmic',   category: 'gradient', label: 'Cósmico',    style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } },
  { id: 'gradient-rose-gold',category: 'gradient', label: 'Rose Gold',  style: { background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' } },
  { id: 'gradient-aurora',   category: 'gradient', label: 'Aurora',     style: { background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' } },
  { id: 'gradient-golden',   category: 'gradient', label: 'Dourado',    style: { background: 'linear-gradient(135deg, #F7971E 0%, #FFD200 100%)' } },
  { id: 'gradient-emerald',  category: 'gradient', label: 'Esmeralda',  style: { background: 'linear-gradient(135deg, #134E5E 0%, #71B280 100%)' } },
  { id: 'gradient-royal',    category: 'gradient', label: 'Royal',      style: { background: 'linear-gradient(135deg, #141E30 0%, #243B55 100%)' } },
  // --- Fundos escuros ---
  { id: 'dark-navy',       category: 'dark', label: 'Naval',       style: { background: '#0d1b2e' } },
  { id: 'dark-graphite',   category: 'dark', label: 'Grafite',     style: { background: 'linear-gradient(135deg, #232526 0%, #414345 100%)' } },
  { id: 'dark-midnight',   category: 'dark', label: 'Meia-Noite',  style: { background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 100%)' } },
  { id: 'dark-mocha',      category: 'dark', label: 'Mocha',       style: { background: 'linear-gradient(135deg, #3E2723 0%, #5D4037 100%)' } },
  { id: 'dark-forest',     category: 'dark', label: 'Floresta',    style: { background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)' } },
  { id: 'dark-wine',       category: 'dark', label: 'Vinho',       style: { background: 'linear-gradient(135deg, #4A1942 0%, #6B2D5E 100%)' } },
  // --- Fundos claros ---
  { id: 'light-white',     category: 'light', label: 'Branco',     style: { background: '#ffffff' } },
  { id: 'light-pearl',     category: 'light', label: 'Pérola',     style: { background: 'linear-gradient(135deg, #f8f9fa 0%, #dee2e6 100%)' } },
  { id: 'light-rose',      category: 'light', label: 'Rosa Suave', style: { background: 'linear-gradient(135deg, #fff0f3 0%, #ffc8d3 100%)' } },
  { id: 'light-sky',       category: 'light', label: 'Céu',        style: { background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)' } },
  { id: 'light-cream',     category: 'light', label: 'Creme',      style: { background: 'linear-gradient(135deg, #FFF9C4 0%, #FFECB3 100%)' } },
  { id: 'light-lavender',  category: 'light', label: 'Lavanda',    style: { background: 'linear-gradient(135deg, #EDE7F6 0%, #D1C4E9 100%)' } },
  // --- Texturas suaves ---
  { id: 'texture-dots',    category: 'texture', label: 'Pontos',   style: { backgroundColor: '#f8f9fa', backgroundImage: 'radial-gradient(circle, #b0b8c1 1px, transparent 1px)', backgroundSize: '18px 18px' } },
  { id: 'texture-grid',    category: 'texture', label: 'Grade',    style: { backgroundColor: '#f1f5f9', backgroundImage: 'linear-gradient(rgba(100,116,139,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(100,116,139,.18) 1px, transparent 1px)', backgroundSize: '24px 24px' } },
  { id: 'texture-diagonal',category: 'texture', label: 'Diagonal', style: { backgroundColor: '#fafafa', backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0, #e2e8f0 1px, transparent 0, transparent 50%)', backgroundSize: '16px 16px' } },
  { id: 'texture-carbon',  category: 'texture', label: 'Carbono',  style: { backgroundColor: '#1a1a1a', backgroundImage: 'repeating-linear-gradient(45deg, #2a2a2a 0, #2a2a2a 1px, transparent 0, transparent 50%), repeating-linear-gradient(-45deg, #2a2a2a 0, #2a2a2a 1px, transparent 0, transparent 50%)', backgroundSize: '8px 8px' } },
  { id: 'texture-waves',   category: 'texture', label: 'Ondas',    style: { backgroundColor: '#e0f2fe', backgroundImage: 'repeating-radial-gradient(circle at 0 50%, transparent 0, #e0f2fe 14px), repeating-linear-gradient(#bae6fd55, #bae6fd)' } },
  { id: 'texture-linen',   category: 'texture', label: 'Linho',    style: { backgroundColor: '#f5f0e8', backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(180,160,130,.07) 2px, rgba(180,160,130,.07) 4px), repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(180,160,130,.07) 2px, rgba(180,160,130,.07) 4px)', backgroundSize: '4px 4px' } },
];

// Solid color quick-pick palette
const SOLID_PRESETS = [
  '#ffffff','#f1f5f9','#e2e8f0','#94a3b8','#64748b','#334155','#1e293b','#000000',
  '#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899',
  '#fde68a','#bbf7d0','#bfdbfe','#ddd6fe','#fecaca','#fed7aa','#a7f3d0','#fbcfe8',
];

const CARD_THEME_COLOR_DEFAULTS = {
  avatarBorderColor: '#ffffff',
  shareBackground: '#ffffff',
  sharePanelBackground: '#f1f5f9',
  shareTextColor: '#0f172a',
  shareMutedColor: '#64748b',
  shareAccentColor: '#0891b2',
  shareButtonBackground: '#e2e8f0',
  shareButtonTextColor: '#0f172a',
  shareFooterColor: '#64748b',
};

// --- DATA TEMPLATE ---
const getDefaultTemplate = (settings) => ({
  personal: {
    firstName: "",
    lastName: "",
    title: "",
    company: "",
    bio: "",
    location: ""
  },
  contact: {
    email: "",
    phone: "",
    website: "",
  },
  social: { linkedin: "", twitter: "", instagram: "", github: "" },
  theme: {
    color: "indigo", style: "modern", variant: "swiish", effect: "none", effectColor: "#0891b2", effectOpacity: 60,
    avatarBorderColor: "#ffffff",
    shareBackground: "#ffffff", sharePanelBackground: "#f1f5f9",
    shareTextColor: "#0f172a", shareMutedColor: "#64748b",
    shareAccentColor: "#0891b2", shareButtonBackground: "#e2e8f0",
    shareButtonTextColor: "#0f172a", shareFooterColor: "#64748b", shareHeading: "Compartilhe meu contato",
    shareSimpleLabel: "Somente link", shareRichLabel: "Detalhes completos", shareCloseLabel: "Fechar"
  },
  images: { avatar: null, banner: null, bg: null },
  links: [],
  privacy: {
    requireInteraction: true,  // ON by default
    clientSideObfuscation: false,  // OFF by default
    blockRobots: false  // OFF by default
  },
  blocks: [],
  analytics: { enabled: false, reportEmail: '' },
});

// --- STYLES ---
// Helper functions to get color classes from settings
const getThemeGradient = (colorName, settings) => {
  if (!settings?.theme_colors) return "linear-gradient(135deg, #4f46e5, #7c3aed)";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.gradientStyle || "linear-gradient(135deg, #4f46e5, #7c3aed)";
};

const getButtonColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.buttonStyle || "#4f46e5";
};

const getLinkColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.linkStyle || "#4f46e5";
};

const getTextColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.textStyle || "#4f46e5";
};

// --- COLOR GENERATION UTILITIES ---
const TAILWIND_COLORS = ['indigo', 'blue', 'rose', 'emerald', 'slate', 'purple', 'cyan', 'teal', 'orange', 'pink', 'violet', 'fuchsia', 'amber', 'lime', 'green', 'yellow', 'red'];

const COMPLEMENTARY_MAP = {
  indigo: 'purple',
  blue: 'cyan',
  rose: 'orange',
  emerald: 'teal',
  slate: 'slate',
  purple: 'indigo',
  cyan: 'blue',
  teal: 'emerald',
  orange: 'rose',
  pink: 'fuchsia',
  violet: 'purple',
  fuchsia: 'pink',
  amber: 'orange',
  lime: 'green',
  green: 'emerald',
  yellow: 'amber',
  red: 'rose'
};

const getComplementaryColor = (baseColor) => {
  return COMPLEMENTARY_MAP[baseColor] || 'purple';
};

const getTailwindShades = () => {
  return [400, 500, 600, 700, 800];
};

// Tailwind color to hex mapping (for common shades)
const TAILWIND_TO_HEX = {
  indigo: { 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3' },
  blue: { 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af' },
  rose: { 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239' },
  emerald: { 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46' },
  slate: { 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b' },
  purple: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  cyan: { 400: '#22d3ee', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75' },
  teal: { 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  orange: { 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412' },
  pink: { 400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9f1239' },
  violet: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  fuchsia: { 400: '#f0abfc', 500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f' },
  amber: { 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e' },
  lime: { 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#365314' },
  green: { 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534' },
  yellow: { 400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e' },
  red: { 400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b' }
};

const getTailwindColorHex = (colorName, shade = 600) => {
  return TAILWIND_TO_HEX[colorName]?.[shade] || '#4f46e5';
};

// Utility to darken a hex color
const darkenHex = (hex, percent) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Darken by percent
  const factor = 1 - (percent / 100);
  const newR = Math.max(0, Math.floor(r * factor));
  const newG = Math.max(0, Math.floor(g * factor));
  const newB = Math.max(0, Math.floor(b * factor));
  
  // Convert back to hex
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};


// Extract base color from existing gradient class (for migration)
const extractBaseColorFromGradient = (gradient) => {
  if (!gradient) return null;
  const match = gradient.match(/from-(\w+)-(\d+)/);
  if (match) {
    return { baseColor: match[1], shade: parseInt(match[2]) };
  }
  return null;
};

// Modal Component
function Modal({ isOpen, onClose, type = 'info', title, message, onConfirm, confirmText = 'OK', cancelText = 'Cancel', inputLabel, inputPlaceholder, inputValue, onInputChange }) {
  if (!isOpen) return null;

  const typeStyles = {
    info: { icon: AlertCircle, iconColor: 'text-info-text dark:text-info-text-dark', bgColor: 'bg-info-bg dark:bg-info-bg-dark', borderColor: 'border-info-border dark:border-info-border-dark' },
    success: { icon: Check, iconColor: 'text-success-text dark:text-success-text-dark', bgColor: 'bg-success-bg dark:bg-success-bg-dark', borderColor: 'border-success-border dark:border-success-border-dark' },
    error: { icon: AlertCircle, iconColor: 'text-error-text dark:text-error-text-dark', bgColor: 'bg-error-bg dark:bg-error-bg-dark', borderColor: 'border-error-border dark:border-error-border-dark' },
    confirm: { icon: AlertCircle, iconColor: 'text-text-muted dark:text-text-muted-dark', bgColor: 'bg-surface dark:bg-surface-dark', borderColor: 'border-border dark:border-border-dark' }
  };

  const style = typeStyles[type] || typeStyles.info;
  const Icon = style.icon;
  const hasInput = inputLabel || inputPlaceholder;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    // onClose will handle calling the onClose callback
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && hasInput) {
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card dark:bg-card-dark rounded-card shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-12 h-12 rounded-full ${style.bgColor} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-6 h-6 ${style.iconColor}`} />
        </div>
        {title && <h3 className="text-xl font-bold text-text-primary dark:text-text-primary-dark text-center mb-2">{title}</h3>}
        {message && <p className="text-text-secondary dark:text-text-secondary-dark text-center mb-6">{message}</p>}
        {hasInput && (
          <div className="mb-6">
            {inputLabel && <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{inputLabel}</label>}
            <input
              type="text"
              value={inputValue || ''}
              onChange={(e) => onInputChange && onInputChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={inputPlaceholder}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3">
          {(type === 'confirm' || hasInput) && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2.5 rounded-full font-bold text-white transition-colors ${
              type === 'error' ? 'bg-error dark:bg-error-dark hover:bg-error-hover dark:hover:bg-error-hover-dark' :
              type === 'success' ? 'bg-success dark:bg-success-dark hover:bg-success-hover dark:hover:bg-success-hover-dark' :
              type === 'confirm' ? 'bg-confirm dark:bg-confirm-dark hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark text-confirm-text dark:text-confirm-text-dark' :
              'bg-info dark:bg-info-dark hover:bg-info-hover dark:hover:bg-info-hover-dark'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionBadge() {
  const [isOutdated, setIsOutdated] = useState(false);
  const [isAhead, setIsAhead] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  
  // Determine if we're on a non-release branch
  // Show branch name if it's not main, master, or a version tag pattern (e.g., v0.3.1)
  const isReleaseBranch = !GIT_BRANCH || 
    GIT_BRANCH === 'main' || 
    GIT_BRANCH === 'master' || 
    /^v?\d+\.\d+/.test(GIT_BRANCH) ||
    GIT_BRANCH === 'HEAD'; // Detached HEAD state (common in CI/Docker)
  
  const showBranch = GIT_BRANCH && !isReleaseBranch;

  useEffect(() => {
    // Check GitHub for latest version
    const checkVersion = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/MrCrin/swiish/releases/latest', {
          headers: {
            'Accept': 'application/vnd.github.v3+json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const latestVersion = data.tag_name?.replace(/^v/, '') || data.tag_name; // Remove 'v' prefix if present
          const currentVersion = APP_VERSION;
          
          // SemVer-compliant version comparison
          // Follows SemVer precedence rules: pre-release versions have lower precedence than stable versions
          const compareVersions = (v1, v2) => {
            // Parse versions into base version and pre-release identifier
            const parseVersion = (version) => {
              const dashIndex = version.indexOf('-');
              if (dashIndex === -1) {
                return {
                  base: version,
                  prerelease: null
                };
              }
              return {
                base: version.substring(0, dashIndex),
                prerelease: version.substring(dashIndex + 1)
              };
            };

            const parsed1 = parseVersion(v1);
            const parsed2 = parseVersion(v2);

            // Compare base versions numerically
            const base1 = parsed1.base.split('.').map(Number);
            const base2 = parsed2.base.split('.').map(Number);

            for (let i = 0; i < Math.max(base1.length, base2.length); i++) {
              const part1 = base1[i] || 0;
              const part2 = base2[i] || 0;
              if (part1 < part2) return -1;
              if (part1 > part2) return 1;
            }

            // Base versions are equal, now check pre-release identifiers
            // Rule: A version without a pre-release identifier has higher precedence
            if (parsed1.prerelease === null && parsed2.prerelease === null) {
              return 0; // Both are stable, equal
            }
            if (parsed1.prerelease === null) {
              return 1; // v1 is stable, v2 is pre-release, v1 > v2
            }
            if (parsed2.prerelease === null) {
              return -1; // v1 is pre-release, v2 is stable, v1 < v2
            }

            // Both have pre-release identifiers, compare lexicographically
            const prerelease1 = parsed1.prerelease.split('.');
            const prerelease2 = parsed2.prerelease.split('.');

            for (let i = 0; i < Math.max(prerelease1.length, prerelease2.length); i++) {
              const part1 = prerelease1[i];
              const part2 = prerelease2[i];

              if (part1 === undefined) return -1; // v1 has fewer parts, v1 < v2
              if (part2 === undefined) return 1; // v2 has fewer parts, v1 > v2

              // Try numeric comparison first, fall back to string comparison
              const num1 = Number(part1);
              const num2 = Number(part2);

              if (!isNaN(num1) && !isNaN(num2)) {
                // Both are numeric
                if (num1 < num2) return -1;
                if (num1 > num2) return 1;
              } else {
                // At least one is non-numeric, compare as strings
                if (part1 < part2) return -1;
                if (part1 > part2) return 1;
              }
            }

            return 0; // Pre-release identifiers are equal
          };
          
          if (latestVersion) {
            const comparison = compareVersions(currentVersion, latestVersion);
            if (comparison < 0) {
              setIsOutdated(true);
            } else if (comparison > 0) {
              setIsAhead(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check version:', error);
        // Silently fail - don't show error to user
      } finally {
        setIsChecking(false);
      }
    };

    checkVersion();
  }, []);

  // Build the display string: "v0.3.1" or "v0.3.1 (feature-branch)"
  const versionDisplay = showBranch 
    ? `v${APP_VERSION} (${GIT_BRANCH})`
    : `v${APP_VERSION}`;
  
  // Build the tooltip
  const tooltip = (!isOutdated && !isAhead && !showBranch)
    ? "Você está na versão mais recente"
    : (isAhead || showBranch)
      ? "Você está em uma versão de desenvolvimento não lançada - podem ocorrer erros"
      : isOutdated
        ? "Atualização disponível no GitHub"
        : "Ver no GitHub";

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <a
        href={GITHUB_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-xs font-medium transition-all bg-card dark:bg-card-dark border-2 border-border dark:border-border-dark px-2 py-1 rounded shadow-sm hover:shadow-md ${
          showBranch
            ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700'
            : isOutdated 
              ? 'text-error-text dark:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark hover:border-error-border dark:hover:border-error-border-dark' 
              : isAhead
                ? 'text-info-text dark:text-info-text-dark hover:bg-info-bg dark:hover:bg-info-bg-dark hover:border-info-border dark:hover:border-info-border-dark'
                : 'text-text-primary dark:text-text-primary-dark hover:text-action dark:hover:text-action-dark hover:border-success-border dark:hover:border-success-border-dark'
        }`}
        title={tooltip}
      >
        {versionDisplay}
      </a>
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // Note: useParams() doesn't work at App level (Routes are children), so we extract params from location.pathname
  const [view, setView] = useState(() => {
    // Initialize view based on current path - don't default to 'loading' for public routes
    const initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const pathParts = initialPath.substring(1).split('/').filter(p => p);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isOrgScoped || (pathParts.length === 1 && pathParts[0] && !initialPath.startsWith('/people') && !initialPath.startsWith('/login') && !initialPath.startsWith('/setup') && !initialPath.startsWith('/settings') && !initialPath.startsWith('/users') && !initialPath.startsWith('/cards') && initialPath !== '/');
    return isPublicRoute ? 'public-loading' : 'loading';
  }); 
  const [data, setData] = useState(() => getDefaultTemplate(null));
  const [currentSlug, setCurrentSlug] = useState('');
  const [availableThemes, setAvailableThemes] = useState(BUILTIN_THEME_META);
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'owner' or 'member'
  const [csrfToken, setCsrfToken] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, onClose: null, confirmText: 'OK', cancelText: 'Cancelar' });
  const [createCardModal, setCreateCardModal] = useState({ isOpen: false, slug: '', userId: null });
  const [targetUserIdForNewCard, setTargetUserIdForNewCard] = useState(null);
  const editInProgressRef = useRef(false);
  const [actionSelectionModal, setActionSelectionModal] = useState({ isOpen: false });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupData, setSetupData] = useState({ organisationName: '', adminEmail: '', adminPassword: '' });
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSuccessSetup, setIsSuccessSetup] = useState(false);
  const [isSuccessCreateUser, setIsSuccessCreateUser] = useState(false);
  const [isSuccessInvite, setIsSuccessInvite] = useState(false);
  
  // Demo mode state management
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoResetInterval, setDemoResetInterval] = useState(60);
  
  // Dark mode state management
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      const isDark = stored === 'true';
      // Sync with document class immediately
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return isDark;
    }
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    return prefersDark;
  });

  // Apply dark mode class to document whenever state changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Listen for system preference changes (only if no manual preference set)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const stored = localStorage.getItem('darkMode');
      if (stored === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Check for demo mode on app load
  useEffect(() => {
    fetch(`${API_ENDPOINT}/demo/status`)
      .then(res => res.json())
      .then(data => {
        if (data.demoMode) {
          setIsDemoMode(true);
          setDemoResetInterval(data.resetInterval || 60);
        }
      })
      .catch(err => {
        // Silently fail - demo endpoint may not exist if demo mode is off
      });
  }, []);

  const toggleDarkMode = () => {
    const currentDark = document.documentElement.classList.contains('dark');
    const newValue = !currentDark;

    if (newValue) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to localStorage
    localStorage.setItem('darkMode', newValue.toString());

    // Update React state
    setDarkMode(newValue);

    setTimeout(() => {
      const sampleEl = document.querySelector('.bg-main');
      if (sampleEl) {
        void sampleEl.offsetHeight;
      }
    }, 100);
  };

  // Demo Mode Banner Component
  const DemoModeBanner = () => {
    if (!isDemoMode) return null;

    return (
      <div className="sticky top-0 left-0 right-0 z-50 bg-amber-100 dark:bg-amber-900 border-b-2 border-amber-400 dark:border-amber-700 px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="text-2xl">🛠️</span>
          <span className="font-semibold text-amber-900 dark:text-amber-100">
            Modo Demo
          </span>
          <span className="text-sm text-amber-700 dark:text-amber-300">
            Todas as alterações são redefinidas a cada {demoResetInterval} minutos
          </span>
        </div>
      </div>
    );
  };

  // Helper functions to show modals
  const showAlert = (message, type = 'info', title = '', onClose = null) => {
    setModal({ isOpen: true, type, title, message, onConfirm: null, onClose, confirmText: 'OK', cancelText: 'Cancelar' });
  };

  const showConfirm = (message, onConfirm, title = 'Confirmar', confirmText = 'Confirmar', cancelText = 'Cancelar') => {
    setModal({ isOpen: true, type: 'confirm', title, message, onConfirm, onClose: null, confirmText, cancelText });
  };

  const closeModal = () => {
    const currentModal = modal;
    setModal(prev => ({ ...prev, isOpen: false }));
    // Call onClose callback after state update
    if (currentModal.onClose) {
      setTimeout(() => currentModal.onClose(), 0);
    }
  };

const THEME_PRESETS = {
  swiish: [
    { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-cyan-600 bg-cyan-50 border-indigo-100 hover:bg-cyan-100", text: "text-cyan-600" },
    { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
    { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
    { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
    { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
  ],
  minimal: [
    { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
    { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
    { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
  ]
};

const [settings, setSettings] = useState({
  default_organisation: 'My Organisation',
  theme_variant: 'swiish',
  theme_colors: THEME_PRESETS.swiish
  });

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/csrf-token`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      }
    } catch (e) {
      console.error('Failed to fetch CSRF token:', e);
    }
  };

  // Helper function to make authenticated API calls
  const apiCall = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (csrfToken && (options.method === 'POST' || options.method === 'DELETE')) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
  };
  
  // Check setup status
  const checkSetupStatus = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/status`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSetupStatus(data);
        return data;
      }
    } catch (e) {
      console.error('Failed to check setup status:', e);
    }
    return null;
  };

  // Router Logic - handle route changes and set view based on route
  useEffect(() => {
    const path = location.pathname;
    // Handle editor route (/people/edit/:slug) - extract slug from pathname since useParams() doesn't work at App level
    if (path.startsWith('/people/edit/')) {
      // Extract slug from pathname (remove '/people/edit/' prefix)
      const slug = path.replace('/people/edit/', '');
      // Only load if this is a different slug or we're not already in editor view
      if (currentSlug !== slug || view !== 'admin-editor') {
        setCurrentSlug(slug);
        fetchCsrfToken();
        checkAuth().then((authResult) => {
          if (authResult.isAuthenticated) {
            // After auth check, load the card data — but skip if handleEdit is already
            // in progress (e.g. triggered by the admin clicking the Edit button, which
            // navigates here and would otherwise cause a second fetch without userId)
            if (!editInProgressRef.current) {
              handleEdit(slug);
            }
          } else {
            // If auth fails, redirect to login (explicit redirect for unauthorized access)
            navigate('/login');
          }
        }).catch(() => {
          // If auth fails, redirect to login
          navigate('/login');
        });
      }
      return;
    }
    
    // Skip public card route handling here - PublicCardRoute component handles it
    // This prevents duplicate fetches
    // Public routes: /:orgSlug/:cardSlug or canonical /:slug
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isOrgScoped || (pathParts.length === 1 && pathParts[0] && !path.startsWith('/people') && !path.startsWith('/login') && !path.startsWith('/setup') && !path.startsWith('/settings') && !path.startsWith('/users') && !path.startsWith('/cards') && path !== '/');
    
    if (isPublicRoute) {
      // Don't interfere with public card routes - let PublicCardRoute handle it
      // Only set initial state if view is still 'loading' (first render)
      // Once PublicCardRoute takes over (view is 'public-loading' or 'public-card'), don't touch it
      if (view === 'loading') {
        setView('public-loading');
      } else if (view === 'public-loading' || view === 'public-card' || view === '404') {
        // PublicCardRoute is managing state - don't interfere
      }
      return;
    }
    
    // Set view based on route
    if (path === '/setup') {
      // Check if setup is already complete - if so, redirect to login
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        if (status && status.setupComplete && status.userCount > 0) {
          // Setup already complete, redirect to login
          navigate('/login', { replace: true });
        } else {
          // Setup not complete, show wizard
          setView('setup-wizard');
          document.title = "Initial Setup";
        }
      }).catch(() => {
        // If check fails, show wizard anyway
        setView('setup-wizard');
        document.title = "Initial Setup";
      });
      return;
    } else if (path === '/login') {
      // Login page - check if already authenticated
      setView('loading');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (authResult.isAuthenticated) {
          // If authenticated, redirect to dashboard (explicit redirect)
          navigate('/people', { replace: true });
        } else {
          // Not authenticated, show login page
          setView('admin-login');
          document.title = "Admin Login";
        }
      }).catch(() => {
        // Not authenticated, show login page
        setView('admin-login');
        document.title = "Admin Login";
      });
    } else if (path === '/settings') {
      setView('admin-settings');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login', { replace: true });
        }
      });
    } else if (path === '/users') {
      setView('user-management');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login', { replace: true });
        }
      });
    } else if (path === '/admin' || path === '/' || path === '/people') {
      // Check demo mode status first (will be known from setup/status response)
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        // Check if demo mode is active (from status response or isDemoMode state)
        const demoModeActive = status?.demoMode || isDemoMode;

        if (demoModeActive) {
          // Demo mode: skip setup, go directly to auth check
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people', { replace: true });
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (authResult.isAuthenticated) {
              // Demo mode: always show dashboard for demo user (owner role)
              setView('admin-dashboard');
              document.title = "Admin Dashboard";
            } else {
              // This shouldn't happen in demo mode, but fallback to login just in case
              navigate('/login', { replace: true });
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login', { replace: true });
          });
          return;
        }

        // Normal mode: continue with regular auth flow
        // (status already checked above, setupComplete must be true to get here)
        if (status === null) {
          // If check failed (server not running, network error, etc.), default to setup wizard
          navigate('/setup', { replace: true });
          document.title = "Initial Setup";
        } else if (!status.setupComplete || status.userCount === 0) {
          // Setup not complete or no users exist, show setup wizard
          navigate('/setup', { replace: true });
          document.title = "Initial Setup";
        } else {
          // Setup complete, check authentication
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people', { replace: true });
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (authResult.isAuthenticated) {
              // Determine view based on role and route
              if (authResult.userData.role === 'member') {
                if (authResult.cardList.length === 0) {
                  setView('member-empty');
                } else {
                  // Navigate to first card editor (explicit navigation)
                  const firstCard = authResult.cardList[0];
                  navigate(`/people/edit/${firstCard.slug}`, { replace: true });
                }
              } else {
                // Owner - show dashboard
                setView('admin-dashboard');
              }
          document.title = "Admin Dashboard";
            } else {
              // Not authenticated, redirect to login (explicit redirect)
              navigate('/login', { replace: true });
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login', { replace: true });
          });
        }
      }).catch((e) => {
        // If promise rejects, default to setup wizard
        console.error('Setup status check failed:', e);
        navigate('/setup', { replace: true });
        document.title = "Initial Setup";
      });
    }
  }, [location.pathname, navigate]);


  // checkAuth: Only updates state, never navigates
  // Returns { isAuthenticated: boolean, userData: object | null, cardList: array }
  const checkAuth = async () => {
    try {
      // Fetch CSRF token first if not already fetched
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Fetch user info to get role
      const userRes = await apiCall(`${API_ENDPOINT}/auth/me`);
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserRole(userData.role);
        setCurrentUserId(userData.id);
        setCurrentUserEmail(userData.email);
        
        // Fetch cards
        const res = await apiCall(`${API_ENDPOINT}/admin/cards`);
        if (res.ok) {
          const list = await res.json();
          setCardList(list);
          setIsAuthenticated(true);
          if (userData.role === 'member') {
            fetchPublicSettings(userData.orgSlug || 'default');
          } else {
            fetchSettings();
          }
          
          return { isAuthenticated: true, userData, cardList: list };
        } else {
          setIsAuthenticated(false);
          setUserRole(null);
          return { isAuthenticated: false, userData: null, cardList: [] };
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        return { isAuthenticated: false, userData: null, cardList: [] };
      }
    } catch (e) {
      setIsAuthenticated(false);
      setUserRole(null);
      return { isAuthenticated: false, userData: null, cardList: [] };
    }
  };

  const fetchPublicCard = async (slug) => {
    setIsPublicLoading(true);
    setView('public-loading'); // Ensure view is set to loading while fetching
    try {
      // Fetch settings first so colors are available
      await fetchPublicSettings();
      const res = await fetch(`${API_ENDPOINT}/cards/${slug}`);
      if (res.ok) {
        const json = await res.json();
        const defaultTemplate = getDefaultTemplate(settings);
        setData({ 
          ...defaultTemplate, 
          ...json, 
          links: json.links || [],
          privacy: json.privacy || defaultTemplate.privacy
        });
        setView('public-card');
        document.title = json.personal?.name ? `${json.personal.name} - ANRVA Card` : "ANRVA Card";
      } else {
        setError('Card not found');
        setView('404');
        document.title = "Card Not Found";
      }
    } catch (e) {
      console.error('Error fetching public card:', e);
      setError('Connection failed');
      setView('404');
      document.title = "Error Loading Card";
    } finally {
      setIsPublicLoading(false);
    }
  };

  const fetchCardByOrgAndSlug = useCallback(async (orgSlug, cardSlug) => {
    setIsPublicLoading(true);
    try {
      // Fetch settings for the organization from the URL
      await fetchPublicSettings(orgSlug);
      const res = await fetch(`${API_ENDPOINT}/cards/${orgSlug}/${cardSlug}`);
      if (res.ok) {
        const cardData = await res.json();
        const defaultTemplate = getDefaultTemplate(settings);
        const mergedData = { 
          ...defaultTemplate, 
          ...cardData, 
          links: cardData.links || [],
          privacy: cardData.privacy || defaultTemplate.privacy,
          _slug: cardData._slug || cardSlug
        };
        // Set isPublicLoading to false FIRST, then data, then view
        // This prevents renderAdminViews from showing loading when view='public-card'
        setIsPublicLoading(false);
        setData(mergedData);
        setView('public-card');
        const name = `${cardData.personal?.firstName || ''} ${cardData.personal?.lastName || ''}`.trim();
        // Apply this card's own theme variant (per-card visual theming)
        const cardVariant2 = cardData.theme?.variant || settings?.theme_variant || 'swiish';
        document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
        document.body.classList.add(`theme-${cardVariant2}`);
        applyThemeCssVars(cardVariant2);
        document.title = name ? `${name} | ANRVA Presença Digital` : 'ANRVA Presença Digital';
      } else {
        setView('404');
        setError('Card not found');
        document.title = 'Card Not Found';
      }
    } catch (e) {
      console.error('[FRONTEND] Error fetching card by org and slug:', e);
      setView('404');
      setError('Failed to load card');
      document.title = 'Card Not Found';
    } finally {
      setIsPublicLoading(false);
    }
  }, [settings]);

  // Helper to initialize color data for settings (shared between fetchSettings and SettingsView)
  // Note: SettingsView has its own initializeColorData function, but we need this one for fetchSettings
  const initializeColorDataForSettings = (colors) => {
    if (!colors || !Array.isArray(colors)) return [];
    return colors.map(color => {
      let hexBase, baseColor, colorType;
      
      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');
      
      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }
      
      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);
      
      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;
      
      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const loadCustomThemes = useCallback(async () => {
    try {
      const res = await fetch('/api/themes');
      if (!res.ok) return;
      const custom = await res.json();
      custom.forEach(t => { RUNTIME_THEMES[t.id] = t; });
      setAvailableThemes([...BUILTIN_THEME_META, ...custom.map(t => ({
        id: t.id,
        displayName: t.displayName || t.name || t.id,
        swatchColors: t.swatchColors || Object.values(t.colors || {}).slice(0, 3).map(c => c?.light || '#888'),
      }))]);
    } catch {}
  }, []); // eslint-disable-line

  const fetchSettings = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/settings`);
      if (res.ok) {
        const settingsData = await res.json();
      const merged = {
        ...settingsData,
        theme_variant: settingsData.theme_variant || 'swiish',
        theme_colors: initializeColorDataForSettings(settingsData.theme_colors || [])
      };
      setSettings(merged);
      applyThemeCssVars(merged.theme_variant);
      document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
      document.body.classList.add(`theme-${merged.theme_variant}`);
      loadCustomThemes();
      }
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    }
  };

  const fetchPublicSettings = async (orgSlug = 'default') => {
    try {
      const url = orgSlug ? `${API_ENDPOINT}/settings?orgSlug=${encodeURIComponent(orgSlug)}` : `${API_ENDPOINT}/settings`;
      const res = await fetch(url);
      if (res.ok) {
        const settingsData = await res.json();
        const mergedVariant = settingsData.theme_variant || 'swiish';
        
        // Match admin pattern: create new object from API response
        // This ensures React sees it as a new object and triggers re-renders
        const merged = {
          default_organisation: settingsData.default_organisation || 'My Organisation',
          theme_variant: mergedVariant,
          theme_colors: (settingsData.theme_colors && Array.isArray(settingsData.theme_colors)) 
            ? initializeColorDataForSettings(settingsData.theme_colors)
            : THEME_PRESETS.swiish
        };
        
        // Set state and apply theme immediately (same pattern as fetchSettings)
        setSettings(merged);
        applyThemeCssVars(mergedVariant);
        document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
        document.body.classList.add(`theme-${mergedVariant}`);
        loadCustomThemes();
      }
    } catch (e) {
      console.error('Failed to fetch public settings:', e);
    }
  };

  // Group cards by user (userId or userEmail as fallback)
  const groupCardsByUser = (cards) => {
    const userMap = new Map();
    
    cards.forEach(card => {
      // Use userId as primary key, fallback to userEmail if userId not available
      const userKey = card.userId || card.userEmail || 'unknown';
      
      if (!userMap.has(userKey)) {
        userMap.set(userKey, {
          userId: card.userId || null,
          userEmail: card.userEmail || null,
          userRole: card.userRole || null,
          userCreatedAt: card.userCreatedAt || null,
          cards: []
        });
      }
      
      // Add card to user's cards array (even if slug is null, it represents a user without cards)
      userMap.get(userKey).cards.push(card);
    });
    
    // Convert map to array and sort by user creation date (newest first)
    return Array.from(userMap.values()).sort((a, b) => {
      if (!a.userCreatedAt && !b.userCreatedAt) return 0;
      if (!a.userCreatedAt) return 1;
      if (!b.userCreatedAt) return -1;
      return new Date(b.userCreatedAt) - new Date(a.userCreatedAt);
    });
  };

  const fetchCardList = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/cards`);
      if (res.ok) {
        const list = await res.json();
        setCardList(list);
        // Removed setView('admin-dashboard') - fetchCardList should only update data, not view
        // View should be determined by route, not by data fetching
        setIsAuthenticated(true);
        // Fetch settings when dashboard loads (only if we're actually on dashboard)
        if (location.pathname === '/people') {
        fetchSettings();
        }
      } else {
        setIsAuthenticated(false);
        // Only set view to login if we're on a protected route
        if (location.pathname !== '/login' && location.pathname !== '/setup') {
        setView('admin-login');
        }
      }
    } catch (e) {
      setIsAuthenticated(false);
      // Only set view to login if we're on a protected route
      if (location.pathname !== '/login' && location.pathname !== '/setup') {
      setView('admin-login');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.toLowerCase().trim(), password })
      });
      if (res.ok) {
        // Token is now in httpOnly cookie, fetch CSRF token and check auth
        await fetchCsrfToken();
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful login (explicit user action)
          navigate('/people');
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || 'E-mail ou senha inválidos');
      }
    } catch (e) { 
      setError('Falha ao fazer login'); 
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_ENDPOINT}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout API call failed:', e);
    }
    // Force hard redirect immediately - don't set state first as it causes race conditions
    // The hard redirect will reload the page and useEffect will handle the login view
    window.location.href = '/login';
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setIsSettingUp(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          organisationName: setupData.organisationName.trim(),
          adminEmail: setupData.adminEmail.toLowerCase().trim(),
          adminPassword: setupData.adminPassword
        })
      });
      if (res.ok) {
        // Setup successful, automatically log in
        setIsSuccessSetup(true);
        setTimeout(() => setIsSuccessSetup(false), 2000);
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful setup (explicit user action)
          navigate('/people');
        }
      } else {
        const rawText = await res.text().catch(() => '(sem resposta)');
        let serverMsg = rawText;
        try { serverMsg = JSON.parse(rawText).error || rawText; } catch {}
        setError(`HTTP ${res.status}: ${serverMsg}`);
      }
    } catch (e) {
      setError(`Erro de rede: ${e.message}`);
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleCreateNew = () => {
    setCreateCardModal({ isOpen: true, slug: '', userId: null });
  };

  const handleCreateCardConfirm = () => {
    const slug = createCardModal.slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
    if (!slug) {
      showAlert('Por favor insira uma URL de usuário válida (ex.: "sarah")', 'error', 'URL do Usuário Inválida');
      return;
    }
    if (slug.length < 1) {
      showAlert('A URL do usuário deve ter pelo menos 1 caractere', 'error', 'URL do Usuário Inválida');
      return;
    }
    // Prevent duplicate user URLs for the same user
    // Flatten all cards from grouped structure to check for duplicates
    const allCards = cardList.filter(c => c.slug === slug);
    if (allCards.length > 0) {
      showAlert(`Uma URL de usuário "${slug}" já existe. Por favor escolha outra URL de usuário.`, 'error', 'URL do Usuário já existe');
      return;
    }
    // Store userId for this new card if provided
    if (createCardModal.userId) {
      setTargetUserIdForNewCard(createCardModal.userId);
    }
    setCreateCardModal({ isOpen: false, slug: '', userId: null });
    setCurrentSlug(slug);
    setData(getDefaultTemplate(settings)); 
    setView('admin-editor');
    // Navigate to editor route, just like handleEdit does
    navigate(`/people/edit/${slug}`);
  };

  const handleCreateCardCancel = () => {
    setCreateCardModal({ isOpen: false, slug: '' });
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert('E-mail e senha são obrigatórios', 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert('A senha deve ter pelo menos 8 caracteres', 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      
      if (res.ok) {
        setIsSuccessCreateUser(true);
        setTimeout(() => setIsSuccessCreateUser(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member' });
        checkAuth(); // Refresh card list
      } else {
        // Try to parse error response
        let errorMessage = 'Falha ao criar usuário';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = `Falha ao criar usuário: ${res.status} ${res.statusText}`;
        }
        if (showAlert) showAlert(errorMessage, 'error');
        // Don't close modal on error - let user fix and retry
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || 'Erro ao criar usuário. Tente novamente.';
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert('E-mail é obrigatório', 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        setIsSuccessInvite(true);
        setTimeout(() => setIsSuccessInvite(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Falha ao enviar convite', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro ao enviar convite', 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        if (showAlert) showAlert('Cargo do usuário atualizado com sucesso', 'success');
        checkAuth(); // Refresh card list
        setEditingUserId(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Falha ao atualizar cargo', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro ao atualizar cargo', 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        `Tem certeza de que deseja excluir permanentemente ${userEmail}? Isso excluirá o usuário e todos os seus cartões. Esta ação não pode ser desfeita.`,
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('Usuário excluído com sucesso', 'success');
              checkAuth(); // Refresh card list
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || 'Falha ao excluir usuário', 'error');
            }
          } catch (e) {
            if (showAlert) showAlert('Erro ao excluir usuário', 'error');
          }
        },
        'Excluir Usuário',
        'Excluir',
        'Cancelar'
      );
    }
  };

  const handleEdit = async (slug, userId) => {
    editInProgressRef.current = true;
    try {
      // User IDs are UUID strings (crypto.randomUUID()). Guard against null/undefined/non-string values.
      const validUserId = userId && typeof userId === 'string' && userId.trim() ? userId : null;
      const isOtherUser = validUserId !== null && validUserId !== currentUserId;
      const fetchUrl = isOtherUser
        ? `${API_ENDPOINT}/admin/cards/${validUserId}/${slug}`
        : `${API_ENDPOINT}/cards/${slug}`;
      const res = await apiCall(fetchUrl);
      if (res.ok) {
        const json = await res.json();
        const defaultTemplate = getDefaultTemplate(settings);
        setData({ 
          ...defaultTemplate, 
          ...json,
          // Enforce default_organisation from organization settings
          personal: {
            ...defaultTemplate.personal,
            ...json.personal,
            company: json.personal?.company || ''
          },
          links: json.links || [],
          privacy: json.privacy || defaultTemplate.privacy
        });
        // Track the owner so handleSave sends it to the correct user's card
        if (isOtherUser) {
          setTargetUserIdForNewCard(validUserId);
        } else {
          setTargetUserIdForNewCard(null);
        }
        setCurrentSlug(slug);
        setView('admin-editor');
        // Navigate to editor route only if not already there
        if (location.pathname !== `/people/edit/${slug}`) {
          navigate(`/people/edit/${slug}`);
        }
      } else {
        let errorMsg = 'Failed to load card';
        try {
          const errData = await res.json();
          errorMsg = errData.error || errorMsg;
        } catch {}
        showAlert(`${errorMsg} (${res.status})`, 'error');
      }
    } finally {
      editInProgressRef.current = false;
    }
  };

  const handleDelete = async (slug, userId) => {
    showConfirm(
      `Tem certeza de que deseja excluir ${slug}?`,
      async () => {
        const validUserId = userId && typeof userId === 'string' && userId.trim() ? userId : null;
        const isOtherUser = validUserId !== null && validUserId !== currentUserId;
        const deleteUrl = isOtherUser
          ? `${API_ENDPOINT}/cards/${slug}?userId=${encodeURIComponent(validUserId)}`
          : `${API_ENDPOINT}/cards/${slug}`;
        const res = await apiCall(deleteUrl, {
          method: 'DELETE'
        });
        if (res.ok) {
          showAlert('Cartão excluído com sucesso', 'success', '', () => {
            fetchCardList();
          });
        } else {
          showAlert('Falha ao excluir cartão', 'error');
        }
      },
      'Excluir Cartão',
      'Excluir',
      'Cancelar'
    );
  };

  const handleSave = async () => {
    const performSave = async () => {
      setIsSaving(true);
      const startTime = Date.now();
      try {
        // Include userId in request body if creating card for another user
        const body = { ...data };
        if (targetUserIdForNewCard) {
          body.userId = targetUserIdForNewCard;
        }

        const res = await apiCall(`${API_ENDPOINT}/cards/${currentSlug}`, {
          method: 'POST',
          body: JSON.stringify(body)
        });

        // Ensure at least 500ms has passed
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < 500) {
          await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
        }

        if (res.ok) {
          // Clear targetUserIdForNewCard after successful save
          setTargetUserIdForNewCard(null);
          setIsSuccess(true);
          fetchCardList();
          setTimeout(() => setIsSuccess(false), 2000);
        } else {
          showAlert('Falha ao salvar', 'error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const email = (data.contact?.email || '').trim().toLowerCase();
    if (email && cardList.length > 0) {
      // Only check against actual cards (those with slugs) - this excludes user entries without cards
      // Also exclude the current card being edited
      const duplicates = cardList.filter(c => 
        c.slug && // Must be an actual card
        c.email && // Must have a contact email
        c.email === email && // Must match the email being saved
        c.slug !== currentSlug // Must not be the current card being edited
      );
      if (duplicates.length > 0) {
        showConfirm(
          `Outro cartão já usa este endereço de e-mail (${email}). Deseja salvar mesmo assim?`,
          performSave,
          'E-mail já em uso',
          'Salvar mesmo assim',
          'Cancelar'
        );
        return;
      }
    }

    await performSave();
  };

  // Always use Routes for proper URL handling
  // Admin routes must be defined before /:slug to prevent matching
  // All admin views are consolidated here to avoid duplication
  const renderAdminViews = () => (
    <>
      {view === 'loading' && (
        <>
          <div className="h-screen flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark bg-main dark:bg-main-dark bg-main-texture">Carregando...</div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === '404' && (
        <>
          <div className="h-screen flex flex-col items-center justify-center bg-main dark:bg-main-dark bg-main-texture">
            <h1 className="text-4xl font-bold text-text-primary dark:text-text-primary-dark mb-2">404</h1>
            <p className="text-text-muted dark:text-text-muted-dark">Cartão não encontrado.</p>
          </div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'setup-wizard' && (
        <>
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#04090f' }}>
            <div className="w-full max-w-md">
              <div className="text-center mb-8">
                <img src="/graphics/ANRVA_Logo_DarkBg.svg" alt="ANRVA" className="h-7 w-auto mx-auto mb-2" />
                <p className="text-sm font-semibold tracking-widest uppercase" style={{ color: '#0891b2' }}>Presença Digital</p>
              </div>
              <div className="rounded-2xl p-8" style={{ background: '#0d1b2e', border: '1px solid rgba(8,145,178,0.25)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}>
                <h1 className="text-xl font-bold text-white mb-1 text-center">Configuração Inicial</h1>
                <p className="text-sm text-slate-400 text-center mb-6">Crie o acesso de administrador</p>
                <form onSubmit={handleSetup} className="space-y-4">
                  {[
                    { label: 'Nome da Organização', type: 'text', key: 'organisationName', placeholder: 'ANRVA Presença Digital', required: true, autoFocus: true },
                    { label: 'E-mail Admin', type: 'email', key: 'adminEmail', placeholder: 'admin@exemplo.com', required: true },
                    { label: 'Senha Admin', type: 'password', key: 'adminPassword', placeholder: 'Mínimo 8 caracteres', required: true, minLength: 8 },
                  ].map(({ label, key, ...rest }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
                      <input
                        {...rest}
                        value={setupData[key]}
                        onChange={e => setSetupData({ ...setupData, [key]: e.target.value })}
                        className="w-full px-5 py-3 rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all"
                        style={{ background: '#04090f', border: '1px solid #1e3a5f' }}
                        onFocus={e => e.target.style.borderColor='#0891b2'}
                        onBlur={e => e.target.style.borderColor='#1e3a5f'}
                      />
                    </div>
                  ))}
                  {error && (
                    <div className="rounded-xl p-3 text-xs space-y-1" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                      <p className="font-semibold text-red-400">Erro ao configurar</p>
                      <p className="text-red-300 break-all font-mono whitespace-pre-wrap">{error}</p>
                      <p className="text-red-400 pt-1">CSRF token: <span className="font-mono">{csrfToken ? `obtido (${csrfToken.length} chars)` : '❌ não obtido'}</span></p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSettingUp}
                    className="w-full py-3.5 rounded-full font-bold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                    style={{ background: '#0891b2' }}
                    onMouseOver={e => !isSettingUp && (e.currentTarget.style.background='#0e7490')}
                    onMouseOut={e => e.currentTarget.style.background='#0891b2'}
                  >
                    {isSettingUp ? <RefreshCw className="w-4 h-4 animate-spin" /> : isSuccessSetup ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    {isSettingUp ? 'Configurando...' : 'Concluir Configuração'}
                  </button>
                </form>
              </div>
            </div>
          </div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-login' && (
        <>
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#04090f' }}>
            <div className="w-full max-w-sm">
              <div className="text-center mb-8">
                <img src="/graphics/ANRVA_Logo_DarkBg.svg" alt="ANRVA" className="h-7 w-auto mx-auto mb-2" />
                <p className="text-sm font-semibold tracking-widest uppercase" style={{ color: '#0891b2' }}>Presença Digital</p>
              </div>
              <div className="rounded-2xl p-8" style={{ background: '#0d1b2e', border: '1px solid rgba(8,145,178,0.25)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}>
                <h1 className="text-xl font-bold text-white mb-6 text-center">Acesso ao Painel</h1>
                <form onSubmit={handleLogin} className="space-y-3">
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="E-mail" autoFocus
                    className="w-full px-5 py-3 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 transition-all"
                    style={{ background: '#04090f', border: '1px solid #1e3a5f', focusRingColor: '#0891b2' }}
                    onFocus={e => e.target.style.borderColor='#0891b2'}
                    onBlur={e => e.target.style.borderColor='#1e3a5f'}
                  />
                  <input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Senha"
                    className="w-full px-5 py-3 rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all"
                    style={{ background: '#04090f', border: '1px solid #1e3a5f' }}
                    onFocus={e => e.target.style.borderColor='#0891b2'}
                    onBlur={e => e.target.style.borderColor='#1e3a5f'}
                  />
                  {error && <div className="text-red-400 text-sm px-1">{error}</div>}
                  <button type="submit" className="w-full py-3.5 rounded-full font-bold text-white transition-all mt-2" style={{ background: '#0891b2' }} onMouseOver={e => e.currentTarget.style.background='#0e7490'} onMouseOut={e => e.currentTarget.style.background='#0891b2'}>Entrar</button>
                </form>
              </div>
            </div>
          </div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-dashboard' && (
        <>
          <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
            {/* ANRVA topbar */}
            <div className="w-full px-6 md:px-10 py-3 flex items-center justify-between" style={{ background: '#04090f', borderBottom: '1px solid rgba(8,145,178,0.2)' }}>
              <img src="/graphics/ANRVA_Logo_DarkBg.svg" alt="ANRVA" className="h-5 w-auto" />
              <div className="flex items-center gap-2">
                <button onClick={toggleDarkMode} className="p-2 rounded-full text-slate-400 hover:text-white transition-colors" title={darkMode ? 'Modo claro' : 'Modo escuro'}>
                  {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <button onClick={handleLogout} className="px-3 py-1.5 rounded-full text-sm font-medium text-slate-300 hover:text-white transition-colors" style={{ border: '1px solid #1e3a5f' }}>Sair</button>
                {userRole === 'owner' && (
                  <button onClick={() => navigate('/settings')} className="px-3 py-1.5 rounded-full text-sm font-medium text-slate-300 hover:text-white transition-colors flex items-center gap-1.5" style={{ border: '1px solid #1e3a5f' }}>
                    <Settings className="w-3.5 h-3.5" /> Configurações
                  </button>
                )}
                {userRole === 'owner' && (
                  <button onClick={() => navigate('/users')} className="px-3 py-1.5 rounded-full text-sm font-medium text-slate-300 hover:text-white transition-colors flex items-center gap-1.5" style={{ border: '1px solid #1e3a5f' }}>
                    <Users className="w-3.5 h-3.5" /> Usuários
                  </button>
                )}
                <button onClick={handleCreateNew} className="px-4 py-1.5 rounded-full text-sm font-bold text-white flex items-center gap-1.5 transition-all" style={{ background: '#0891b2' }} onMouseOver={e => e.currentTarget.style.background='#0e7490'} onMouseOut={e => e.currentTarget.style.background='#0891b2'}>
                  <Plus className="w-4 h-4" /> Novo Cartão
                </button>
              </div>
            </div>
          <div className="p-6 md:p-10 flex flex-col flex-1">
          <div className="max-w-6xl mx-auto flex-1 w-full">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-text-primary dark:text-text-primary-dark">Cartões</h1>
              <p className="text-sm md:text-base text-text-muted dark:text-text-muted-dark">Gerencie seus cartões digitais</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {cardList.filter(c => c.slug).map(card => {
                return (
                  <div key={card.slug} className="bg-card dark:bg-card-dark rounded-card shadow-sm border border-border-subtle dark:border-border-dark hover:shadow-md transition-shadow" style={{ aspectRatio: '1.586 / 1' }}>
                    <div className="w-full h-full flex flex-col p-4">
                      {/* Avatar + info + delete */}
                      <div className="flex-1 flex flex-row items-start gap-3 mb-3 relative">
                        <div className="w-16 h-16 rounded-full bg-surface dark:bg-surface-dark overflow-hidden border-2 border-border-subtle dark:border-border-dark flex-shrink-0">
                          {card.avatar ? <img src={card.avatar} className="w-full h-full object-cover" alt="avatar" /> : <User className="w-full h-full p-4 text-text-muted-subtle dark:text-text-muted-dark" />}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(card.slug, card.userId); }} className="absolute top-0 right-0 p-1.5 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark rounded-full transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <div className="flex-1 flex flex-col text-left min-w-0">
                          <h3 className="font-bold text-text-primary dark:text-text-primary-dark text-sm mb-0.5 truncate">{card.name}</h3>
                          {card.title && <p className="text-text-muted dark:text-text-muted-dark text-xs mb-1 truncate">{card.title}</p>}
                          <div className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark font-mono truncate">/{card.slug}</div>
                          {userRole === 'owner' && card.userEmail && (
                            <div className="text-[10px] text-cyan-600 dark:text-cyan-400 truncate mt-0.5">{card.userEmail}</div>
                          )}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-auto">
                        <a href={`/${card.slug}`} target="_blank" rel="noreferrer" className="flex-1 py-1.5 text-xs font-medium text-confirm-text dark:text-confirm-text-dark bg-confirm dark:bg-confirm-dark rounded-button hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark flex items-center justify-center gap-1">
                          <ExternalLink className="w-3 h-3"/> Visualizar
                        </a>
                        <button onClick={() => handleEdit(card.slug, card.userId)} className="flex-1 py-1.5 text-xs font-medium text-white bg-action dark:bg-action-dark rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-1">
                          <Edit3 className="w-3 h-3"/> Editar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Novo Cartão — tile placeholder */}
              <div className="bg-surface dark:bg-surface-dark/30 rounded-card border-2 border-dashed border-border dark:border-border-dark flex items-center justify-center" style={{ aspectRatio: '1.586 / 1' }}>
                <button onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: null })} className="px-4 py-2.5 text-sm font-medium text-white bg-action dark:bg-action-dark rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4"/> Novo Cartão
                </button>
              </div>
          </div>
          </div>
          </div>
          </div>
          {/* Modals */}
          {/* Create User Modal */}
          {showCreateUserModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Criar Novo Usuário</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">E-mail</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Senha</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      placeholder="Mínimo 8 caracteres"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Cargo</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    >
                      <option value="member">Membro</option>
                      <option value="owner">Proprietário</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setNewUser({ email: '', password: '', role: 'member' });
                    }}
                    className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateUser}
                    disabled={isSavingUser}
                    className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50"
                  >
                    {isSavingUser ? 'Criando...' : 'Criar Usuário'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
          <Modal
            isOpen={createCardModal.isOpen}
            onClose={handleCreateCardCancel}
            type="info"
            title="Novo Cartão"
            message={`URL pública: ${window.location.origin}/${createCardModal.slug.toLowerCase().replace(/[^a-z0-9-]/g, '') || '...'}`}
            inputLabel="Slug (parte final da URL)"
            inputPlaceholder="ex: anderson ou casa-verde"
            inputValue={createCardModal.slug}
            onInputChange={(value) => setCreateCardModal(prev => ({ ...prev, slug: value }))}
            onConfirm={handleCreateCardConfirm}
            confirmText="Criar"
            cancelText="Cancelar"
          />
        </>
      )}
      {view === 'member-empty' && (
        <>
          <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex items-center justify-center p-6">
            <div className="bg-card dark:bg-card-dark rounded-card shadow-lg max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
              </div>
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Você ainda não tem um cartão</h2>
              <p className="text-text-secondary dark:text-text-muted-dark mb-6">Crie seu primeiro cartão para começar.</p>
              <button
                onClick={() => setCreateCardModal({ isOpen: true, slug: '' })}
                className="w-full px-4 py-3 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Criar Cartão
              </button>
              <button
                onClick={handleLogout}
                className="w-full mt-3 px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                Sair
              </button>
            </div>
          </div>
          <Modal 
            isOpen={createCardModal.isOpen} 
            onClose={handleCreateCardCancel} 
            type="info" 
            title="Criar Novo Cartão" 
            message="Insira uma URL de usuário para o novo cartão (ex.: 'sarah'):"
            inputLabel="URL do Usuário"
            inputPlaceholder="sarah"
            inputValue={createCardModal.slug}
            onInputChange={(value) => setCreateCardModal(prev => ({ ...prev, slug: value }))}
            onConfirm={handleCreateCardConfirm}
            confirmText="Criar"
            cancelText="Cancelar"
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-editor' && (
        <>
          <EditorView 
            data={data} 
            setData={setData} 
            onBack={() => { navigate('/people'); fetchCardList(); }} 
            onSave={handleSave}
            slug={currentSlug}
            settings={settings}
            csrfToken={csrfToken}
            showAlert={showAlert}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            isSaving={isSaving}
            isSuccess={isSuccess}
            onLogout={userRole === 'member' ? handleLogout : undefined}
            availableThemes={availableThemes}
            setAvailableThemes={setAvailableThemes}
          />

          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-settings' && (
        <>
          <SettingsView
            settings={settings}
            setSettings={setSettings}
            apiCall={apiCall}
            onBack={() => { navigate('/people'); fetchCardList(); }}
            onSave={async () => {
              await fetchSettings();
              fetchCardList();
              }}
            showAlert={showAlert}
            showConfirm={showConfirm}
            availableThemes={availableThemes}
            setAvailableThemes={setAvailableThemes}
            csrfToken={csrfToken}
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'user-management' && (
        <>
          <UserManagementView
            apiCall={apiCall}
            userRole={userRole}
            onBack={() => { navigate('/people'); fetchCardList(); }}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
    </>
  );

  return (
    <>
      <DemoModeBanner />
      <Routes>
        {/* Admin routes - must come before public routes to prevent matching */}
        <Route path="/login" element={renderAdminViews()} />
        <Route path="/setup" element={renderAdminViews()} />
        <Route path="/people/edit/:slug" element={renderAdminViews()} />
        <Route path="/people" element={renderAdminViews()} />
        <Route path="/settings" element={renderAdminViews()} />
        <Route path="/users" element={renderAdminViews()} />
        <Route path="/cards" element={renderAdminViews()} />
        <Route path="/" element={renderAdminViews()} />
        {/* Invitation acceptance route - must come before public card routes */}
        <Route path="/invite/:token" element={
          <InvitationAcceptance
            apiCall={apiCall}
            showAlert={showAlert}
            API_ENDPOINT={API_ENDPOINT}
          />
        } />
        {/* Public card route — /:slug only */}
        <Route path="/:slug" element={
          <PublicCardRoute 
            view={view}
            isPublicLoading={isPublicLoading}
            error={error}
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            showAlert={showAlert}
            fetchCardByOrgAndSlug={fetchCardByOrgAndSlug}
            fetchPublicCard={fetchPublicCard}
          />
        } />
        {/* Catch-all for other routes */}
        <Route path="*" element={renderAdminViews()} />
      </Routes>
    </>
  );
}

function PublicCardRoute({ view, isPublicLoading, error, data, settings, darkMode, toggleDarkMode, showAlert, fetchCardByOrgAndSlug, fetchPublicCard }) {
  const params = useParams();
  const location = useLocation();
  
  
  // Track the last fetched pathname to prevent duplicate fetches
  const lastFetchedPathRef = useRef(null);

  // Public cards have one explicit visual theme. The dashboard dark mode must
  // not create a second implicit version of the card. Restore it on exit.
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (wasDark) root.classList.add('dark'); };
  }, []);
  
  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  // This ensures the theme is applied to public cards the same way as admin views
  useEffect(() => {
    const variant = settings?.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [settings]);
  
  // Fetch the card when the route changes
  useEffect(() => {
    const path = location.pathname;
    
    // Single guard: skip if we've already fetched for this exact pathname
    if (lastFetchedPathRef.current === path) {
      return;
    }
    
    // Parse route to determine fetch strategy
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isSingleSlug = pathParts.length === 1;
    
    // Mark this path as being fetched BEFORE calling fetch (prevents race conditions)
    lastFetchedPathRef.current = path;
    
    // Fetch based on route type
    if (isOrgScoped && fetchCardByOrgAndSlug) {
      fetchCardByOrgAndSlug(pathParts[0], pathParts[1]);
    } else if (isSingleSlug && fetchPublicCard) {
      fetchPublicCard(pathParts[0]);
    } else {
      console.error('[PublicCardRoute] No valid fetch strategy for path:', path);
      // Reset ref if we can't fetch (allows retry on next render if functions become available)
      lastFetchedPathRef.current = null;
    }
  }, [location.pathname, fetchCardByOrgAndSlug, fetchPublicCard]);
  
  // Show loading state while fetching card
  const pathParts = location.pathname.substring(1).split('/').filter(p => p);
  const hasPublicRouteParams = params.slug || params.cardSlug || params.orgSlug || pathParts.length > 0;
  
  if (isPublicLoading || ((view === 'loading' || view === 'public-loading') && hasPublicRouteParams)) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-text-muted-subtle dark:text-text-muted-dark">Carregando...</div>
      </div>
    );
  }
  
  // Show 404 if card not found
  if (view === '404') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Cartão Não Encontrado</h1>
          <p className="text-text-secondary dark:text-text-muted-dark">{error || 'O cartão que você está procurando não existe.'}</p>
        </div>
      </div>
    );
  }
  
  // Show card when loaded
  if (view === 'public-card' && data && data.personal) {
    return (
      <PublicCardFrame
        data={data}
        settings={settings}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        showAlert={showAlert}
      />
    );
  }

  // Default: show loading (shouldn't reach here, but safety net)
  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify_center items-center">
      <div className="text-text-muted-subtle dark:text-text-muted-dark">Carregando...</div>
    </div>
  );
}

// Frame do cartão publicado. É usado tanto na rota pública quanto na prévia
// do editor (`contained`), para que o editor mostre exatamente o mesmo
// resultado — mesmo layout, mesma largura, mesmo fundo e mesmo efeito.
function PublicCardFrame({ data, settings, darkMode, toggleDarkMode, showAlert, contained = false }) {
  const publicEffect = (() => {
    if (data.theme?.effect && data.theme.effect !== 'none') return data.theme.effect;
    const themeObj = RUNTIME_THEMES[data.theme?.variant || 'swiish'];
    return themeObj?.effect || 'none';
  })();
  const hasEffect = publicEffect !== 'none';
  const isPageLayout = (data.theme?.layout || 'card') === 'page';
  const cardBgStyle = isPageLayout
    ? { zIndex: 1 }
    : hasEffect
      ? { backgroundColor: 'rgba(255,255,255,0.86)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 1 }
      : { zIndex: 1 };

  // Compute page background from images.bg
  const pageBgStyle = (() => {
    const bg = data.images?.bg;
    if (bg?.type === 'custom' && bg.url) return { backgroundImage: `url(${bg.url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    if (bg?.type === 'solid' && bg.value) return { background: bg.value };
    if (bg?.type === 'gradient' && bg.from && bg.to) return { background: 'linear-gradient(' + (bg.angle || 135) + 'deg, ' + bg.from + ', ' + bg.to + ')' };
    if (bg?.type === 'preset' && bg.value) {
      const preset = BG_PRESETS.find(p => p.id === bg.value);
      if (preset) return preset.style;
    }
    return {};
  })();
  const hasCustomPageBg = !!pageBgStyle.background || !!pageBgStyle.backgroundImage || !!pageBgStyle.backgroundSize;
  const minHeight = contained ? 'min-h-full' : 'min-h-screen';

  return (
    <div
      className={`${minHeight} w-full flex justify-center items-start${isPageLayout ? '' : ' lg:items-center p-0 lg:p-8'} relative${(hasEffect || hasCustomPageBg) ? '' : ' bg-main dark:bg-main-dark bg-main-texture'}`}
      style={hasCustomPageBg ? pageBgStyle : undefined}
    >
      <BackgroundEffect effect={publicEffect} color={data.theme?.effectColor || "#0891b2"} opacity={data.theme?.effectOpacity ?? 60} darkMode={false} contained={contained} />
      <div
        className={isPageLayout
          ? `w-full ${minHeight} relative animate-in fade-in duration-500 flex flex-col`
          : `w-full max-w-md sm:max-w-lg ${minHeight} lg:min-h-0 lg:h-auto lg:rounded-page shadow-2xl overflow-hidden relative animate-in fade-in duration-500 flex flex-col`
        }
        style={cardBgStyle}
      >
        <div className="flex-1">
          <CardDisplay
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            showAlert={showAlert}
          />
        </div>
      </div>
    </div>
  );
}

function CardDisplay({ data, settings, darkMode, toggleDarkMode, showAlert }) {
  const { personal = {}, contact = {}, social = {}, images = {}, theme = { color: 'indigo' }, links = [], privacy = {}, analytics = {} } = data;
  const themeColor = settings?.theme_colors?.find(c => c.name === theme.color);
  const isPageLayout = (theme.layout || 'card') === 'page';
  const avatarPos = theme.avatarPosition || 'center';
  const heroStyle = theme.heroStyle || 'classic';
  // Per-card manual color overrides (only when explicitly set by user)
  const _nameColor     = theme.nameColor     || undefined;
  const _titleColor    = theme.titleColor    || themeColor?.textStyle || getTextColor(theme.color, settings);
  const _bioColor      = theme.bioColor      || undefined;
  const _locationColor = theme.locationColor || undefined;
  const _footerColor   = theme.footerColor   || undefined;
  const _linkColor     = theme.linkColor     || themeColor?.linkStyle || getLinkColor(theme.color, settings);
  const _avatarBorderColor = theme.avatarBorderColor || '#ffffff';
  const _share = {
    background: theme.shareBackground || '#ffffff',
    panel: theme.sharePanelBackground || '#f1f5f9',
    text: theme.shareTextColor || '#0f172a',
    muted: theme.shareMutedColor || '#64748b',
    accent: theme.shareAccentColor || '#0891b2',
    buttonBg: theme.shareButtonBackground || '#e2e8f0',
    buttonText: theme.shareButtonTextColor || '#0f172a',
    footer: theme.shareFooterColor || theme.shareMutedColor || '#64748b',
    heading: theme.shareHeading || 'Compartilhe meu contato',
    simpleLabel: theme.shareSimpleLabel || 'Somente link',
    richLabel: theme.shareRichLabel || 'Detalhes completos',
    closeLabel: theme.shareCloseLabel || 'Fechar',
  };
  // Card-level button style: null when user hasn't set anything (blocks keep Tailwind classes)
  // buttonBg pode ser hex legado OU objeto do BackgroundPicker (sólido/degradê/preset).
  const _resolvedBtnBg = resolveCardBg(theme.buttonBg, BG_PRESETS);
  const _cardBtnStyle = (theme.buttonBg || theme.buttonText)
    ? { bg: _resolvedBtnBg || themeColor?.buttonStyle || getButtonColor(theme.color, settings),
        text: theme.buttonText || '#ffffff' }
    : null;
  // Merged theme color for SocialIcon hover (respects per-card buttonBg when set).
  // Hover só aceita cor sólida — degradê cai no fallback do tema.
  const effectiveThemeColor = _cardBtnStyle
    ? { ...themeColor, buttonStyle: isHexColor(_cardBtnStyle.bg) ? _cardBtnStyle.bg : (themeColor?.buttonStyle || getButtonColor(theme.color, settings)) }
    : themeColor;
  const [showQR, setShowQR] = useState(false);
  const [qrMode, setQrMode] = useState(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine ? 'simple' : 'rich';
    }
    return 'simple';
  });
  const [qrSimpleDataUrl, setQrSimpleDataUrl] = useState('');
  const [qrRichDataUrl, setQrRichDataUrl] = useState('');
  const [offlineQrPayload, setOfflineQrPayload] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [qrError, setQrError] = useState(null);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // ── Analytics tracking (only fires in public view — the slug is
  //    embedded when the card is loaded via a public API, not in editor preview)
  const analyticsId      = data._slug;
  const analyticsEnabled = !!(analyticsId && analytics?.enabled);
  const trackEvent = useCallback((eventType, eventData = {}) => {
    if (!analyticsEnabled) return;
    const device = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      ? 'mobile' : 'desktop';
    fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: analyticsId, event_type: eventType, event_data: eventData, device }),
    }).catch(() => {}); // fire-and-forget, never blocks UX
  }, [analyticsEnabled, analyticsId]);
  // Track visit once on mount (skipped entirely in editor preview)
  useEffect(() => { if (analyticsEnabled) trackEvent('visit'); }, []); // eslint-disable-line


  // Lead capture deep links, using the card owner's contact details
  const ownerPhone = contact.phone || '';
  const ownerEmail = contact.email || '';
  // Extract digits only from E.164 format (e.g., +447779331447 -> 447779331447)
  // This works for both E.164 (+44...) and legacy formats
  const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');

  const whatsappLink = ownerPhoneDigits && ownerPhoneDigits.length >= 8
    ? `https://wa.me/${ownerPhoneDigits}?text=${encodeURIComponent(
        'Olá, nos conhecemos pelo seu cartão ANRVA. Meu nome é ... e meu número/e-mail é ...'
      )}`
    : null;

  const emailLink = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent(
        'Meus detalhes do ANRVA'
      )}&body=${encodeURIComponent(
        'Olá, nos conhecemos pelo seu cartão ANRVA.\nMeu nome é ...\nMeu telefone é ...\nMeu e-mail é ...'
      )}`
    : null;

  const dropCallLink = ownerPhone ? `tel:${ownerPhone}` : null;
  
  // Helper functions for obfuscation
  const obfuscateContact = (value) => {
    if (!value) return '';
    return btoa(value);
  };
  
  const deobfuscateContact = (obfuscated) => {
    if (!obfuscated) return '';
    try {
      return atob(obfuscated);
    } catch (e) {
      return '';
    }
  };
  
  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  // This ensures the theme is applied when CardDisplay renders (both in editor preview and public view)
  useEffect(() => {
    const variant = settings?.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [settings]);
  
  // Set tab title to card owner's name with ANRVA branding
  useEffect(() => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    if (firstName || lastName) {
      const name = `${firstName} ${lastName}`.trim();
      document.title = `${name} | ANRVA Presença Digital`;
    }
  }, [personal]);

  // Apply this card's own theme variant — each card/slug gets its own visual style
  useEffect(() => {
    const variant = data.theme?.variant || settings?.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, data.theme?.fontPair);
  }, [data.theme?.variant, data.theme?.fontPair, settings?.theme_variant]);

  // Dynamic per-card manifest link (per-card app name & start_url)
  // Note: The manifest link is updated synchronously in index.html before React loads
  // This useEffect is just a backup to ensure it's correct after navigation
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const path = window.location.pathname || '';
    const segments = path.replace(/^\//, '').split('/').filter(Boolean);
    const slug = segments[segments.length - 1] || '';
    if (!slug || slug === 'admin') return;

    const head = document.head;
    let link = document.querySelector('link[rel="manifest"]');
    const dynamicHref = `/manifest/${slug}.json`;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'manifest');
      head.appendChild(link);
    }

    if (link.getAttribute('href') !== dynamicHref) {
      link.setAttribute('href', dynamicHref);
    }
  }, []);

  // Handle PWA install prompt (keep button visible until actually installed)
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setDeferredPrompt(null);
    };

    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (isStandalone) {
        setIsPwaInstalled(true);
      }
    };

    checkStandalone();

    // Set up event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const shouldShowInstallButton = !isPwaInstalled;

  // Track online/offline status for UI
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle robots meta tag
  useEffect(() => {
    if (privacy.blockRobots) {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    }
    
    // Cleanup on unmount
    return () => {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    };
  }, [privacy.blockRobots]);

  // Fetch QR code when modal opens; also cache rich payload for offline use
  useEffect(() => {
    if (!showQR) return;

    setQrError(null);

    const pathParts = typeof window !== 'undefined' ? window.location.pathname.substring(1).split('/').filter(Boolean) : [];
    const qrIdentifier = pathParts[pathParts.length - 1] || data._slug || '';
    const canonicalPath = typeof window !== 'undefined' ? (window.location.pathname.endsWith('/') && window.location.pathname !== '/' ? window.location.pathname.slice(0, -1) : window.location.pathname) : '';
    const canonicalUrl = typeof window !== 'undefined' ? window.location.origin + canonicalPath : '';
    const payload = buildQrPayload(canonicalUrl, { personal, contact, social, images, theme });
    // Always cache latest rich payload for offline use
    saveQrPayloadToStorage(payload);

    // Online check – if offline, try to use cached payload instead of hitting API
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (!isOnline) {
      // In offline mode, only rich mode can leverage cached payload;
      // simple (link-only) QR is effectively online-only unless previously loaded.
      if (qrMode === 'rich') {
        const cached = loadQrPayloadFromStorage();
        if (cached) {
          setOfflineQrPayload(cached);
        }
      }
      return;
    }

    if (qrMode === 'simple' && !qrSimpleDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}?path=${encodeURIComponent(canonicalPath)}`, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrSimpleDataUrl(data.qrCode);
          }
        })
        .catch(err => {
          console.error('Failed to fetch simple QR code:', err);
          setQrError('Não foi possível carregar o QR somente de link agora. Tente novamente em instantes.');
        });
    } else if (qrMode === 'rich' && !qrRichDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payload })
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrRichDataUrl(data.qrCode);
          }
          saveQrPayloadToStorage(payload);
          setOfflineQrPayload(payload);
        })
        .catch(err => {
          console.error('Failed to fetch rich QR code:', err);
          const cached = loadQrPayloadFromStorage();
          if (cached) {
            setOfflineQrPayload(cached);
          }
          setQrError('Não foi possível carregar o QR com detalhes completos agora. Seus últimos detalhes salvos ainda estão disponíveis offline.');
        });
    }
  }, [showQR, qrMode, qrSimpleDataUrl, qrRichDataUrl, personal, contact, social, images, theme, data]);

  const generateVCard = () => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    const company = sanitizeText(personal.company || '');
    const title = sanitizeText(personal.title || '');
    const phone = sanitizeText(contact.phone || '');
    const email = sanitizeText(contact.email || '');
    const website = sanitizeText(contact.website || '');
    const bio = sanitizeText(personal.bio || '');
    
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
ORG:${company}
TITLE:${title}
TEL;TYPE=CELL:${phone}
EMAIL;TYPE=WORK:${email}
URL:${website}
NOTE:${bio}
END:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${firstName}_${lastName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentQrDataUrl = qrMode === 'simple' ? qrSimpleDataUrl : qrRichDataUrl;

  const canonicalPathForDisplay = typeof window !== 'undefined'
    ? (window.location.pathname.endsWith('/') && window.location.pathname !== '/'
      ? window.location.pathname.slice(0, -1)
      : window.location.pathname)
    : '';
  const canonicalUrlForDisplay = typeof window !== 'undefined'
    ? window.location.origin + canonicalPathForDisplay
    : '';
  const cardName = `${personal.firstName || ''} ${personal.lastName || ''}`.trim();
  const company = personal.company || '';

  // On mobile the share view is full screen; on desktop it is a centered modal
  if (showQR) {
    return (
      <div className="fixed inset-0 z-[100] flex items-stretch justify-center lg:items-center lg:p-6 lg:bg-black/60 lg:backdrop-blur-sm" onClick={() => setShowQR(false)}>
        <div className="w-full h-full min-h-screen flex flex-col text-center overflow-y-auto lg:max-w-[440px] lg:h-auto lg:min-h-0 lg:max-h-[calc(100vh-3rem)] lg:rounded-[22px] lg:shadow-2xl" style={{ backgroundColor: _share.background, color: _share.text }} onClick={(event) => event.stopPropagation()}>
        {/* QR Code section at top */}
        <div className="flex flex-col items-center justify-start pt-8 px-4 pb-8 lg:pt-6 lg:pb-4 lg:flex-shrink-0">
          <div className="w-[90%] lg:w-72">
            <div className="w-full p-3 rounded-input border-thick flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#ffffff', borderColor: _share.accent }}>
              {currentQrDataUrl ? (
                <img src={currentQrDataUrl} className="w-full aspect-square mix-blend-multiply dark:mix-blend-normal" alt="QR code" />
              ) : qrMode === 'rich' && offlineQrPayload ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-xs space-y-1" style={{ color: _share.muted }}>
                  <span>{isOnline ? 'Detalhes salvos' : 'Modo offline'}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    Este código inclui seus detalhes ANRVA salvos e um link para seu cartão quando escaneado por um dispositivo online.
                  </span>
                </div>
              ) : (!isOnline && qrMode === 'simple' && !qrSimpleDataUrl) ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-xs text-center space-y-1" style={{ color: _share.muted }}>
                  <span>O QR somente de link está disponível quando você está online.</span>
                  <span className="text-[10px] opacity-80 px-1">
                    Mude para \"Detalhes completos\" para usar seu código offline salvo.
                  </span>
                </div>
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-xs text-center" style={{ color: _share.muted }}>
                  {isOnline
                    ? (qrError || 'Carregando seu QR code...')
                    : 'Conecte-se uma vez para gerar e salvar seu QR code para uso offline.'}
                </div>
              )}
            </div>
          </div>

          {/* Card information display */}
          <div className="mt-6 space-y-2 px-4">
            <div className="text-sm font-semibold" style={{ color: _share.accent }}>{sanitizeText(_share.heading)}</div>
            {cardName && (
              <h2 className="text-xl font-bold" style={{ color: _share.text }}>{cardName}</h2>
            )}
            {company && (
              <div className="text-sm" style={{ color: _share.muted }}>{company}</div>
            )}
            {canonicalUrlForDisplay && (
              <div className="text-xs font-mono break-all" style={{ color: _share.muted }}>{canonicalUrlForDisplay}</div>
            )}
          </div>

          {/* Offline note */}
          {!isOnline && offlineQrPayload && (
            <p className="text-xs mt-4 px-4" style={{ color: _share.muted }}>
              Usando os últimos detalhes QR salvos (offline). O código inclui suas informações de contato e um link para seu cartão ANRVA.
            </p>
          )}
        </div>

        {/* Controls and logo section at bottom */}
        <div className="mt-auto pb-4 px-4 space-y-3 lg:pb-4 lg:pt-4 lg:flex-shrink-0">
          {/* Toggle buttons */}
          <div className="flex w-full items-center justify-center gap-1 rounded-full p-1 text-[11px] max-w-md mx-auto" style={{ backgroundColor: _share.panel }}>
            <button
              type="button"
              onClick={() => setQrMode('simple')}
              style={{ backgroundColor: qrMode === 'simple' ? _share.accent : 'transparent', color: qrMode === 'simple' ? _share.background : _share.muted }}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'simple'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {sanitizeText(_share.simpleLabel)}
            </button>
            <button
              type="button"
              onClick={() => setQrMode('rich')}
              style={{ backgroundColor: qrMode === 'rich' ? _share.accent : 'transparent', color: qrMode === 'rich' ? _share.background : _share.muted }}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'rich'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              {sanitizeText(_share.richLabel)}
            </button>
          </div>

          {/* Close button */}
          <button onClick={() => setShowQR(false)} className="w-full max-w-md mx-auto py-3 font-bold rounded-input text-sm transition-opacity hover:opacity-90" style={{ backgroundColor: _share.buttonBg, color: _share.buttonText }}>{sanitizeText(_share.closeLabel)}</button>

          <AnrvaFooter className="mt-4" footerColor={_share.footer} />
        </div>
        </div>
      </div>
    );
  }

  // Otherwise, render the normal card view
  return (
    <div className={`flex flex-col h-full anrva-card${isPageLayout ? '' : ' bg-card dark:bg-card-dark'}`}>
      {heroStyle === 'overlay' ? (
        <HeroOverlay
          personal={personal}
          images={images}
          eyebrow={theme.heroEyebrow}
          actions={theme.heroActions}
          veilColor={(RUNTIME_THEMES[theme.variant]?.colors?.main?.dark) || '#12100c'}
          nameColor={_nameColor}
          titleColor={_titleColor}
          bioColor={_bioColor}
          btnStyle={_cardBtnStyle}
          onShowQR={() => setShowQR(true)}
        />
      ) : (
      <div className={`${isPageLayout ? 'h-56 sm:h-72' : 'h-44'} w-full relative bg-surface dark:bg-surface-dark`}>
        {(() => {
          // Uploaded image takes top priority
          if (images.banner) {
            return <img src={images.banner} className="w-full h-full object-cover" alt="banner" />;
          }
          // Preset / solid / custom from header bg picker
          const hbg = images.headerBg;
          if (hbg?.type === 'custom' && hbg.url) {
            return <img src={hbg.url} className="w-full h-full object-cover" alt="cabeçalho" />;
          }
          if (hbg?.type === 'solid' && hbg.value) {
            return <div className="w-full h-full" style={{ background: hbg.value }} />;
          }
          if (hbg?.type === 'gradient' && hbg.from && hbg.to) {
            const gradient = 'linear-gradient(' + (hbg.angle || 135) + 'deg, ' + hbg.from + ', ' + hbg.to + ')';
            return <div className="w-full h-full" style={{ background: gradient }} />;
          }
          if (hbg?.type === 'preset' && hbg.value) {
            const preset = BG_PRESETS.find(p => p.id === hbg.value);
            if (preset) return <div className="w-full h-full" style={preset.style} />;
          }
          // Fallback: theme gradient
          const gradientStyle = getThemeGradient(theme.color, settings);
          return <div className="w-full h-full opacity-90" style={{ background: gradientStyle }} />;
        })()}
        <div className="absolute top-4 right-4 flex gap-2">
          <button onClick={() => setShowQR(true)} className="bg-white/30 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/40 transition-all border border-white/20 dark:border-white/10 shadow-sm" aria-label="Mostrar QR code" title="Mostrar QR code">
            <Share2 className="w-5 h-5" />
          </button>
          {shouldShowInstallButton && (
            <button
              onClick={async () => {
                if (deferredPrompt) {
                  try {
                    await deferredPrompt.prompt();
                    const { outcome } = await deferredPrompt.userChoice;
                    if (outcome === 'accepted') {
                      setIsPwaInstalled(true);
                    }
                  } catch (e) {
                    console.error('Install prompt failed:', e);
                    if (typeof showAlert === 'function') {
                      showAlert(
                        'Falha ao instalar. Use o menu do navegador: Chrome/Edge (menu de três pontos > Instalar app), Firefox (menu > Instalar), ou Safari (Compartilhar > Adicionar à Tela de Início).',
                        'error',
                        'Falha na Instalação'
                      );
                    }
                  } finally {
                    setDeferredPrompt(null);
                  }
                } else {
                  // Check if app is already installed
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                                      window.navigator.standalone === true;
                  
                  if (isStandalone) {
                    if (typeof showAlert === 'function') {
                      showAlert(
                        'Este aplicativo já está instalado no seu dispositivo.',
                        'info',
                        'Já Instalado'
                      );
                    }
                    setIsPwaInstalled(true);
                  } else {
                    // Provide manual installation instructions
                    const userAgent = navigator.userAgent.toLowerCase();
                    let instructions = 'Para instalar este aplicativo:\n\n';
                    
                    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
                      instructions += 'Chrome/Edge: Clique no menu de três pontos (⋮) na barra de endereços e selecione "Instalar app" ou "Adicionar à Tela de Início".';
                    } else if (userAgent.includes('firefox')) {
                      instructions += 'Firefox: Clique no botão de menu e selecione "Instalar" ou "Adicionar à Tela de Início".';
                    } else if (userAgent.includes('safari')) {
                      instructions += 'Safari (iOS): Toque no botão Compartilhar e selecione "Adicionar à Tela de Início".';
                    } else {
                      instructions += 'Abra o menu do navegador e procure por "Instalar app" ou "Adicionar à Tela de Início".';
                    }
                    
                    instructions += '\n\nObservação: O botão de instalação pode não estar disponível se o app não atender aos requisitos do PWA (service worker, manifesto válido, etc.).';
                    
                    if (typeof showAlert === 'function') {
                      showAlert(
                        instructions,
                        'info',
                        'Instalar ANRVA'
                      );
                    } else {
                      // Fallback if showAlert is not available
                      alert(instructions);
                    }
                  }
                }
              }}
              className="bg-white/30 dark:bg-black/30 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-white/40 dark:hover:bg-black/40 transition-all border border-white/20 dark:border-white/10 shadow-sm"
              aria-label="Instalar app para acesso offline"
              title="Instalar app para acesso offline"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          {!isOnline && offlineQrPayload && (
            <span className="hidden xs:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold bg-amber-500/80 text-white shadow-sm">
              QR offline pronto
            </span>
          )}
        </div>
      </div>
      )}

      <div className={`${isPageLayout ? 'max-w-3xl mx-auto w-full' : ''} px-6 pb-6 ${heroStyle === 'overlay' ? 'pt-8' : '-mt-16'} relative flex-1 flex flex-col min-h-0`}>
        {heroStyle !== 'overlay' && (avatarPos === 'left' ? (
          <div className="flex items-end gap-4 mb-8">
            <div className="w-20 h-20 flex-shrink-0 rounded-full border-4 shadow-xl overflow-hidden bg-card" style={{ borderColor: _avatarBorderColor }}>
              {images.avatar ? <img src={images.avatar} className="w-full h-full object-cover" alt="avatar" /> : <div className="w-full h-full bg-surface dark:bg-surface-dark flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark"><User className="w-8 h-8" /></div>}
            </div>
            <div className="space-y-0.5 pb-1 min-w-0">
              <h1 className={`text-2xl font-bold tracking-tight truncate${_nameColor ? '' : ' text-text-primary dark:text-text-primary-dark'}`} style={_nameColor ? { color: _nameColor } : undefined}>{sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || 'Sem título'}</h1>
              {personal.title && <div className="text-base font-medium" style={{ color: _titleColor }}>{sanitizeText(personal.title)}</div>}
              {personal.company && <div className="flex items-center text-text-muted dark:text-text-muted-dark text-xs gap-1.5"><Briefcase className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{sanitizeText(personal.company)}</span></div>}
              {personal.location && <div className={`flex items-center text-xs gap-1.5 mt-0.5${_locationColor ? '' : ' text-text-muted-subtle dark:text-text-muted-dark'}`} style={_locationColor ? { color: _locationColor } : undefined}><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{sanitizeText(personal.location)}</span></div>}
            </div>
          </div>
        ) : (
          <>
            <div className={`w-32 h-32 min-h-[8rem] flex-shrink-0 rounded-full border-avatar shadow-xl overflow-hidden bg-card relative mb-4`} style={{ borderColor: _avatarBorderColor, alignSelf: isPageLayout ? 'center' : undefined }}>
              {images.avatar ? <img src={images.avatar} className="w-full h-full object-cover" alt="avatar" /> : <div className="w-full h-full bg-surface dark:bg-surface-dark flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark"><User className="w-12 h-12" /></div>}
            </div>
            <div className={`space-y-1 mb-8${isPageLayout ? ' text-center' : ''}`}>
              <h1 className={`text-3xl font-bold tracking-tight${_nameColor ? '' : ' text-text-primary dark:text-text-primary-dark'}`} style={_nameColor ? { color: _nameColor } : undefined}>{sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || 'Sem título'}</h1>
              {personal.title && <div className="text-lg font-medium" style={{ color: _titleColor }}>{sanitizeText(personal.title)}</div>}
              {personal.company && <div className={`flex items-center text-text-muted dark:text-text-muted-dark text-sm gap-2${isPageLayout ? ' justify-center' : ''}`}><Briefcase className="w-4 h-4" /><span>{sanitizeText(personal.company)}</span></div>}
              {personal.location && <div className={`flex items-center text-sm gap-2 mt-1${isPageLayout ? ' justify-center' : ''}${_locationColor ? '' : ' text-text-muted-subtle dark:text-text-muted-dark'}`} style={_locationColor ? { color: _locationColor } : undefined}><MapPin className="w-4 h-4" /><span>{sanitizeText(personal.location)}</span></div>}
            </div>
          </>
        ))}

        {heroStyle !== 'overlay' && personal.bio && <div className="mb-8"><p className={`leading-relaxed text-sm${_bioColor ? '' : ' text-text-secondary dark:text-text-secondary-dark'}`} style={_bioColor ? { color: _bioColor } : undefined} dangerouslySetInnerHTML={{ __html: sanitizeHTML(personal.bio) }}></p></div>}

        {/* Custom Blocks */}
        {(data.blocks || []).length > 0 && (() => {
          const blocks = data.blocks;
          const rows = [];
          let i = 0;
          while (i < blocks.length) {
            const b = blocks[i];
            if (b.width === 'half' && i + 1 < blocks.length && blocks[i + 1].width === 'half') {
              rows.push({ kind: 'pair', items: [blocks[i], blocks[i + 1]] });
              i += 2;
            } else {
              rows.push({ kind: 'single', items: [b] });
              i++;
            }
          }
          return (
            <div className="mb-8 space-y-3">
              {rows.map((row, idx) =>
                row.kind === 'pair' ? (
                  <div key={idx} className="grid grid-cols-2 gap-3">
                    {row.items.map(b => <BlockRenderer key={b.id} block={b} themeColor={effectiveThemeColor} settings={settings} onTrack={trackEvent} cardData={data} btnStyle={_cardBtnStyle} />)}
                  </div>
                ) : (
                  <div key={idx}>
                    <BlockRenderer block={row.items[0]} themeColor={effectiveThemeColor} settings={settings} onTrack={trackEvent} cardData={data} btnStyle={_cardBtnStyle} />
                  </div>
                )
              )}
            </div>
          );
        })()}

        {links.length > 0 && (
          <div className="flex flex-col gap-3 mb-8">
            {links.map(link => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackEvent('link_click', { label: link.title, url: link.url })}
                className="flex items-center p-4 rounded-input border transition-all active:scale-[0.99]"
                style={_cardBtnStyle
                  ? { background: _cardBtnStyle.bg, color: _cardBtnStyle.text || '#fff', borderColor: isHexColor(_cardBtnStyle.bg) ? _cardBtnStyle.bg + '40' : 'transparent' }
                  : { color: _linkColor, backgroundColor: _linkColor + '15', borderColor: _linkColor + '30' }}
                onMouseEnter={e => { e.currentTarget.style.filter = _cardBtnStyle ? 'brightness(1.1)' : ''; e.currentTarget.style.background = _cardBtnStyle ? _cardBtnStyle.bg : (_linkColor + '25'); }}
                onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.background = _cardBtnStyle ? _cardBtnStyle.bg : (_linkColor + '15'); }}
              >
                <div className={`mr-4 p-2 rounded-container transition-transform link-icon-container${_cardBtnStyle ? '' : ' bg-input-bg dark:bg-input-bg-dark shadow-sm'}`}>
                  {React.createElement(ICON_MAP[link.icon] || LinkIcon, { className: "w-5 h-5", style: _cardBtnStyle ? { color: 'inherit' } : undefined })}
                </div>
                <span className="font-semibold text-sm flex-1">{sanitizeText(link.title || '')}</span>
                <ExternalLink className="w-4 h-4 opacity-50" />
              </a>
            ))}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 mb-8">
           <SocialIcon url={contact.website} icon={Globe} label="Web" themeColor={themeColor} />
           <SocialIcon url={social.linkedin} icon={Linkedin} label="LinkedIn" themeColor={themeColor} />
           <SocialIcon url={social.twitter} icon={Twitter} label="X" themeColor={themeColor} />
           <SocialIcon url={social.instagram} icon={Instagram} label="Insta" themeColor={themeColor} />
           <SocialIcon url={social.github} icon={Github} label="Git" themeColor={themeColor} />
        </div>

        <AnrvaFooter className="mt-auto" footerColor={_footerColor} />
      </div>
    </div>
  );
}

function SortableLinkItem({ link, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: link.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return children({ setNodeRef, style, attributes, listeners });
}

function EditorView({ data, setData, onBack, onSave, slug, settings, csrfToken, showAlert, darkMode, toggleDarkMode, isSaving, isSuccess, onLogout, availableThemes = [], setAvailableThemes }) {
  const [activeTab, setActiveTab] = useState('details');
  const [isUploading, setIsUploading] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );
  
  const handleInputChange = (section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  // When a theme is selected, also auto-apply its preset background
  const handleThemeSelect = (themeEntry) => {
    setData(prev => ({
      ...prev,
      theme: { ...prev.theme, variant: themeEntry.id },
      ...(themeEntry.presetBg ? { images: { ...prev.images, bg: themeEntry.presetBg } } : {}),
    }));
  };

  const handleImageUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_ENDPOINT}/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        body: formData
      });
      
      if (res.ok) {
        const { url } = await res.json();
        setData(prev => ({ ...prev, images: { ...prev.images, [type]: url } }));
      } else {
        if (showAlert) showAlert('Falha no upload', 'error');
      }
    } catch (error) {
      if (showAlert) showAlert('Erro no upload', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const addLink = () => {
    const newLink = { id: Date.now(), title: '', url: '', icon: 'link' };
    setData(prev => ({ ...prev, links: [...prev.links, newLink] }));
  };
  const removeLink = (id) => {
    setData(prev => ({ ...prev, links: prev.links.filter(l => l.id !== id) }));
  };
  const updateLink = (id, field, value) => {
    setData(prev => ({ ...prev, links: prev.links.map(l => l.id === id ? { ...l, [field]: value } : l) }));
  };

  const reorderLinks = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;
    setData(prev => ({ ...prev, links: arrayMove(prev.links, oldIndex, newIndex) }));
  };

  const moveLinkUp = (index) => {
    if (index <= 0) return;
    reorderLinks(index, index - 1);
  };

  const moveLinkDown = (index) => {
    if (index >= data.links.length - 1) return;
    reorderLinks(index, index + 1);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.links.findIndex(link => link.id === active.id);
    const newIndex = data.links.findIndex(link => link.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderLinks(oldIndex, newIndex);
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 bg-card dark:bg-card-dark border-r border-border dark:border-border-dark h-auto lg:h-screen overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-border-subtle dark:border-border-dark flex items-center justify-between bg-card dark:bg-card-dark sticky top-0 z-10">
          <div className="flex items-center gap-4">
             {!onLogout && <button onClick={onBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark"><ArrowLeft className="w-5 h-5"/></button>}
             <div>
               <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">Editando: {slug}</h1>
             </div>
          </div>
          <div className="flex items-center gap-2">
            {onLogout && (
              <button
                onClick={onLogout}
                className="px-4 py-2 rounded-full text-sm font-medium text-text-muted dark:text-text-muted-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
              >
                Sair
              </button>
            )}
            <button
              onClick={onSave}
              disabled={isSaving}
              className="px-5 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : isSuccess ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Salvar
            </button>
          </div>
        </div>

        <div className="flex-1 p-6 space-y-8">
           <div className="flex p-1 bg-surface dark:bg-surface-dark rounded-input mb-6">
              {['details', 'links', 'blocos', 'images', 'style', 'privacy'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2 text-sm font-medium rounded-button capitalize transition-all ${activeTab === tab ? 'bg-card dark:bg-surface-dark shadow text-text-primary dark:text-text-primary-dark' : 'text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'}`}>{tab}</button>
              ))}
           </div>

           {activeTab === 'details' && (
             <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Nome / Título do Cartão" value={data.personal.firstName} onChange={v => handleInputChange('personal', 'firstName', v)} />
                  <Input label="Sobrenome / Complemento" value={data.personal.lastName} onChange={v => handleInputChange('personal', 'lastName', v)} />
                  <Input label="Cargo / Tagline" value={data.personal.title} onChange={v => handleInputChange('personal', 'title', v)} />
                </div>
                <Input label="Localização" value={data.personal.location} onChange={v => handleInputChange('personal', 'location', v)} />
                <TextArea label="Bio" value={data.personal.bio} onChange={v => handleInputChange('personal', 'bio', v)} />
                <div className="h-px bg-surface dark:bg-surface-dark" />
                <div className="space-y-4">
                  <Input icon={Mail} placeholder="E-mail" value={data.contact.email} onChange={v => handleInputChange('contact', 'email', v)} type="email" />
                  <div className="space-y-1">
                    <PhoneInput
                      international
                      defaultCountry="GB"
                      value={data.contact.phone || ''}
                      onChange={(value) => handleInputChange('contact', 'phone', value || '')}
                      placeholder="Phone"
                      flags={flags}
                    />
                  </div>
                  <Input icon={Globe} placeholder="Site" value={data.contact.website} onChange={v => handleInputChange('contact', 'website', v)} type="url" />
                  <Input icon={Linkedin} placeholder="LinkedIn" value={data.social.linkedin} onChange={v => handleInputChange('social', 'linkedin', v)} type="url" />
                  <Input icon={Twitter} placeholder="Twitter / X" value={data.social.twitter} onChange={v => handleInputChange('social', 'twitter', v)} type="url" />
                  <Input icon={Instagram} placeholder="Instagram" value={data.social.instagram} onChange={v => handleInputChange('social', 'instagram', v)} type="url" />
                  <Input icon={Github} placeholder="Github" value={data.social.github} onChange={v => handleInputChange('social', 'github', v)} type="url" />
                </div>
             </div>
           )}

           {activeTab === 'links' && (
             <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Links Personalizados</h3>
                    {settings?.allow_links_customisation !== false ? (
                      <button onClick={addLink} className="text-sm font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1"><Plus className="w-4 h-4" /> Adicionar Link</button>
                    ) : (
                      <div className="flex items-center gap-2 text-text-muted dark:text-text-muted-dark">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm">Bloqueado</span>
                      </div>
                    )}
                </div>
                {settings?.allow_links_customisation === false ? (
                  <LockedOption message="Sua organização desativou links personalizados. Entre em contato com seu administrador para habilitar este recurso.">
                    <div className="space-y-4">
                      {data.links.map((link, index) => (
                        <div key={link.id} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                          <div className="grid gap-3">
                            <div className="flex gap-3 items-center">
                              <div className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0">
                                {React.createElement(ICON_MAP[link.icon] || LinkIcon, { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                              </div>
                              <input type="text" value={link.title} disabled className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                            </div>
                            <input type="text" value={link.url} disabled className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                          </div>
                        </div>
                      ))}
                      {data.links.length === 0 && (
                        <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                          Nenhum link personalizado ainda.
                        </div>
                      )}
                    </div>
                  </LockedOption>
                ) : (
                <div className="space-y-4">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={data.links.map(link => link.id)} strategy={verticalListSortingStrategy}>
                        {data.links.map((link, index) => (
                          <SortableLinkItem key={link.id} link={link}>
                            {({ setNodeRef, style, attributes, listeners }) => (
                              <div
                                ref={setNodeRef}
                                style={style}
                                className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark relative"
                              >
                                <div className="absolute top-2 left-2 flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action"
                                    {...attributes}
                                    {...listeners}
                                    aria-label="Arrastar para reordenar"
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="absolute top-2 right-10 flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveLinkUp(index)}
                                    disabled={index === 0}
                                    className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                    aria-label="Mover link para cima"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveLinkDown(index)}
                                    disabled={index === data.links.length - 1}
                                    className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                    aria-label="Mover link para baixo"
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </button>
                                </div>
                                <button onClick={() => removeLink(link.id)} className="absolute top-2 right-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark rounded-full p-1 transition-colors" aria-label="Remover link">
                                  <X className="w-4 h-4"/>
                                </button>
                                <div className="grid gap-3 pt-6">
                                  <div className="flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0">
                                      {React.createElement(ICON_MAP[link.icon], { className: "w-5 h-5 text-text-secondary dark:text-text-secondary-dark" })}
                                    </div>
                                    <input 
                                      type="text" 
                                      placeholder="Título do Link (ex.: Baixar CV)"
                                      value={link.title}
                                      onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                                      className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                    />
                                  </div>
                                  <input 
                                    type="text" 
                                    placeholder="https://..."
                                    value={link.url}
                                    onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                    className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                  />
                                  {/* Icon Picker */}
                                  <IconPicker selected={link.icon} onChange={key => updateLink(link.id, 'icon', key)} />
                                </div>
                              </div>
                            )}
                          </SortableLinkItem>
                        ))}
                      </SortableContext>
                    </DndContext>
                    {data.links.length === 0 && (
                        <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                            Nenhum link personalizado ainda.
                        </div>
                    )}
                </div>
                )}
             </div>
           )}

           {activeTab === 'blocos' && (
             <BlocksEditor
               blocks={data.blocks || []}
               onChange={(blocks) => setData(prev => ({ ...prev, blocks }))}
               csrfToken={csrfToken}
               showAlert={showAlert}
             />
           )}

           {activeTab === 'images' && (
              <div className="space-y-8">
                {/* Page Background Library */}
                <BackgroundPicker
                  label="Fundo da Página"
                  value={data.images.bg}
                  onChange={bg => handleInputChange('images', 'bg', bg)}
                  csrfToken={csrfToken}
                />
                <div className="h-px bg-border-subtle dark:bg-surface-dark" />
                {/* Header Background */}
                <BackgroundPicker
                  label="Fundo do Cabeçalho"
                  value={data.images.headerBg}
                  onChange={bg => handleInputChange('images', 'headerBg', bg)}
                  csrfToken={csrfToken}
                />
                <div className="h-px bg-border-subtle dark:bg-surface-dark" />
                {/* Profile photo + banner upload */}
                {settings?.allow_image_customisation === false ? (
                  <LockedOption message="Sua organização desativou o upload de imagens personalizadas. Entre em contato com seu administrador para habilitar este recurso.">
                    <div className="space-y-8">
                      <ImageUpload label="Foto de Perfil" image={data.images.avatar} onUpload={() => {}} onRemove={() => {}} disabled={true} />
                      <ImageUpload label="Imagem do Cabeçalho" image={data.images.banner} onUpload={() => {}} onRemove={() => {}} isBanner disabled={true} />
                    </div>
                  </LockedOption>
                ) : (
                  <>
                    {isUploading && <div className="text-center text-sm text-cyan-600 dark:text-cyan-400 animate-pulse">Enviando imagem...</div>}
                    <ImageUpload label="Foto de Perfil" image={data.images.avatar} onUpload={e => handleImageUpload('avatar', e)} onRemove={() => handleInputChange('images', 'avatar', null)} />
                    <ImageUpload label="Imagem do Cabeçalho" image={data.images.banner} onUpload={e => handleImageUpload('banner', e)} onRemove={() => handleInputChange('images', 'banner', null)} isBanner />
                  </>
                )}
              </div>
           )}

            {activeTab === 'style' && (
               <div className="space-y-6">
                 {/* ── Tema Visual por Cartão ─────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Tema Visual</label>
                   <div className="grid grid-cols-2 gap-2">
                     {(availableThemes.length ? availableThemes : [{id:'swiish',displayName:'ANRVA Padrão',swatchColors:['#0d1b2e','#0891b2','#f5f5f4']},{id:'minimal',displayName:'Minimal',swatchColors:['#1c1917','#ffffff','#a3a3a3']}]).map(t => {
                       const isSelected = (data.theme?.variant || 'swiish') === t.id;
                       return (
                         <button
                           key={t.id}
                           onClick={() => handleThemeSelect(t)}
                           className={`relative flex flex-col items-start gap-2 p-3 rounded-input border transition-all text-left ${isSelected ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}
                         >
                           <div className="flex gap-1">
                             {(t.swatchColors || []).map((c, i) => (
                               <span key={i} className="w-5 h-5 rounded-full border border-black/10" style={{ backgroundColor: c }} />
                             ))}
                           </div>
                           <span className="text-xs font-medium text-text-primary dark:text-text-primary-dark leading-tight">{t.displayName}</span>
                           {isSelected && <Check className="w-3.5 h-3.5 text-action dark:text-action-dark absolute top-2 right-2" />}
                         </button>
                       );
                     })}
                   </div>
                 </div>

                 {/* ── Tipografia ─────────────────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Tipografia</label>
                   <div className="grid grid-cols-2 gap-2">
                     {Object.entries(FONT_PAIRS).map(([key, fp]) => {
                       const isSel = (data.theme?.fontPair || 'system') === key;
                       return (
                         <button key={key} onClick={() => handleInputChange('theme', 'fontPair', key)}
                           className={`flex flex-col items-start gap-1 py-3 px-3 rounded-input border transition-all text-left ${isSel ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}>
                           <span className="text-sm font-semibold text-text-primary dark:text-text-primary-dark" style={fp.display ? { fontFamily: fp.display } : undefined}>{fp.label}</span>
                           <span className="text-[11px] text-text-muted dark:text-text-muted-dark" style={fp.body ? { fontFamily: fp.body } : undefined}>Aa Bb Cc — 123</span>
                           {isSel && <Check className="w-3 h-3 text-action dark:text-action-dark" />}
                         </button>
                       );
                     })}
                   </div>
                   <p className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark mt-2">Alguns temas já vêm com uma tipografia própria; aqui você força outra.</p>
                 </div>

                 {/* ── Hero (cabeçalho) ───────────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Estilo do Hero</label>
                   <div className="grid grid-cols-2 gap-2">
                     {[
                       { id: 'classic', label: 'Clássico',    desc: 'Banner + foto + nome' },
                       { id: 'overlay', label: 'Sobreposto',  desc: 'Texto sobre a imagem' },
                     ].map(opt => {
                       const isSel = (data.theme?.heroStyle || 'classic') === opt.id;
                       return (
                         <button key={opt.id} onClick={() => handleInputChange('theme', 'heroStyle', opt.id)}
                           className={`flex flex-col items-center gap-1 py-3 px-2 rounded-input border transition-all text-center ${isSel ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}>
                           <span className="text-[11px] font-semibold text-text-primary dark:text-text-primary-dark leading-tight">{opt.label}</span>
                           <span className="text-[10px] text-text-muted dark:text-text-muted-dark leading-tight">{opt.desc}</span>
                           {isSel && <Check className="w-3 h-3 text-action dark:text-action-dark" />}
                         </button>
                       );
                     })}
                   </div>
                   {(data.theme?.heroStyle === 'overlay') && (
                     <div className="mt-3 space-y-3 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark">
                       <div>
                         <label className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1 block">Olho-d'água do Hero</label>
                         <input type="text" value={data.theme?.heroEyebrow || ''} maxLength={40}
                           onChange={e => handleInputChange('theme', 'heroEyebrow', e.target.value)}
                           placeholder="ex: Compositor"
                           className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:border-action dark:focus:border-action-dark" />
                       </div>
                       <div className="space-y-2">
                         <label className="text-xs text-text-secondary dark:text-text-secondary-dark block">Botões do Hero (até 2)</label>
                         {[0, 1].map(i => {
                           const act = (data.theme?.heroActions || [])[i] || { label: '', url: '', variant: i === 0 ? 'solid' : 'outline' };
                           const setAct = patch => {
                             const arr = [ ...(data.theme?.heroActions || []) ];
                             arr[i] = { ...act, ...patch };
                             handleInputChange('theme', 'heroActions', arr.filter(a => a && (a.label || a.url)));
                           };
                           return (
                             <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                               <input type="text" value={act.label} placeholder={`Texto ${i + 1}`} onChange={e => setAct({ label: e.target.value })}
                                 className="px-2 py-1.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-xs focus:outline-none" />
                               <input type="url" value={act.url} placeholder="https://..." onChange={e => setAct({ url: e.target.value })}
                                 className="px-2 py-1.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-xs focus:outline-none" />
                               <select value={act.variant || 'solid'} onChange={e => setAct({ variant: e.target.value })}
                                 className="px-1 py-1.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-xs focus:outline-none">
                                 <option value="solid">Cheio</option>
                                 <option value="outline">Contorno</option>
                               </select>
                             </div>
                           );
                         })}
                       </div>
                       <p className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark">Use "Página Cheia" no layout e envie uma imagem de Cabeçalho na aba Imagens.</p>
                     </div>
                   )}
                 </div>

                 {/* ── Efeito de Fundo ────────────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Efeito de Fundo</label>
                   <div className="grid grid-cols-3 gap-2">
                     {[
                       { id: 'none',      label: 'Nenhum',     icon: '○' },
                       { id: 'stars',     label: 'Estrelas',   icon: '✦' },
                       { id: 'geometric', label: 'Geométrico', icon: '◆' },
                       { id: 'network',   label: 'Rede',       icon: '⬡' },
                       { id: 'modern',    label: 'Moderno',    icon: '◉' },
                       { id: 'bubbles',   label: 'Bolhas',     icon: '◌' },
                       { id: 'rain',      label: 'Chuva',      icon: '╱' },
                       { id: 'orbits',    label: 'Órbitas',    icon: '◎' },
                       { id: 'beach',     label: 'Praia',      icon: '☀' },
                       { id: 'hearts',    label: 'Corações',   icon: '♡' },
                       { id: 'snacks',    label: 'Lanches',    icon: '◒' },
                       { id: 'beauty',    label: 'Beauty',     icon: '✧' },
                       { id: 'fit',       label: 'Fit',        icon: '↔' },
                       { id: 'music',     label: 'Música',     icon: '♫' },
                       { id: 'pet',       label: 'Pet',        icon: '♢' },
                       { id: 'internet',  label: 'Internet',   icon: '⌁' },
                     ].map(ef => {
                       const currentEffect = data.theme?.effect != null
                         ? data.theme.effect
                         : (RUNTIME_THEMES[data.theme?.variant || 'swiish']?.effect || 'none');
                       const isSelected = currentEffect === ef.id;
                       return (
                         <button
                           key={ef.id}
                           onClick={() => handleInputChange('theme', 'effect', ef.id)}
                           className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-input border transition-all text-center ${isSelected ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}
                         >
                           <span className="text-lg leading-none">{ef.icon}</span>
                           <span className="text-[11px] font-medium text-text-primary dark:text-text-primary-dark leading-tight">{ef.label}</span>
                           {isSelected && <Check className="w-3 h-3 text-action dark:text-action-dark" />}
                         </button>
                       );
                     })}
                   </div>
                   {((data.theme?.effect != null ? data.theme.effect : (RUNTIME_THEMES[data.theme?.variant || "swiish"]?.effect || "none")) !== "none") && (
                     <div className="mt-4 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark space-y-3">
                       <div className="flex items-center justify-between gap-3">
                         <div>
                           <div className="text-xs font-medium text-text-primary dark:text-text-primary-dark">Cor e intensidade</div>
                           <div className="text-[10px] text-text-muted dark:text-text-muted-dark">30% a 100% da cor selecionada</div>
                         </div>
                         <div className="flex items-center gap-2">
                           <label className="relative w-11 h-11 rounded-input border border-border dark:border-border-dark overflow-hidden cursor-pointer" title="Selecionar cor do efeito">
                             <span className="absolute inset-0" style={{ backgroundColor: data.theme?.effectColor || "#0891b2" }} />
                             <input type="color" value={data.theme?.effectColor || "#0891b2"} onChange={e => handleInputChange("theme", "effectColor", e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                           </label>
                           <EyedropperButton onPick={color => handleInputChange("theme", "effectColor", color)} title="Capturar cor do efeito" />
                         </div>
                       </div>
                       <div className="flex items-center gap-3">
                         <div className="w-7 h-7 shrink-0 rounded border border-border dark:border-border-dark" title="Mais transparente (30%)" style={{ backgroundColor: "#fff", backgroundImage: "linear-gradient(45deg,#cbd5e1 25%,transparent 25%),linear-gradient(-45deg,#cbd5e1 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#cbd5e1 75%),linear-gradient(-45deg,transparent 75%,#cbd5e1 75%)", backgroundSize: "10px 10px", backgroundPosition: "0 0,0 5px,5px -5px,-5px 0" }} />
                         <input type="range" min="30" max="100" step="1" value={data.theme?.effectOpacity ?? 60} onChange={e => handleInputChange("theme", "effectOpacity", Number(e.target.value))} className="flex-1 cursor-pointer" style={{ accentColor: data.theme?.effectColor || "#0891b2" }} aria-label="Intensidade da cor do efeito" />
                         <div className="w-7 h-7 shrink-0 rounded border border-border dark:border-border-dark" title="Cor sólida (100%)" style={{ backgroundColor: data.theme?.effectColor || "#0891b2" }} />
                         <span className="w-9 text-right text-[11px] font-mono text-text-muted dark:text-text-muted-dark">{data.theme?.effectOpacity ?? 60}%</span>
                       </div>
                     </div>
                   )}
                 </div>

                 <div className="h-px bg-border-subtle dark:bg-border-dark" />

                 {/* ── Layout da Página ───────────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Layout da Página</label>
                   <div className="grid grid-cols-2 gap-2">
                     {[
                       { id: 'card',  label: 'Cartão',       desc: 'Moldura centralizada', icon: '▭' },
                       { id: 'page',  label: 'Página Cheia', desc: 'Ocupa toda a tela',    icon: '⬜' },
                     ].map(opt => {
                       const isSel = (data.theme?.layout || 'card') === opt.id;
                       return (
                         <button key={opt.id} onClick={() => handleInputChange('theme', 'layout', opt.id)}
                           className={`flex flex-col items-center gap-1 py-3 px-2 rounded-input border transition-all text-center ${isSel ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}>
                           <span className="text-lg leading-none">{opt.icon}</span>
                           <span className="text-[11px] font-semibold text-text-primary dark:text-text-primary-dark leading-tight">{opt.label}</span>
                           <span className="text-[10px] text-text-muted dark:text-text-muted-dark leading-tight">{opt.desc}</span>
                           {isSel && <Check className="w-3 h-3 text-action dark:text-action-dark" />}
                         </button>
                       );
                     })}
                   </div>
                 </div>

                 <div className="h-px bg-border-subtle dark:bg-border-dark" />

                 {/* ── Posição da Foto ────────────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-3 block">Posição da Foto de Perfil</label>
                   <div className="grid grid-cols-2 gap-2">
                     {[
                       { id: 'center', label: 'Centralizada', icon: '⊙' },
                       { id: 'left',   label: 'Esquerda',     icon: '⊕' },
                     ].map(opt => {
                       const isSel = (data.theme?.avatarPosition || 'center') === opt.id;
                       return (
                         <button key={opt.id} onClick={() => handleInputChange('theme', 'avatarPosition', opt.id)}
                           className={`flex flex-col items-center gap-1 py-3 px-2 rounded-input border transition-all text-center ${isSel ? 'border-action dark:border-action-dark bg-action/5 dark:bg-action-dark/10' : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark'}`}>
                           <span className="text-lg leading-none">{opt.icon}</span>
                           <span className="text-[11px] font-semibold text-text-primary dark:text-text-primary-dark leading-tight">{opt.label}</span>
                           {isSel && <Check className="w-3 h-3 text-action dark:text-action-dark" />}
                         </button>
                       );
                     })}
                   </div>
                 </div>

                 <div className="h-px bg-border-subtle dark:bg-border-dark" />
                 {/* ── Cor do Perfil ──────────────────────────────── */}
                 {settings?.allow_theme_customisation === false ? (
                   <LockedOption message="Sua organização desativou a personalização de cores do tema. Seu cartão usará a cor padrão do tema.">
                     <div className="flex flex-wrap gap-4">
                       {(settings?.theme_colors || []).map(color => (
                         <div 
                          key={color.name} 
                          className={`w-12 h-12 rounded-full relative ${data.theme.color === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                          style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                        >
                          {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                        </div>
                       ))}
                     </div>
                   </LockedOption>
                 ) : (
                   <div className="flex flex-wrap gap-4">
                     {(settings?.theme_colors || []).map(color => (
                       <button 
                        key={color.name} 
                        onClick={() => handleInputChange('theme', 'color', color.name)} 
                        className={`w-12 h-12 rounded-full relative hover:scale-110 ${data.theme.color === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                        style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                      >
                        {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                      </button>
                     ))}
                   </div>
                 )}

                 <div className="h-px bg-border-subtle dark:bg-border-dark" />

                 {/* ── Botão — fundo (sólido ou degradê) ──────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-1 block">Botão — fundo</label>
                   <p className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark mb-2">Cor sólida ou degradê. Vale para os botões dos blocos e dos links.</p>
                   <BackgroundPicker
                     label="Fundo do botão"
                     value={
                       (data.theme?.buttonBg && typeof data.theme.buttonBg === 'object')
                         ? data.theme.buttonBg
                         : (typeof data.theme?.buttonBg === 'string' && data.theme.buttonBg
                             ? { type: 'solid', value: data.theme.buttonBg }
                             : null)
                     }
                     onChange={bg => handleInputChange('theme', 'buttonBg', bg)}
                     csrfToken={csrfToken}
                     showImageUpload={false}
                   />
                 </div>

                 <div className="h-px bg-border-subtle dark:bg-border-dark" />

                 {/* ── Cores Personalizadas ───────────────────────── */}
                 <div>
                   <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark mb-1 block">Cores Personalizadas</label>
                   <p className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark mb-3">Sobrescreve as cores do tema escolhido. Clique no × para voltar ao padrão do tema.</p>
                   <div className="space-y-2">
                     {[
                       { key: 'nameColor',     label: 'Nome' },
                       { key: 'titleColor',    label: 'Cargo / Tagline' },
                       { key: 'bioColor',      label: 'Bio / Descrição' },
                       { key: 'locationColor', label: 'Localização' },
                       { key: 'footerColor',   label: 'Footer — texto' },
                       { key: 'buttonText',    label: 'Botão — texto' },
                       { key: 'linkColor',     label: 'Links — cor de destaque' },
                       { key: 'avatarBorderColor', label: 'Foto — cor da borda' },
                       { key: 'shareBackground', label: 'Compartilhar — fundo' },
                       { key: 'sharePanelBackground', label: 'Compartilhar — painel' },
                       { key: 'shareTextColor', label: 'Compartilhar — texto principal' },
                       { key: 'shareMutedColor', label: 'Compartilhar — texto secundário' },
                       { key: 'shareAccentColor', label: 'Compartilhar — destaque' },
                       { key: 'shareButtonBackground', label: 'Compartilhar — botão' },
                       { key: 'shareButtonTextColor', label: 'Compartilhar — texto do botão' },
                       { key: 'shareFooterColor', label: 'Compartilhar — footer' },
                     ].map(({ key, label }) => (
                       <div key={key} className="flex items-center gap-3 py-1">
                         <span className="text-xs text-text-secondary dark:text-text-secondary-dark flex-1 truncate">{label}</span>
                         <div className="flex items-center gap-2 flex-shrink-0">
                           {data.theme?.[key] ? (
                             <button
                               onClick={() => handleInputChange('theme', key, '')}
                               className="text-[13px] leading-none text-text-muted dark:text-text-muted-dark hover:text-red-500 dark:hover:text-red-400 transition-colors"
                               title="Restaurar padrão do tema"
                             >×</button>
                           ) : (
                             <span className="w-[13px]" />
                           )}
                           <label className="relative cursor-pointer group">
                             <span
                               className="w-7 h-7 rounded border-2 border-border dark:border-border-dark block transition-transform group-hover:scale-110"
                               style={{ background: data.theme?.[key] || CARD_THEME_COLOR_DEFAULTS[key] || (key === 'buttonText' ? '#ffffff' : '#cccccc') }}
                             />
                             <input
                               type="color"
                               value={data.theme?.[key] || CARD_THEME_COLOR_DEFAULTS[key] || (key === 'buttonText' ? '#ffffff' : '#888888')}
                               onChange={e => handleInputChange('theme', key, e.target.value)}
                               className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                             />
                           </label>
                           <EyedropperButton compact onPick={color => handleInputChange("theme", key, color)} title={"Capturar " + label.toLowerCase()} />
                         </div>
                       </div>
                     ))}
                   </div>

                   <div className="mt-5 pt-4 border-t border-border-subtle dark:border-border-dark space-y-3">
                     <label className="text-xs font-semibold uppercase tracking-wide text-text-muted dark:text-text-muted-dark block">Textos do Compartilhamento</label>
                     {[
                       { key: 'shareHeading', label: 'Título', fallback: 'Compartilhe meu contato' },
                       { key: 'shareSimpleLabel', label: 'Opção QR simples', fallback: 'Somente link' },
                       { key: 'shareRichLabel', label: 'Opção QR completa', fallback: 'Detalhes completos' },
                       { key: 'shareCloseLabel', label: 'Botão de fechar', fallback: 'Fechar' },
                     ].map(item => (
                       <div key={item.key}>
                         <label className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1 block">{item.label}</label>
                         <input
                           type="text"
                           value={data.theme?.[item.key] ?? item.fallback}
                           onChange={e => handleInputChange('theme', item.key, e.target.value.substring(0, 60))}
                           maxLength={60}
                           className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                         />
                       </div>
                     ))}
                   </div>
                 </div>
               </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                {settings?.allow_privacy_customisation === false ? (
                  <LockedOption message="Sua organização desativou a personalização das configurações de privacidade. As configurações de privacidade são controladas pela sua organização.">
                    <div className="space-y-6">
                      <Toggle
                        label="Exigir Interação"
                        description="Exige que os usuários cliquem em um botão para revelar e-mail e telefone. Impede que bots básicos vejam informações de contato no carregamento inicial da página."
                        checked={data.privacy?.requireInteraction ?? true}
                        onChange={() => {}}
                      />
                      <div className="h-px bg-border-subtle" />
                      <Toggle
                        label="Ofuscação no Lado do Cliente"
                        description="Codifica e-mail e telefone no HTML para dificultar raspagem. Nota: Raspadores determinados ainda podem decodificar isso."
                        checked={data.privacy?.clientSideObfuscation ?? false}
                        onChange={() => {}}
                      />
                      <div className="h-px bg-border-subtle" />
                      <Toggle
                        label="Bloquear Mecanismos de Busca"
                        description="Adiciona meta tag robots para impedir que mecanismos de busca indexem este cartão."
                        checked={data.privacy?.blockRobots ?? false}
                        onChange={() => {}}
                      />
                    </div>
                  </LockedOption>
                ) : (
                  <div className="space-y-6">
                    <Toggle
                      label="Exigir Interação"
                      description="Exige que os usuários cliquem em um botão para revelar e-mail e telefone. Impede que bots básicos vejam informações de contato no carregamento inicial da página."
                      checked={data.privacy?.requireInteraction ?? true}
                      onChange={(checked) => handleInputChange('privacy', 'requireInteraction', checked)}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label="Ofuscação no Lado do Cliente"
                      description="Codifica e-mail e telefone no HTML para dificultar raspagem. Nota: Raspadores determinados ainda podem decodificar isso."
                      checked={data.privacy?.clientSideObfuscation ?? false}
                      onChange={(checked) => handleInputChange('privacy', 'clientSideObfuscation', checked)}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label="Bloquear Mecanismos de Busca"
                      description="Adiciona meta tag robots para impedir que mecanismos de busca indexem este cartão."
                      checked={data.privacy?.blockRobots ?? false}
                      onChange={(checked) => handleInputChange('privacy', 'blockRobots', checked)}
                    />
                  </div>
                )}

                {/* ── Analytics & Relatórios ─────────────────────────── */}
                <div className="h-px bg-border-subtle dark:bg-border-dark" />
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark flex items-center gap-2">
                    <BarChart2 className="w-4 h-4 text-action dark:text-action-dark" />
                    Analytics &amp; Relatórios
                  </h3>
                  <Toggle
                    label="Ativar Analytics"
                    description="Coleta visitas, cliques em links, WhatsApp e Mapa. Os dados são armazenados de forma privada no servidor."
                    checked={data.analytics?.enabled ?? false}
                    onChange={(checked) => handleInputChange('analytics', 'enabled', checked)}
                  />
                  {(data.analytics?.enabled) && (
                    <div className="space-y-1.5 pt-1">
                      <label className="block text-xs font-medium text-text-primary dark:text-text-primary-dark">
                        E-mail para Relatório Semanal
                      </label>
                      <input
                        type="email"
                        value={data.analytics?.reportEmail ?? ''}
                        onChange={(e) => handleInputChange('analytics', 'reportEmail', e.target.value)}
                        placeholder="exemplo@gmail.com"
                        className="w-full px-3 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                      />
                      <p className="text-[11px] text-text-muted dark:text-text-muted-dark leading-relaxed">
                        Resumo enviado semanalmente via e-mail com total de visitas, top links clicados e taxa de cliques (CTR).
                        Só é enviado se houver atividade no período.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
      
      {/* Prévia: mesmo frame da página pública, ocupando toda a coluna da
          direita (sem moldura nem margem), com rolagem própria. O que o editor
          mostra é o que o visitante vê: layout, fundo, efeito e rodapé. */}
      <div className="hidden lg:block w-1/2 h-screen sticky top-0 relative overflow-hidden bg-border-subtle dark:bg-card-dark">
        <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
          <PublicCardFrame
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            contained
          />
        </div>
      </div>
    </div>
  );
}

function AnrvaFooter({ dark = false, className = '', footerColor }) {
  return (
    <footer className={`w-full text-center py-4 px-4 ${className}`}>
      <p
        className={footerColor ? 'text-[11px] leading-relaxed' : `text-[11px] leading-relaxed ${dark ? 'text-slate-400' : 'text-text-muted-subtle dark:text-slate-400'}`}
        style={footerColor ? { color: footerColor } : undefined}
      >
        © {new Date().getFullYear()} &bull; Criado com <span style={{ color: '#e05252' }}>♥</span> por{' '}
        <a href="https://www.anrvainfo.com.br" target="_blank" rel="noopener noreferrer"
          className={`font-semibold underline underline-offset-2 transition-opacity hover:opacity-70 ${dark ? 'text-cyan-400' : footerColor ? '' : 'text-cyan-600 dark:text-cyan-400'}`}
          style={footerColor ? { color: footerColor } : undefined}
        >
          ANRVAinfo
        </a>
        . Todos os direitos reservados.
      </p>
    </footer>
  );
}

function Input({ label, icon: Icon, value, onChange, type = "text", placeholder }) {
  return (
    <div className="space-y-1">
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>}
      <div className="relative">
        {Icon && <Icon className="absolute left-4 top-3 w-5 h-5 text-text-muted-subtle dark:text-text-muted-dark" />}
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`w-full ${Icon ? 'pl-11' : 'px-4'} py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark placeholder:text-text-muted-subtle dark:placeholder:text-text-muted-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark`} />
      </div>
    </div>
  );
}

function LockedOption({ message, children }) {
  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-surface/80 dark:bg-card-dark/80 rounded-container border-thick border-dashed border-border dark:border-border-dark">
        <div className="bg-input-bg dark:bg-input-bg-dark rounded-container p-4 border border-border dark:border-border-dark shadow-lg max-w-sm mx-4">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-text-muted dark:text-text-muted-dark" />
            <span className="text-sm font-semibold text-text-primary dark:text-text-secondary-dark">Bloqueado pela Organização</span>
          </div>
          <p className="text-xs text-text-secondary dark:text-text-muted-dark">{message}</p>
        </div>
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark resize-none" />
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
          {description && <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{description}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:ring-offset-2 ${
            checked ? 'bg-action dark:bg-action-dark' : 'bg-border dark:bg-surface-dark'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function ImageUpload({ label, image, onUpload, onRemove, isBanner, disabled = false }) {
  return (
    <section>
      <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-3">{label}</h3>
      <div className={`relative ${isBanner ? 'w-full h-32' : 'w-24 h-24'} rounded-input bg-surface dark:bg-surface-dark border-thick border-dashed border-border dark:border-border-dark flex items-center justify-center overflow-hidden group ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-dark dark:hover:border-border-dark'} transition-colors`}>
        {image ? <img src={image} className="w-full h-full object-cover" alt="upload" /> : <div className="text-center text-text-muted-subtle dark:text-text-muted-dark pointer-events-none"><Upload className="w-6 h-6 mx-auto mb-1" /><span className="text-xs">Upload</span></div>}
        <input type="file" accept="image/*" onChange={onUpload} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed" />
      </div>
      {image && !disabled && <button onClick={onRemove} className="mt-2 text-sm text-red-500 dark:text-red-400 font-medium hover:text-red-600 dark:hover:text-red-300">Remover</button>}
    </section>
  );
}

function SocialIcon({ url, icon: Icon, label, themeColor }) {
  if (!url) return null;
  // Use theme color if available, otherwise default to indigo
  const hoverColor = themeColor?.buttonStyle || themeColor?.textStyle || '#4f46e5';
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group">
      <div className="w-14 h-14 rounded-card bg-surface dark:bg-surface-dark border border-border-subtle dark:border-border-dark flex items-center justify-center text-text-secondary dark:text-text-secondary-dark group-hover:scale-105 transition-transform group-hover:shadow-md group-hover:text-white flex-shrink-0" style={{ '--hover-bg': hoverColor }}>
        <style>{`.group:hover div { background-color: ${hoverColor}; border-color: ${hoverColor}; }`}</style>
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider text-text-muted-subtle dark:text-text-muted-dark">{label}</span>
    </a>
  );
}

// ============================================================
// ANRVA BLOCKS SYSTEM — isolated, optional, data-driven blocks
// ============================================================

const BLOCK_TYPES = [
  { type: 'whatsapp',     label: 'WhatsApp',           icon: MessageCircle },
  { type: 'map',          label: 'Google Maps',         icon: MapPin },
  { type: 'video',        label: 'Vídeo (YouTube/Vimeo)', icon: Video },
  { type: 'media_list',   label: 'Lista de Mídia',       icon: GalleryHorizontal },
  { type: 'gallery',      label: 'Galeria',             icon: GalleryHorizontal },
  { type: 'services',     label: 'Serviços',            icon: DollarSign },
  { type: 'social_icons', label: 'Ícones Sociais',      icon: Hash },
  { type: 'link_preview', label: 'Link com Preview',    icon: ExternalLink },
  { type: 'simple_link',  label: 'Link Simples',        icon: LinkIcon },
  { type: 'text_block',   label: 'Texto / Separador',    icon: AlignLeft },
  { type: 'contact_info', label: 'E-mail / Telefone',    icon: Phone },
  { type: 'save_contact', label: 'Salvar Contato (VCF)', icon: UserPlus },
  { type: 'share_card',   label: 'Enviar Meus Detalhes', icon: Share2 },
];

function BlockRenderer({ block, themeColor, settings, onTrack, cardData, btnStyle }) {
  switch (block.type) {
    case 'whatsapp':     return <BlockWhatsApp config={block} onTrack={onTrack} btnStyle={btnStyle} />;
    case 'map':          return <BlockMap config={block} onTrack={onTrack} btnStyle={btnStyle} />;
    case 'video':        return <BlockVideo config={block} onTrack={onTrack} btnStyle={btnStyle} />;
    case 'media_list':   return <BlockMediaList config={block} onTrack={onTrack} iconMap={ICON_MAP} />;
    case 'gallery':      return <BlockGallery config={block} btnStyle={btnStyle} />;
    case 'services':     return <BlockServices config={block} />;
    case 'social_icons': return <BlockSocialIcons config={block} themeColor={themeColor} />;
    case 'link_preview': return <BlockLinkPreview config={block} themeColor={themeColor} settings={settings} />;
    case 'simple_link':  return <BlockSimpleLink config={block} btnStyle={btnStyle} />;
    case 'text_block':   return <BlockTextBlock config={block} />;
    case 'contact_info': return <BlockContactInfo config={block} cardData={cardData} btnStyle={btnStyle} />;
    case 'save_contact': return <BlockSaveContact config={block} cardData={cardData} btnStyle={btnStyle} />;
    case 'share_card':   return <BlockShareCard config={block} cardData={cardData} btnStyle={btnStyle} />;
    default:             return null;
  }
}

function BlockWhatsApp({ config, onTrack, btnStyle }) {
  const { phone, message, buttonText } = config;
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  const url = `https://wa.me/${digits}${message ? '?text=' + encodeURIComponent(message) : ''}`;
  const bs = btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={() => onTrack && onTrack('whatsapp_click', { phone: digits })}
      className={`flex items-center justify-center gap-3 w-full py-4 rounded-input border transition-all active:scale-[0.99]${btnStyle ? '' : ' bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark border-border dark:border-border-dark'}`}
      style={bs}
      onMouseEnter={btnStyle ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
      onMouseLeave={btnStyle ? e => { e.currentTarget.style.filter = ''; } : undefined}
    >
      <MessageCircle className="w-5 h-5" style={btnStyle ? { color: 'inherit' } : { color: '#22c55e' }} />
      {sanitizeText(buttonText || 'Falar no WhatsApp')}
    </a>
  );
}

function BlockMap({ config, onTrack, btnStyle }) {
  const { address, lat, lng } = config;
  if (!address && !lat) return null;
  const query = (lat && lng) ? `${lat},${lng}` : encodeURIComponent(address);
  const embedUrl = `https://maps.google.com/maps?q=${query}&output=embed`;
  const linkUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
  return (
    <div className="rounded-input overflow-hidden border border-border dark:border-border-dark">
      <iframe
        title="Mapa"
        src={embedUrl}
        width="100%"
        height="200"
        style={{ border: 0, display: 'block' }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      <a
        href={linkUrl}
        target="_blank"
        rel="noreferrer"
        onClick={() => onTrack && onTrack('map_click', { address: address || `${lat},${lng}` })}
        className={`flex items-center gap-2 px-4 py-3 text-xs transition-colors${btnStyle ? '' : ' bg-card dark:bg-card-dark text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'}`}
        style={btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff' } : {}}
        onMouseEnter={btnStyle ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
        onMouseLeave={btnStyle ? e => { e.currentTarget.style.filter = ''; } : undefined}
      >
        <MapPin className="w-3.5 h-3.5 shrink-0" style={btnStyle ? { color: 'inherit' } : undefined} />
        <span className="truncate">{sanitizeText(address || '')}</span>
        <ExternalLink className="w-3 h-3 ml-auto shrink-0 opacity-60" />
      </a>
    </div>
  );
}

function BlockGallery({ config, btnStyle }) {
  const { images = [], buttonText } = config;
  const validImages = images.filter(img => img && img.url);
  const [lightbox, setLightbox] = useState(null);
  const touchStartRef = useRef(null);
  const total = validImages.length;
  const multiple = total > 1;
  const isOpen = lightbox !== null;

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const go = useCallback((dir) => {
    setLightbox(l => (l === null ? l : (l + dir + total) % total));
  }, [total]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeLightbox, go]);

  if (!total) return null;
  const bs = btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  const current = validImages[lightbox] || {};
  const navBtn = "absolute top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 active:bg-black/70 text-white rounded-full p-3 backdrop-blur-sm transition-colors";

  return (
    <>
      <button
        onClick={() => setLightbox(0)}
        className={`flex items-center justify-center gap-2 w-full py-3 rounded-input border transition-all active:scale-[0.99]${btnStyle ? '' : ' bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark border-border dark:border-border-dark'}`}
        style={bs}
        onMouseEnter={btnStyle ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
        onMouseLeave={btnStyle ? e => { e.currentTarget.style.filter = ''; } : undefined}
      >
        <GalleryHorizontal className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : undefined} />
        {sanitizeText(buttonText || 'Ver Galeria')}
        <span className="ml-1 text-xs font-normal opacity-60">({total})</span>
      </button>
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 select-none overscroll-contain"
          style={{ height: '100dvh', width: '100dvw' }}
          onClick={closeLightbox}
          onTouchStart={e => { touchStartRef.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (start === null || !multiple) return;
            const dx = e.changedTouches[0].clientX - start;
            if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center p-0 sm:p-8">
            <img
              src={current.url}
              alt={sanitizeText(current.caption || '')}
              className="max-w-full max-h-full w-auto h-auto object-contain sm:rounded-input"
              onClick={e => e.stopPropagation()}
              draggable={false}
            />
          </div>

          {multiple && (
            <>
              <button
                onClick={e => { e.stopPropagation(); go(-1); }}
                aria-label="Imagem anterior"
                className={`${navBtn} left-2 sm:left-4`}
              >
                <ChevronUp className="w-6 h-6 -rotate-90" />
              </button>
              <button
                onClick={e => { e.stopPropagation(); go(1); }}
                aria-label="Proxima imagem"
                className={`${navBtn} right-2 sm:right-4`}
              >
                <ChevronDown className="w-6 h-6 -rotate-90" />
              </button>
              <div
                className="absolute left-1/2 -translate-x-1/2 z-10 text-white/80 text-xs font-medium bg-black/40 backdrop-blur-sm rounded-full px-3 py-1"
                style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
              >
                {lightbox + 1} / {total}
              </div>
            </>
          )}

          <button
            onClick={e => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Fechar galeria"
            className="absolute z-20 bg-black/40 hover:bg-black/60 active:bg-black/70 text-white rounded-full p-3 backdrop-blur-sm transition-colors"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', right: 'calc(env(safe-area-inset-right, 0px) + 12px)' }}
          >
            <X className="w-6 h-6" />
          </button>

          {current.caption && (
            <div
              className="absolute left-0 right-0 z-10 text-center text-white text-sm px-6 pt-10 pb-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"
              style={{ bottom: 0, paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
            >
              {sanitizeText(current.caption)}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function BlockServices({ config }) {
  const { items = [] } = config;
  const validItems = items.filter(it => it && it.name);
  if (!validItems.length) return null;
  return (
    <div className="space-y-2">
      {validItems.map((item, idx) => (
        <div key={idx} className="flex items-start justify-between gap-3 p-4 rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-text-primary dark:text-text-primary-dark">{sanitizeText(item.name)}</div>
            {item.description && (
              <div className="text-xs text-text-muted dark:text-text-muted-dark mt-0.5 leading-relaxed">
                {sanitizeText(item.description)}
              </div>
            )}
          </div>
          {item.price && (
            <div className="shrink-0 font-bold text-sm text-text-primary dark:text-text-primary-dark whitespace-nowrap">
              {sanitizeText(item.price)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BlockSocialIcons({ config, themeColor }) {
  const links = [
    { key: 'instagram', Icon: Instagram,  label: 'Insta' },
    { key: 'facebook',  Icon: Facebook,   label: 'Facebook' },
    { key: 'youtube',   Icon: Youtube,    label: 'YouTube' },
    { key: 'linkedin',  Icon: Linkedin,   label: 'LinkedIn' },
    { key: 'tiktok',    Icon: TikTokIcon, label: 'TikTok' },
    { key: 'rss',       Icon: Rss,        label: 'RSS' },
  ].filter(l => config[l.key]);
  if (!links.length) return null;
  const hoverColor = themeColor?.buttonStyle || themeColor?.textStyle || '#06b6d4';
  return (
    <div className="grid grid-cols-4 gap-3">
      {links.map(({ key, Icon, label }) => (
        <a
          key={key}
          href={sanitizeText(config[key])}
          target="_blank"
          rel="noreferrer"
          className="flex flex-col items-center gap-2 group"
        >
          <div
            className="w-full aspect-square rounded-card bg-surface dark:bg-surface-dark border border-border-subtle dark:border-border-dark flex items-center justify-center text-text-secondary dark:text-text-secondary-dark group-hover:scale-105 transition-all group-hover:shadow-md"
            style={{ '--hover-color': hoverColor }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = hoverColor; e.currentTarget.style.borderColor = hoverColor; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }}
          >
            <Icon className="w-6 h-6" />
          </div>
          <span className="text-[10px] uppercase font-bold tracking-wider text-text-muted-subtle dark:text-text-muted-dark">{label}</span>
        </a>
      ))}
    </div>
  );
}

function BlockLinkPreview({ config }) {
  const { title, description, image, url } = config;
  if (!url) return null;
  return (
    <a
      href={sanitizeText(url)}
      target="_blank"
      rel="noreferrer"
      className="flex gap-3 p-3 rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark hover:bg-surface dark:hover:bg-surface-dark transition-all active:scale-[0.99] group"
    >
      {image && (
        <div className="w-20 h-16 shrink-0 rounded-container overflow-hidden bg-surface dark:bg-surface-dark">
          <img src={sanitizeText(image)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        {title && <div className="font-semibold text-sm text-text-primary dark:text-text-primary-dark leading-tight line-clamp-2">{sanitizeText(title)}</div>}
        {description && <div className="text-xs text-text-muted dark:text-text-muted-dark mt-1 line-clamp-2">{sanitizeText(description)}</div>}
        <div className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark mt-1 truncate">{sanitizeText(url)}</div>
      </div>
      <ExternalLink className="w-4 h-4 shrink-0 text-text-muted-subtle dark:text-text-muted-dark self-center opacity-0 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

function BlockSimpleLink({ config, btnStyle }) {
  const { title, url, icon, variant = 'solid' } = config;
  if (!url) return null;
  // variant: 'solid' (padrão, usa o botão do cartão), 'outline' (contorno),
  //          'dashed' (contorno tracejado, discreto). outline/dashed ignoram btnStyle.
  const isPlain = variant === 'outline' || variant === 'dashed';
  const useBtn = btnStyle && !isPlain;
  const bs = useBtn ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  const plainCls = variant === 'dashed'
    ? ' border-dashed border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark'
    : ' bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark border-border dark:border-border-dark';
  return (
    <a
      href={sanitizeText(url)}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center justify-center gap-2 w-full py-3 rounded-input border font-semibold text-sm transition-all active:scale-[0.99]${useBtn ? '' : plainCls}`}
      style={bs}
      onMouseEnter={useBtn ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
      onMouseLeave={useBtn ? e => { e.currentTarget.style.filter = ''; } : undefined}
    >
      {React.createElement(ICON_MAP[icon] || LinkIcon, { className: 'w-4 h-4', style: useBtn ? { color: 'inherit' } : undefined })}
      {sanitizeText(title || url)}
    </a>
  );
}

function BlockTextBlock({ config }) {
  const { text, style, color } = config;
  if (!text && style !== 'divider') return null;

  if (style === 'divider') {
    return (
      <div className="flex items-center gap-3 w-full py-1">
        <div className={`flex-1 h-px${color ? '' : ' bg-border dark:bg-border-dark'}`} style={color ? { backgroundColor: color } : undefined} />
        {text && <span className={`text-xs whitespace-nowrap shrink-0${color ? '' : ' text-text-muted dark:text-text-muted-dark'}`} style={color ? { color } : undefined}>{sanitizeText(text)}</span>}
        <div className={`flex-1 h-px${color ? '' : ' bg-border dark:bg-border-dark'}`} style={color ? { backgroundColor: color } : undefined} />
      </div>
    );
  }

  if (style === 'eyebrow') {
    return (
      <p
        className={`text-xs font-semibold uppercase tracking-[0.25em]${color ? '' : ' text-action dark:text-action-dark'}`}
        style={color ? { color } : undefined}
      >
        {sanitizeText(text)}
      </p>
    );
  }

  const cls =
    style === 'heading'    ? `text-base font-bold${color ? '' : ' text-text-primary dark:text-text-primary-dark'}` :
    style === 'subheading' ? `text-sm font-semibold${color ? '' : ' text-text-secondary dark:text-text-secondary-dark'}` :
                             `text-sm leading-relaxed${color ? '' : ' text-text-muted dark:text-text-muted-dark'}`;

  return <p className={cls} style={color ? { color } : undefined}>{sanitizeText(text)}</p>;
}

function BlockContactInfo({ config, cardData, btnStyle }) {
  const { emailLabel, phoneLabel } = config;
  const contact  = cardData?.contact  || {};
  const privacy  = cardData?.privacy  || {};
  const [revealed, setRevealed] = useState(false);

  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  if (!hasEmail && !hasPhone) return null;

  const requireInteraction = privacy.requireInteraction ?? true;
  const useObfuscation     = privacy.clientSideObfuscation ?? false;

  const obf = (v) => btoa(v || '');
  const deobf = (v) => { try { return atob(v); } catch { return ''; } };

  const bs = btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  const bsCls = btnStyle ? '' : ' border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark';
  const bsHover = btnStyle ? { onMouseEnter: e => { e.currentTarget.style.filter = 'brightness(1.1)'; }, onMouseLeave: e => { e.currentTarget.style.filter = ''; } } : {};

  if (requireInteraction && !revealed) {
    return (
      <button
        onClick={() => setRevealed(true)}
        className={`flex items-center justify-center gap-2 w-full py-3 rounded-input border font-semibold text-sm transition-all active:scale-[0.99]${bsCls}`}
        style={bs} {...bsHover}
      >
        <Eye className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : undefined} />
        Ver dados de contato
      </button>
    );
  }

  const emailVal = contact.email || '';
  const phoneVal = contact.phone || '';

  return (
    <div className="flex gap-2 w-full">
      {hasEmail && (
        <a
          href={useObfuscation ? '#' : `mailto:${emailVal}`}
          data-email={useObfuscation ? obf(emailVal) : undefined}
          onClick={(e) => {
            if (useObfuscation) {
              e.preventDefault();
              window.location.href = `mailto:${deobf(e.currentTarget.dataset.email)}`;
            }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-input border font-semibold text-sm transition-all active:scale-[0.99]${bsCls}`}
          style={bs} {...bsHover}
        >
          <Mail className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : undefined} />
          {sanitizeText(emailLabel || 'E-mail')}
        </a>
      )}
      {hasPhone && (
        <a
          href={useObfuscation ? '#' : `tel:${phoneVal}`}
          data-phone={useObfuscation ? obf(phoneVal) : undefined}
          onClick={(e) => {
            if (useObfuscation) {
              e.preventDefault();
              window.location.href = `tel:${deobf(e.currentTarget.dataset.phone)}`;
            }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-input border font-semibold text-sm transition-all active:scale-[0.99]${bsCls}`}
          style={bs} {...bsHover}
        >
          <Phone className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : undefined} />
          {sanitizeText(phoneLabel || 'Ligar')}
        </a>
      )}
    </div>
  );
}

function BlockSaveContact({ config, cardData, btnStyle }) {
  const { buttonText } = config;
  const personal = cardData?.personal || {};
  const contact  = cardData?.contact  || {};

  const handleSave = () => {
    const firstName = personal.firstName || '';
    const lastName  = personal.lastName  || '';
    const fullName  = `${firstName} ${lastName}`.trim() || 'Contato';
    const phone     = (contact.phone  || '').replace(/\s/g, '');
    const email     = contact.email   || '';
    const website   = contact.website || '';
    const company   = personal.company || personal.organisation || '';
    const title     = personal.title  || personal.headline || '';
    const cardUrl   = window.location.href;

    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${fullName}`,
      `N:${lastName};${firstName};;;`,
      company   ? `ORG:${company}`           : null,
      title     ? `TITLE:${title}`           : null,
      phone     ? `TEL;TYPE=CELL:${phone}`   : null,
      email     ? `EMAIL:${email}`           : null,
      website   ? `URL:${website}`           : null,
      `URL;TYPE=ANRVA:${cardUrl}`,
      'END:VCARD',
    ].filter(Boolean).join('\r\n');

    const blob = new Blob([lines], { type: 'text/vcard;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${fullName.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bs = btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  return (
    <button
      onClick={handleSave}
      className={`flex items-center justify-center gap-2 w-full py-3 rounded-input border transition-all active:scale-[0.99]${btnStyle ? '' : ' bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark border-border dark:border-border-dark'}`}
      style={bs}
      onMouseEnter={btnStyle ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
      onMouseLeave={btnStyle ? e => { e.currentTarget.style.filter = ''; } : undefined}
    >
      <UserPlus className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : { opacity: 0.6 }} />
      {sanitizeText(buttonText || 'Salvar Contato')}
    </button>
  );
}

function BlockShareCard({ config, cardData, btnStyle }) {
  const { buttonText } = config;
  const [open, setOpen] = useState(false);

  const contact = cardData?.contact || {};
  const ownerPhone = contact.phone || '';
  const ownerEmail = contact.email || '';
  const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');

  const whatsappLink = ownerPhoneDigits && ownerPhoneDigits.length >= 8
    ? `https://wa.me/${ownerPhoneDigits}?text=${encodeURIComponent('Olá, nos conhecemos pelo seu cartão ANRVA. Meu nome é ... e meu número/e-mail é ...')}`
    : null;

  const emailLink = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent('Meus detalhes do ANRVA')}&body=${encodeURIComponent('Olá, nos conhecemos pelo seu cartão ANRVA.\nMeu nome é ...\nMeu telefone é ...\nMeu e-mail é ...')}`
    : null;

  const dropCallLink = ownerPhone ? `tel:${ownerPhone}` : null;

  const hasAny = whatsappLink || emailLink || dropCallLink;
  if (!hasAny) return null;

  const bs = btnStyle ? { background: btnStyle.bg, color: btnStyle.text || '#fff', borderColor: isHexColor(btnStyle.bg) ? btnStyle.bg + '40' : 'transparent' } : {};
  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-input border transition-all active:scale-[0.99]${btnStyle ? '' : ' bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark border-border dark:border-border-dark'}`}
        style={bs}
        onMouseEnter={btnStyle ? e => { e.currentTarget.style.filter = 'brightness(1.1)'; } : undefined}
        onMouseLeave={btnStyle ? e => { e.currentTarget.style.filter = ''; } : undefined}
      >
        <Share2 className="w-4 h-4" style={btnStyle ? { color: 'inherit' } : { opacity: 0.6 }} />
        {open ? 'Ocultar opções' : sanitizeText(buttonText || 'Enviar Meus Detalhes')}
      </button>

      {open && (
        <div className="mt-2 space-y-2 rounded-input border border-border dark:border-border-dark bg-surface/60 dark:bg-card-dark/60 p-3">
          {whatsappLink && (
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-input font-semibold text-sm bg-success dark:bg-success-dark text-white hover:opacity-90 transition-opacity"
            >
              <MessageCircle className="w-4 h-4" />
              Envie seu número via WhatsApp
            </a>
          )}
          {emailLink && (
            <a
              href={emailLink}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-input font-semibold text-sm border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
            >
              <Mail className="w-4 h-4" />
              Envie seus detalhes por e-mail
            </a>
          )}
          {dropCallLink && (
            <a
              href={dropCallLink}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-input font-semibold text-sm border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
            >
              <Phone className="w-4 h-4" />
              Ligue para mim com seu número
            </a>
          )}
          <p className="text-[11px] text-text-muted dark:text-text-muted-dark text-center pt-1">
            Compartilhado apenas comigo, nunca vendido.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// BLOCKS EDITOR — tab panel inside EditorView
// ============================================================

function BlocksEditor({ blocks, onChange, csrfToken, showAlert }) {
  const [adding, setAdding] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const toggleCollapse = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const setDone = (id) => setCollapsed(prev => ({ ...prev, [id]: true }));

  const inputCls = "w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark";
  const labelCls = "text-xs font-medium text-text-muted dark:text-text-muted-dark";

  const addBlock = (type) => {
    const defaults = {
      whatsapp:     { type: 'whatsapp',     width: 'full', phone: '', message: '', buttonText: 'Falar no WhatsApp' },
      map:          { type: 'map',          width: 'full', address: '', lat: '', lng: '' },
      video:        { type: 'video',        width: 'full', url: '', heading: { eyebrow: '', title: '' }, title: '', subtitle: '', showOpenLink: true },
      media_list:   { type: 'media_list',   width: 'full', layout: 'list', media: 'thumb', columns: 3, rowIcon: 'open', heading: { eyebrow: '', title: '' }, footer: { label: '', url: '' }, items: [] },
      gallery:      { type: 'gallery',      width: 'full', images: [], buttonText: 'Ver Galeria' },
      services:     { type: 'services',     width: 'full', items: [] },
      social_icons: { type: 'social_icons', width: 'full', rss: '', instagram: '', facebook: '', tiktok: '', youtube: '', linkedin: '' },
      link_preview: { type: 'link_preview', width: 'full', title: '', description: '', image: '', url: '' },
      simple_link:  { type: 'simple_link',  width: 'half', title: '', url: '', icon: 'link', variant: 'solid' },
      text_block:   { type: 'text_block',   width: 'full', text: '', style: 'body' },
      contact_info: { type: 'contact_info', width: 'full', emailLabel: 'E-mail', phoneLabel: 'Ligar' },
      save_contact: { type: 'save_contact', width: 'half', buttonText: 'Salvar Contato' },
      share_card:   { type: 'share_card',   width: 'full', buttonText: 'Enviar Meus Detalhes' },
    };
    onChange([...blocks, { id: Date.now(), ...(defaults[type] || { type, width: 'full' }) }]);
    setAdding(false);
  };

  const removeBlock = (id) => onChange(blocks.filter(b => b.id !== id));

  const updateBlock = (id, patch) => onChange(blocks.map(b => b.id === id ? { ...b, ...patch } : b));

  const moveUp = (idx) => {
    if (idx <= 0) return;
    const next = [...blocks];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const moveDown = (idx) => {
    if (idx >= blocks.length - 1) return;
    const next = [...blocks];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  const uploadGalleryImage = async (blockId, file) => {
    if (!file) return;
    setUploadingId(blockId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData
      });
      let body;
      try { body = await res.json(); } catch (e) { body = {}; }
      if (res.ok && body.url) {
        // Use onChange directly to avoid any stale-closure issue with updateBlock
        onChange(blocks.map(b =>
          b.id === blockId
            ? { ...b, images: [...(b.images || []), { url: body.url, caption: '' }] }
            : b
        ));
      } else {
        const msg = body?.error || `Erro ${res.status} ao enviar imagem`;
        console.error('[gallery upload] servidor:', res.status, body);
        if (showAlert) showAlert(msg, 'error');
      }
    } catch (err) {
      console.error('[gallery upload] rede:', err);
      if (showAlert) showAlert('Erro de rede ao enviar imagem', 'error');
    } finally {
      setUploadingId(null);
    }
  };

  const updateGalleryItem = (blockId, imgIdx, patch) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { images: block.images.map((img, i) => i === imgIdx ? { ...img, ...patch } : img) });
  };

  const removeGalleryItem = (blockId, imgIdx) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { images: block.images.filter((_, i) => i !== imgIdx) });
  };

  const updateServiceItem = (blockId, itemIdx, patch) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { items: block.items.map((it, i) => i === itemIdx ? { ...it, ...patch } : it) });
  };

  const removeServiceItem = (blockId, itemIdx) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { items: block.items.filter((_, i) => i !== itemIdx) });
  };

  const updateMediaItem = (blockId, itemIdx, patch) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { items: (block.items || []).map((it, i) => i === itemIdx ? { ...it, ...patch } : it) });
  };
  const removeMediaItem = (blockId, itemIdx) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    updateBlock(blockId, { items: (block.items || []).filter((_, i) => i !== itemIdx) });
  };
  const uploadMediaItemImage = async (blockId, itemIdx, file) => {
    if (!file) return;
    setUploadingId(blockId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/upload', { method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrfToken }, body: fd });
      let body; try { body = await res.json(); } catch (e) { body = {}; }
      if (res.ok && body.url) {
        onChange(blocks.map(b => b.id === blockId
          ? { ...b, items: (b.items || []).map((it, i) => i === itemIdx ? { ...it, image: body.url } : it) }
          : b));
      } else if (showAlert) {
        showAlert(body?.error || `Erro ${res.status} ao enviar imagem`, 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro de rede ao enviar imagem', 'error');
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header — title only */}
      <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Blocos do Cartão</h3>

      {/* Block list */}
      {blocks.map((block, idx) => {
        const meta = BLOCK_TYPES.find(b => b.type === block.type);
        const BlockIcon = meta?.icon || Hash;
        const isCollapsed = !!collapsed[block.id];

        /* ── COLLAPSED: compact row ── */
        if (isCollapsed) {
          return (
            <div key={block.id} className="flex items-center gap-2 px-3 py-2 bg-surface dark:bg-surface-dark rounded-input border border-border dark:border-border-dark">
              <BlockIcon className="w-3.5 h-3.5 text-action dark:text-action-dark shrink-0" />
              <span className="text-sm font-medium text-text-primary dark:text-text-primary-dark flex-1 truncate">{meta?.label || block.type}</span>
              <span className="text-[10px] text-text-muted dark:text-text-muted-dark border border-border dark:border-border-dark rounded px-1.5 py-0.5 mr-1">{block.width === 'half' ? '½' : 'full'}</span>
              <button onClick={() => toggleCollapse(block.id)} className="text-xs font-medium text-action dark:text-action-dark hover:underline px-2 py-1 rounded-button hover:bg-card dark:hover:bg-card-dark transition-colors">Editar</button>
              <button onClick={() => moveUp(idx)} disabled={idx === 0} className="p-1 text-text-muted dark:text-text-muted-dark hover:text-action-dark disabled:opacity-30 transition-colors" aria-label="Mover para cima"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => moveDown(idx)} disabled={idx === blocks.length - 1} className="p-1 text-text-muted dark:text-text-muted-dark hover:text-action-dark disabled:opacity-30 transition-colors" aria-label="Mover para baixo"><ChevronDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => removeBlock(block.id)} className="p-1 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text transition-colors" aria-label="Remover"><X className="w-3.5 h-3.5" /></button>
            </div>
          );
        }

        /* ── EXPANDED: full form ── */
        return (
          <div key={block.id} className="bg-surface dark:bg-surface-dark rounded-input border border-border dark:border-border-dark p-4 relative">
            {/* Block header */}
            <div className="flex items-center gap-2 mb-3 pr-24">
              <BlockIcon className="w-4 h-4 text-action dark:text-action-dark shrink-0" />
              <span className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">{meta?.label || block.type}</span>
              <select
                value={block.width || 'full'}
                onChange={e => updateBlock(block.id, { width: e.target.value })}
                className="ml-auto text-xs bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-button px-2 py-1 text-text-muted dark:text-text-muted-dark focus:outline-none"
              >
                <option value="full">Largura inteira</option>
                <option value="half">Meia largura</option>
              </select>
            </div>
            {/* Block controls */}
            <div className="absolute top-2 right-2 flex items-center gap-1">
              <button onClick={() => moveUp(idx)} disabled={idx === 0} className="p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:text-action-dark disabled:opacity-30 transition-colors" aria-label="Mover para cima"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => moveDown(idx)} disabled={idx === blocks.length - 1} className="p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:text-action-dark disabled:opacity-30 transition-colors" aria-label="Mover para baixo"><ChevronDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => removeBlock(block.id)} className="p-1 rounded-full text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark transition-colors" aria-label="Remover bloco"><X className="w-3.5 h-3.5" /></button>
            </div>

            {/* Fields per block type */}
            <div className="space-y-2">
              {block.type === 'whatsapp' && (
                <>
                  <div><label className={labelCls}>Número WhatsApp (com DDI, ex: 5511999999999)</label><input className={inputCls} value={block.phone || ''} onChange={e => updateBlock(block.id, { phone: e.target.value })} placeholder="5511999999999" /></div>
                  <div><label className={labelCls}>Mensagem automática</label><input className={inputCls} value={block.message || ''} onChange={e => updateBlock(block.id, { message: e.target.value })} placeholder="Olá! Vi seu cartão e..." /></div>
                  <div><label className={labelCls}>Texto do botão</label><input className={inputCls} value={block.buttonText || ''} onChange={e => updateBlock(block.id, { buttonText: e.target.value })} placeholder="Falar no WhatsApp" /></div>
                </>
              )}

              {block.type === 'map' && (
                <>
                  <div><label className={labelCls}>Endereço</label><input className={inputCls} value={block.address || ''} onChange={e => updateBlock(block.id, { address: e.target.value })} placeholder="Rua Exemplo, 123, São Paulo, SP" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Latitude (opcional)</label><input className={inputCls} value={block.lat || ''} onChange={e => updateBlock(block.id, { lat: e.target.value })} placeholder="-23.5505" /></div>
                    <div><label className={labelCls}>Longitude (opcional)</label><input className={inputCls} value={block.lng || ''} onChange={e => updateBlock(block.id, { lng: e.target.value })} placeholder="-46.6333" /></div>
                  </div>
                </>
              )}

              {block.type === 'video' && (
                <>
                  <div><label className={labelCls}>URL do vídeo (YouTube ou Vimeo)</label><input className={inputCls} value={block.url || ''} onChange={e => updateBlock(block.id, { url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Olho-d'água — rótulo de seção acima (opcional)</label><input className={inputCls} value={block.heading?.eyebrow || ''} onChange={e => updateBlock(block.id, { heading: { ...(block.heading || {}), eyebrow: e.target.value } })} placeholder="Lançamento" /></div>
                    <div><label className={labelCls}>Título da seção — acima (opcional)</label><input className={inputCls} value={block.heading?.title || ''} onChange={e => updateBlock(block.id, { heading: { ...(block.heading || {}), title: e.target.value } })} placeholder="A canção mais nova" /></div>
                  </div>
                  <div><label className={labelCls}>Título do vídeo — abaixo (opcional)</label><input className={inputCls} value={block.title || ''} onChange={e => updateBlock(block.id, { title: e.target.value })} placeholder="Te Amo Mil Milhões" /></div>
                  <div><label className={labelCls}>Legenda / meta — abaixo</label><input className={inputCls} value={block.subtitle || ''} onChange={e => updateBlock(block.id, { subtitle: e.target.value })} placeholder="Romântica · 3:29 · Cezar A. Usanovich" /></div>
                  <label className="flex items-center gap-2 text-xs text-text-secondary dark:text-text-secondary-dark">
                    <input type="checkbox" checked={block.showOpenLink !== false} onChange={e => updateBlock(block.id, { showOpenLink: e.target.checked })} />
                    Mostrar botão "abrir no site do vídeo"
                  </label>
                </>
              )}

              {block.type === 'media_list' && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={labelCls}>Layout</label>
                      <select className={inputCls} value={block.layout || 'list'} onChange={e => updateBlock(block.id, { layout: e.target.value })}>
                        <option value="list">Lista</option>
                        <option value="grid">Grade</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Mídia</label>
                      <select className={inputCls} value={block.media || 'thumb'} onChange={e => updateBlock(block.id, { media: e.target.value })}>
                        <option value="thumb">Imagem</option>
                        <option value="icon">Ícone</option>
                        <option value="none">Nenhuma</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Colunas (grade)</label>
                      <select className={inputCls} value={block.columns || 3} onChange={e => updateBlock(block.id, { columns: Number(e.target.value) })}>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Ícone no fim da linha (layout Lista)</label>
                    <select className={inputCls} value={block.rowIcon || 'open'} onChange={e => updateBlock(block.id, { rowIcon: e.target.value })}>
                      <option value="open">Abrir (seta)</option>
                      <option value="play">Play (triângulo)</option>
                      <option value="none">Nenhum</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Olho-d'água (opcional)</label><input className={inputCls} value={block.heading?.eyebrow || ''} onChange={e => updateBlock(block.id, { heading: { ...(block.heading || {}), eyebrow: e.target.value } })} placeholder="Repertório" /></div>
                    <div><label className={labelCls}>Título da seção (opcional)</label><input className={inputCls} value={block.heading?.title || ''} onChange={e => updateBlock(block.id, { heading: { ...(block.heading || {}), title: e.target.value } })} placeholder="Ouça as canções" /></div>
                  </div>
                  <div className="space-y-2">
                    <label className={labelCls}>Itens</label>
                    {(block.items || []).map((item, iIdx) => (
                      <div key={iIdx} className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark p-3 space-y-2 relative">
                        <button onClick={() => removeMediaItem(block.id, iIdx)} className="absolute top-2 right-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text transition-colors"><X className="w-3.5 h-3.5" /></button>
                        <input className={inputCls} value={item.title || ''} onChange={e => updateMediaItem(block.id, iIdx, { title: e.target.value })} placeholder="Título" />
                        <input className={inputCls} value={item.subtitle || ''} onChange={e => updateMediaItem(block.id, iIdx, { subtitle: e.target.value })} placeholder="Subtítulo (ex: Nativa · 4:15)" />
                        <input className={inputCls} value={item.url || ''} onChange={e => updateMediaItem(block.id, iIdx, { url: e.target.value })} placeholder="URL (opcional)" />
                        {(block.media || 'thumb') === 'thumb' && (
                          <div className="flex items-center gap-2">
                            {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded object-cover border border-border dark:border-border-dark" />}
                            <label className="text-xs px-2 py-1.5 rounded-input border border-dashed border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark cursor-pointer hover:border-action-dark hover:text-action-dark transition-colors">
                              {uploadingId === block.id ? 'Enviando...' : (item.image ? 'Trocar imagem' : 'Enviar imagem')}
                              <input type="file" accept="image/*" className="hidden" disabled={uploadingId !== null} onChange={e => { if (e.target.files[0]) uploadMediaItemImage(block.id, iIdx, e.target.files[0]); e.target.value = ''; }} />
                            </label>
                            <input className={`${inputCls} flex-1`} value={item.image || ''} onChange={e => updateMediaItem(block.id, iIdx, { image: e.target.value })} placeholder="ou cole a URL da imagem" />
                          </div>
                        )}
                        {(block.media || 'thumb') === 'icon' && (
                          <div>
                            <label className={labelCls}>Ícone</label>
                            <IconPicker selected={item.icon} onChange={key => updateMediaItem(block.id, iIdx, { icon: key })} />
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => updateBlock(block.id, { items: [...(block.items || []), { image: '', icon: '', title: '', subtitle: '', url: '' }] })}
                      className="w-full py-2 rounded-input border border-dashed border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark text-xs font-medium hover:border-action-dark hover:text-action-dark transition-colors flex items-center justify-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Adicionar item
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={labelCls}>Botão de rodapé — texto</label><input className={inputCls} value={block.footer?.label || ''} onChange={e => updateBlock(block.id, { footer: { ...(block.footer || {}), label: e.target.value } })} placeholder="Ver todos os vídeos no YouTube" /></div>
                    <div><label className={labelCls}>Botão de rodapé — URL</label><input className={inputCls} value={block.footer?.url || ''} onChange={e => updateBlock(block.id, { footer: { ...(block.footer || {}), url: e.target.value } })} placeholder="https://..." /></div>
                  </div>
                </>
              )}

              {block.type === 'gallery' && (
                <>
                  <div><label className={labelCls}>Texto do botão</label><input className={inputCls} value={block.buttonText || ''} onChange={e => updateBlock(block.id, { buttonText: e.target.value })} placeholder="Ver Galeria" /></div>
                  <div className="grid grid-cols-3 gap-2">
                  {(block.images || []).map((img, iIdx) => (
                    <div key={iIdx} className="relative aspect-square rounded-input overflow-hidden bg-card dark:bg-card-dark border border-border dark:border-border-dark group">
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeGalleryItem(block.id, iIdx)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      <input
                        className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 focus:outline-none"
                        value={img.caption || ''}
                        onChange={e => updateGalleryItem(block.id, iIdx, { caption: e.target.value })}
                        placeholder="Legenda..."
                      />
                    </div>
                  ))}
                  <label className="aspect-square rounded-input bg-card dark:bg-card-dark border-2 border-dashed border-border dark:border-border-dark flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-muted-dark cursor-pointer hover:border-action-dark transition-colors">
                    {uploadingId === block.id
                      ? <RefreshCw className="w-5 h-5 animate-spin" />
                      : <><Upload className="w-5 h-5 mb-1" /><span className="text-[10px]">Adicionar</span></>}
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingId !== null} onChange={e => { if (e.target.files[0]) uploadGalleryImage(block.id, e.target.files[0]); e.target.value = ''; }} />
                  </label>
                </div>
                </>
              )}

              {block.type === 'services' && (
                <>
                  {(block.items || []).map((item, iIdx) => (
                    <div key={iIdx} className="bg-card dark:bg-card-dark rounded-input border border-border dark:border-border-dark p-3 space-y-2 relative">
                      <button onClick={() => removeServiceItem(block.id, iIdx)} className="absolute top-2 right-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text transition-colors"><X className="w-3.5 h-3.5" /></button>
                      <input className={inputCls} value={item.name || ''} onChange={e => updateServiceItem(block.id, iIdx, { name: e.target.value })} placeholder="Nome do serviço" />
                      <input className={inputCls} value={item.description || ''} onChange={e => updateServiceItem(block.id, iIdx, { description: e.target.value })} placeholder="Descrição (opcional)" />
                      <input className={inputCls} value={item.price || ''} onChange={e => updateServiceItem(block.id, iIdx, { price: e.target.value })} placeholder="Preço (ex: R$ 150,00)" />
                    </div>
                  ))}
                  <button
                    onClick={() => updateBlock(block.id, { items: [...(block.items || []), { name: '', description: '', price: '' }] })}
                    className="w-full py-2 rounded-input border border-dashed border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark text-xs font-medium hover:border-action-dark hover:text-action-dark transition-colors flex items-center justify-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> Adicionar Serviço
                  </button>
                </>
              )}

              {block.type === 'social_icons' && (
                <div className="space-y-2">
                  {[['instagram','Instagram'],['facebook','Facebook'],['youtube','YouTube'],['linkedin','LinkedIn'],['tiktok','TikTok'],['rss','RSS / Blog']].map(([key, lbl]) => (
                    <div key={key}><label className={labelCls}>{lbl}</label><input className={inputCls} value={block[key] || ''} onChange={e => updateBlock(block.id, { [key]: e.target.value })} placeholder="https://..." /></div>
                  ))}
                </div>
              )}

              {block.type === 'link_preview' && (
                <>
                  <div><label className={labelCls}>URL</label><input className={inputCls} value={block.url || ''} onChange={e => updateBlock(block.id, { url: e.target.value })} placeholder="https://..." /></div>
                  <div><label className={labelCls}>Título</label><input className={inputCls} value={block.title || ''} onChange={e => updateBlock(block.id, { title: e.target.value })} placeholder="Título do link" /></div>
                  <div><label className={labelCls}>Descrição</label><input className={inputCls} value={block.description || ''} onChange={e => updateBlock(block.id, { description: e.target.value })} placeholder="Breve descrição..." /></div>
                  <div><label className={labelCls}>URL da imagem (opcional)</label><input className={inputCls} value={block.image || ''} onChange={e => updateBlock(block.id, { image: e.target.value })} placeholder="https://imagem.jpg" /></div>
                </>
              )}

              {block.type === 'simple_link' && (
                <>
                  <div><label className={labelCls}>Título</label><input className={inputCls} value={block.title || ''} onChange={e => updateBlock(block.id, { title: e.target.value })} placeholder="Nome do link" /></div>
                  <div><label className={labelCls}>URL</label><input className={inputCls} value={block.url || ''} onChange={e => updateBlock(block.id, { url: e.target.value })} placeholder="https://..." /></div>
                  <div>
                    <label className={labelCls}>Estilo do botão</label>
                    <select className={inputCls} value={block.variant || 'solid'} onChange={e => updateBlock(block.id, { variant: e.target.value })}>
                      <option value="solid">Cheio (usa o botão do cartão)</option>
                      <option value="outline">Contorno</option>
                      <option value="dashed">Contorno tracejado (discreto)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Ícone</label>
                    <IconPicker selected={block.icon} onChange={key => updateBlock(block.id, { icon: key })} />
                  </div>
                </>
              )}

              {block.type === 'text_block' && (
                <>
                  <div>
                    <label className={labelCls}>Estilo</label>
                    <select className={inputCls} value={block.style || 'body'} onChange={e => updateBlock(block.id, { style: e.target.value })}>
                      <option value="eyebrow">Olho-d'água (rótulo de seção)</option>
                      <option value="heading">Título</option>
                      <option value="subheading">Subtítulo</option>
                      <option value="body">Texto normal</option>
                      <option value="divider">Separador (linha)</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Texto {block.style === 'divider' ? '(opcional — aparece no meio da linha)' : ''}</label>
                    <input className={inputCls} value={block.text || ''} onChange={e => updateBlock(block.id, { text: e.target.value })} placeholder={block.style === 'divider' ? 'ex: Contatos' : 'Digite o texto...'} />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-text-secondary dark:text-text-secondary-dark flex-1">Cor</span>
                    <div className="flex items-center gap-2">
                      {block.color && (
                        <button onClick={() => updateBlock(block.id, { color: '' })} className="text-[13px] leading-none text-text-muted dark:text-text-muted-dark hover:text-red-500 transition-colors" title="Restaurar cor padrão">×</button>
                      )}
                      <label className="relative cursor-pointer group">
                        <span className="w-7 h-7 rounded border-2 border-border dark:border-border-dark block transition-transform group-hover:scale-110" style={{ background: block.color || '#cccccc' }} />
                        <input type="color" value={block.color || '#888888'} onChange={e => updateBlock(block.id, { color: e.target.value })} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                      </label>
                      <EyedropperButton compact onPick={color => updateBlock(block.id, { color })} title="Capturar cor do bloco" />
                    </div>
                  </div>
                </>
              )}

              {block.type === 'contact_info' && (
                <>
                  <div><label className={labelCls}>Label do botão E-mail</label><input className={inputCls} value={block.emailLabel || ''} onChange={e => updateBlock(block.id, { emailLabel: e.target.value })} placeholder="E-mail" /></div>
                  <div><label className={labelCls}>Label do botão Telefone</label><input className={inputCls} value={block.phoneLabel || ''} onChange={e => updateBlock(block.id, { phoneLabel: e.target.value })} placeholder="Ligar" /></div>
                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1 leading-relaxed">Exibe botões de e-mail e/ou telefone com os dados preenchidos no cartão. Respeita as configurações de privacidade.</p>
                </>
              )}

              {block.type === 'save_contact' && (
                <>
                  <div><label className={labelCls}>Texto do botão</label><input className={inputCls} value={block.buttonText || ''} onChange={e => updateBlock(block.id, { buttonText: e.target.value })} placeholder="Salvar Contato" /></div>
                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1 leading-relaxed">Gera um arquivo .vcf com os dados do cartão (nome, telefone, e-mail, site) para salvar na agenda do visitante.</p>
                </>
              )}

              {block.type === 'share_card' && (
                <>
                  <div><label className={labelCls}>Texto do botão</label><input className={inputCls} value={block.buttonText || ''} onChange={e => updateBlock(block.id, { buttonText: e.target.value })} placeholder="Enviar Meus Detalhes" /></div>
                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1 leading-relaxed">Exibe opções para o visitante enviar os próprios dados de contato (via WhatsApp, e-mail ou ligação). Usa o telefone/e-mail configurados no cartão.</p>
                </>
              )}
            </div>

            {/* Done button */}
            <button
              onClick={() => setDone(block.id)}
              className="mt-3 w-full py-2 rounded-input bg-action dark:bg-action-dark text-white text-xs font-semibold hover:opacity-90 transition-opacity"
            >
              Pronto
            </button>
          </div>
        );
      })}

      {/* Add area — always at the bottom */}
      {adding ? (
        <div className="border-2 border-dashed border-action dark:border-action-dark rounded-input p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-action dark:text-action-dark">Escolha o tipo de bloco:</p>
            <button onClick={() => setAdding(false)} className="p-1 text-text-muted dark:text-text-muted-dark hover:text-error-text transition-colors"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => addBlock(type)}
                className="flex items-center gap-2 p-3 rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark text-sm font-medium hover:bg-surface dark:hover:bg-surface-dark transition-colors text-left"
              >
                <Icon className="w-4 h-4 text-action dark:text-action-dark shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full py-4 rounded-input border-2 border-dashed border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark text-sm font-medium hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {blocks.length === 0 ? 'Adicionar primeiro bloco' : 'Adicionar bloco'}
        </button>
      )}
    </div>
  );
}

function EyedropperButton({ onPick, title = 'Capturar cor da tela', compact = false }) {
  const [isPicking, setIsPicking] = useState(false);

  const handlePick = async () => {
    if (typeof window === 'undefined' || !window.EyeDropper) {
      window.alert('O conta-gotas não é suportado neste navegador. Use uma versão recente do Chrome ou Edge no computador.');
      return;
    }
    try {
      setIsPicking(true);
      const result = await new window.EyeDropper().open();
      if (result?.sRGBHex) onPick(result.sRGBHex.toLowerCase());
    } catch (error) {
      // AbortError means the user cancelled with Esc; no message is needed.
      if (error?.name !== 'AbortError') console.error('EyeDropper failed:', error);
    } finally {
      setIsPicking(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handlePick}
      disabled={isPicking}
      className={`${compact ? "w-8 h-8 rounded" : "w-11 h-11 rounded-input"} border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark transition-colors flex items-center justify-center shrink-0 disabled:opacity-50`}
      title={title}
      aria-label={title}
    >
      <Pipette className={compact ? "w-4 h-4" : "w-5 h-5"} />
    </button>
  );
}

// ============================================================
// BACKGROUND PICKER — fundo do cartão com biblioteca de presets
// ============================================================

function BgPreviewMini({ bg, label }) {
  const preset = bg?.type === 'preset' ? BG_PRESETS.find(p => p.id === bg.value) : null;
  const thumbStyle =
    bg?.type === 'solid'   ? { background: bg.value } :
    bg?.type === 'gradient' ? { background: 'linear-gradient(' + (bg.angle || 135) + 'deg, ' + bg.from + ', ' + bg.to + ')' } :
    bg?.type === 'preset' && preset ? preset.style : null;
  const thumbLabel =
    bg?.type === 'custom' ? 'Imagem personalizada' :
    bg?.type === 'solid'  ? bg.value :
    bg?.type === 'gradient' ? 'Gradiente ' + bg.from + ' → ' + bg.to :
    bg?.type === 'preset' ? (preset?.label || bg.value) : 'Padrão (cor do tema)';
  return (
    <div className={`flex items-center gap-3 p-2 rounded-input border ${bg ? 'border-action dark:border-action-dark' : 'border-border dark:border-border-dark'} bg-surface dark:bg-surface-dark`}>
      <div className="w-14 h-9 rounded overflow-hidden shrink-0 border border-border dark:border-border-dark">
        {bg?.type === 'custom' && bg.url
          ? <img src={bg.url} alt="" className="w-full h-full object-cover" />
          : thumbStyle
            ? <div className="w-full h-full" style={thumbStyle} />
            : <div className="w-full h-full bg-gradient-to-br from-border to-surface-dark" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-text-primary dark:text-text-primary-dark">{label}</div>
        <div className="text-[10px] text-text-muted dark:text-text-muted-dark truncate font-mono">{thumbLabel}</div>
      </div>
    </div>
  );
}

function BackgroundPicker({ value, onChange, csrfToken, label: pickerLabel = 'Fundo', showImageUpload = true }) {
  const [activeCategory, setActiveCategory] = useState('gradient');
  const [pendingBg, setPendingBg]           = useState(null);
  const [isUploading, setIsUploading]       = useState(false);

  // Solid-color state — kept in sync between the wheel and the text field
  const initHex = value?.type === 'solid' ? value.value : '#3b82f6';
  const [solidHex, setSolidHex]           = useState(initHex);         // always a valid #rrggbb
  const [solidHexInput, setSolidHexInput] = useState(initHex.slice(1)); // raw text without #

  const initialGradient = value?.type === 'gradient' ? value : { from: '#3b82f6', to: '#8b5cf6', angle: 135 };
  const [gradientFrom, setGradientFrom] = useState(initialGradient.from);
  const [gradientTo, setGradientTo] = useState(initialGradient.to);
  const [gradientAngle, setGradientAngle] = useState(initialGradient.angle || 135);

  const categories = [
    { id: 'gradient', label: 'Gradientes' },
    { id: 'dark',     label: 'Escuros'    },
    { id: 'light',    label: 'Claros'     },
    { id: 'texture',  label: 'Texturas'   },
    { id: 'solid',    label: 'Cor Sólida' },
    ...(showImageUpload ? [{ id: 'custom', label: 'Imagem' }] : []),
  ];

  const visiblePresets = BG_PRESETS.filter(p => p.category === activeCategory);

  // ── helpers ──────────────────────────────────────────────
  const normalizeHex = (raw) => {
    const h = raw.trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(h))
      return '#' + h.split('').map(c => c + c).join('');
    if (/^[0-9a-fA-F]{6}$/.test(h))
      return '#' + h;
    return null;
  };

  const isPresetSelected = (id) => {
    if (pendingBg) return pendingBg.type === 'preset' && pendingBg.value === id;
    return value?.type === 'preset' && value.value === id;
  };

  const isSolidSelected = (color) => {
    if (pendingBg) return pendingBg.type === 'solid' && pendingBg.value === color;
    return value?.type === 'solid' && value.value === color;
  };

  // ── handlers ─────────────────────────────────────────────
  const handleApply    = () => { onChange(pendingBg); setPendingBg(null); };
  const handleCancel   = () => setPendingBg(null);
  const handleReset    = () => { onChange(null); setPendingBg(null); };
  const switchCategory = (id) => { setActiveCategory(id); setPendingBg(null); };

  const setSolid = (hex) => {
    setSolidHex(hex);
    setSolidHexInput(hex.slice(1));
    setPendingBg({ type: 'solid', value: hex });
  };

  const handleColorWheel = (e) => setSolid(e.target.value);

  const setGradient = (from, to, angle = gradientAngle) => {
    setGradientFrom(from);
    setGradientTo(to);
    setGradientAngle(Number(angle));
    setPendingBg({ type: 'gradient', from, to, angle: Number(angle) });
  };

  const handleHexInput = (raw) => {
    // Strip leading # if pasted with it
    const cleaned = raw.replace(/^#+/, '');
    setSolidHexInput(cleaned);
    const normalized = normalizeHex(cleaned);
    if (normalized) {
      setSolidHex(normalized);
      setPendingBg({ type: 'solid', value: normalized });
    }
  };

  const handleCustomUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData,
      });
      if (res.ok) {
        const { url } = await res.json();
        onChange({ type: 'custom', url });
        setPendingBg(null);
      }
    } catch { /* silent */ } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{pickerLabel}</h3>
        {value && !pendingBg && (
          <button onClick={handleReset} className="text-xs text-error-text dark:text-error-text-dark hover:opacity-80 transition-opacity">
            Remover fundo
          </button>
        )}
      </div>

      {/* Current / preview thumbnail */}
      <BgPreviewMini bg={pendingBg || value} label={pendingBg ? 'Pré-visualização' : 'Fundo atual'} />

      {/* Category tabs */}
      <div className="flex p-1 bg-surface dark:bg-surface-dark rounded-input gap-0.5 overflow-x-auto no-scrollbar">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => switchCategory(cat.id)}
            className={`flex-1 whitespace-nowrap py-1.5 px-2 text-xs font-medium rounded-button transition-all min-w-[52px] ${
              activeCategory === cat.id
                ? 'bg-card dark:bg-card-dark shadow text-text-primary dark:text-text-primary-dark'
                : 'text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {activeCategory === 'gradient' && (
        <div className="space-y-3 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-text-primary dark:text-text-primary-dark">Gradiente personalizado</p>
            <span className="text-[10px] text-text-muted dark:text-text-muted-dark">Use o conta-gotas em cada ponta</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Cor inicial', value: gradientFrom, set: color => setGradient(color, gradientTo) },
              { label: 'Cor final', value: gradientTo, set: color => setGradient(gradientFrom, color) },
            ].map(item => (
              <div key={item.label}>
                <label className="text-[11px] text-text-muted dark:text-text-muted-dark mb-1 block">{item.label}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={item.value} onChange={e => item.set(e.target.value)} className="w-11 h-11 rounded-input border border-border dark:border-border-dark cursor-pointer shrink-0 p-0.5 bg-transparent" />
                  <input type="text" value={item.value} onChange={e => { const color = normalizeHex(e.target.value); if (color) item.set(color); }} className="min-w-0 flex-1 px-3 py-2.5 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-input text-sm font-mono text-text-primary dark:text-text-primary-dark" />
                  <EyedropperButton onPick={item.set} title={'Capturar ' + item.label.toLowerCase()} />
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="flex justify-between text-[11px] text-text-muted dark:text-text-muted-dark mb-1"><span>Ângulo</span><span>{gradientAngle}°</span></div>
            <input type="range" min="0" max="360" step="1" value={gradientAngle} onChange={e => setGradient(gradientFrom, gradientTo, e.target.value)} className="w-full accent-cyan-600" />
          </div>
          <div className="h-14 rounded-input border border-border dark:border-border-dark" style={{ background: 'linear-gradient(' + gradientAngle + 'deg, ' + gradientFrom + ', ' + gradientTo + ')' }} />
        </div>
      )}

      {/* ── Preset grid (gradient / dark / light / texture) ── */}
      {!['solid','custom'].includes(activeCategory) && (
        <div className="grid grid-cols-3 gap-2">
          {visiblePresets.map(preset => (
            <button
              key={preset.id}
              onClick={() => setPendingBg({ type: 'preset', value: preset.id })}
              className={`relative aspect-video rounded-input overflow-hidden border-2 transition-all hover:scale-[0.97] ${
                isPresetSelected(preset.id)
                  ? 'border-action dark:border-action-dark shadow-lg scale-[0.97] ring-2 ring-action/30 dark:ring-action-dark/30'
                  : 'border-transparent hover:border-border dark:hover:border-border-dark'
              }`}
            >
              <div className="w-full h-full" style={preset.style} />
              <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[9px] px-1.5 py-1 text-center truncate font-medium leading-tight">
                {preset.label}
              </div>
              {isPresetSelected(preset.id) && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-action dark:bg-action-dark rounded-full flex items-center justify-center shadow">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Solid color picker ── */}
      {activeCategory === 'solid' && (
        <div className="space-y-4">
          {/* Quick-pick swatches */}
          <div>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-2">Cores rápidas</p>
            <div className="grid grid-cols-8 gap-1.5">
              {SOLID_PRESETS.map(color => (
                <button
                  key={color}
                  onClick={() => setSolid(color)}
                  title={color}
                  className={`aspect-square rounded-full border-2 transition-all hover:scale-110 ${
                    isSolidSelected(color)
                      ? 'border-action dark:border-action-dark scale-110 shadow-md ring-2 ring-action/30'
                      : 'border-border dark:border-border-dark hover:border-action-dark'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Color wheel + hex text field */}
          <div>
            <p className="text-xs text-text-muted dark:text-text-muted-dark mb-2">Cor personalizada</p>
            <div className="flex items-center gap-2">
              {/* Native color picker (wheel) */}
              <input
                type="color"
                value={solidHex}
                onChange={handleColorWheel}
                className="w-11 h-11 rounded-input border border-border dark:border-border-dark cursor-pointer shrink-0 p-0.5 bg-transparent"
                title="Abrir seletor de cor"
              />
              {/* Hex text field */}
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted dark:text-text-muted-dark text-sm font-mono select-none">#</span>
                <input
                  type="text"
                  value={solidHexInput}
                  onChange={e => handleHexInput(e.target.value)}
                  onPaste={e => {
                    e.preventDefault();
                    handleHexInput(e.clipboardData.getData('text'));
                  }}
                  placeholder="ffffff"
                  maxLength={7}
                  spellCheck={false}
                  className="w-full pl-7 pr-3 py-2.5 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input text-sm font-mono focus:outline-none focus:border-action dark:focus:border-action-dark tracking-widest"
                />
              </div>
              <EyedropperButton onPick={setSolid} title="Capturar cor da tela" />
              {/* Live preview swatch */}
              <div
                className="w-11 h-11 rounded-input border border-border dark:border-border-dark shrink-0 shadow-sm"
                style={{ backgroundColor: solidHex }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Custom image upload ── */}
      {activeCategory === 'custom' && (
        <div className="space-y-3">
          <label className="block w-full aspect-video rounded-input border-thick border-dashed border-border dark:border-border-dark bg-surface dark:bg-surface-dark overflow-hidden cursor-pointer hover:border-action-dark transition-colors relative">
            {value?.type === 'custom' && value.url ? (
              <img src={value.url} alt="fundo personalizado" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-text-muted-subtle dark:text-text-muted-dark gap-2">
                {isUploading
                  ? <RefreshCw className="w-6 h-6 animate-spin" />
                  : <><Upload className="w-6 h-6" /><span className="text-xs">Enviar imagem de fundo</span><span className="text-[10px] opacity-70">JPG, PNG, WebP</span></>
                }
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" disabled={isUploading} onChange={e => handleCustomUpload(e.target.files[0])} />
          </label>
          {value?.type === 'custom' && value.url && (
            <button onClick={handleReset} className="text-xs text-error-text dark:text-error-text-dark hover:opacity-80 transition-opacity">
              Remover imagem personalizada
            </button>
          )}
        </div>
      )}

      {/* Apply / cancel pending */}
      {pendingBg && (
        <div className="flex gap-2 pt-1">
          <button onClick={handleApply} className="flex-1 py-2 bg-action dark:bg-action-dark text-white rounded-input text-sm font-semibold hover:opacity-90 transition-opacity">
            Aplicar fundo
          </button>
          <button onClick={handleCancel} className="py-2 px-4 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark rounded-input text-sm font-medium border border-border dark:border-border-dark hover:bg-card dark:hover:bg-card-dark transition-colors">
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}

// Color Selector Component
function ColorSelector({ selectedColor, onSelect, label, showAuto = false, autoLabel = "Auto (complementary)" }) {
  const colorGradients = {
    indigo: 'from-indigo-500 to-indigo-700',
    blue: 'from-blue-500 to-blue-700',
    rose: 'from-rose-500 to-rose-700',
    emerald: 'from-emerald-500 to-emerald-700',
    slate: 'from-slate-500 to-slate-700',
    purple: 'from-purple-500 to-purple-700',
    cyan: 'from-cyan-500 to-cyan-700',
    teal: 'from-teal-500 to-teal-700',
    orange: 'from-orange-500 to-orange-700',
    pink: 'from-pink-500 to-pink-700',
    violet: 'from-violet-500 to-violet-700',
    fuchsia: 'from-fuchsia-500 to-fuchsia-700',
    amber: 'from-amber-500 to-amber-700',
    lime: 'from-lime-500 to-lime-700',
    green: 'from-green-500 to-green-700',
    yellow: 'from-yellow-500 to-yellow-700',
    red: 'from-red-500 to-red-700'
  };

  return (
    <div>
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{label}</label>}
      <div className="grid grid-cols-8 gap-2">
        {showAuto && (
          <button
            onClick={() => onSelect(null)}
            className={`w-10 h-10 rounded-full border-thick flex flex-col items-center justify-center text-[10px] font-medium transition-all ${
              selectedColor === null 
                ? 'border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 ring-2 ring-cyan-200 dark:ring-cyan-800' 
                : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark hover:border-border-dark dark:hover:border-border-dark'
            }`}
          >
            <span>Auto</span>
          </button>
        )}
        {TAILWIND_COLORS.map(color => (
          <button
            key={color}
            onClick={() => onSelect(color)}
            className={`w-10 h-10 rounded-full border-thick transition-all overflow-hidden ${
              selectedColor === color
                ? 'border-cyan-500 dark:border-cyan-400 ring-2 ring-cyan-200 dark:ring-cyan-800 scale-105'
                : 'border-border dark:border-border-dark hover:border-border-dark dark:hover:border-border-dark'
            }`}
            title={color.charAt(0).toUpperCase() + color.slice(1)}
          >
            <div className={`w-full h-full bg-gradient-to-br ${colorGradients[color] || 'from-gray-500 to-gray-700'}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function UserManagementView({ apiCall, userRole, onBack, showAlert, showConfirm }) {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchInvitations();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/auth/me`);
      if (res.ok) {
        const userData = await res.json();
        setCurrentUserId(userData.id);
      }
    } catch (e) {
      console.error('Failed to fetch current user:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        if (showAlert) showAlert('Falha ao carregar usuários', 'error');
      }
    } catch (e) {
      console.error('Failed to fetch users:', e);
      if (showAlert) showAlert('Erro ao carregar usuários', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`);
      if (res.ok) {
        const data = await res.json();
        setInvitations(data.invitations);
      }
    } catch (e) {
      console.error('Failed to fetch invitations:', e);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert('E-mail e senha são obrigatórios', 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert('A senha deve ter pelo menos 8 caracteres', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      
      if (res.ok) {
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member' });
        fetchUsers();
      } else {
        // Try to parse error response
        let errorMessage = 'Falha ao criar usuário';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = `Falha ao criar usuário: ${res.status} ${res.statusText}`;
        }
        if (showAlert) showAlert(errorMessage, 'error');
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || 'Erro ao criar usuário. Tente novamente.';
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert('E-mail é obrigatório', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        const data = await res.json();
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });

        // Show warning if email failed
        if (data.status === 'failed' && showAlert) {
          showAlert('Convite criado, mas o envio do e-mail falhou. Você pode tentar novamente na lista de convites abaixo.', 'warning');
        }

        // Reload invitations list
        fetchInvitations();
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Falha ao enviar convite', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro ao enviar convite', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        if (showAlert) showAlert('Cargo do usuário atualizado com sucesso', 'success');
        fetchUsers();
        setEditingUserId(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Falha ao atualizar cargo', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro ao atualizar cargo', 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        `Tem certeza de que deseja excluir permanentemente ${userEmail}? Isso excluirá o usuário e todos os seus cartões. Esta ação não pode ser desfeita.`,
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('Usuário excluído com sucesso', 'success');
              fetchUsers();
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || 'Falha ao excluir usuário', 'error');
            }
          } catch (e) {
            if (showAlert) showAlert('Erro ao excluir usuário', 'error');
          }
        },
        'Excluir Usuário',
        'Excluir',
        'Cancelar'
      );
    }
  };

  const handleRetryInvitation = async (invitationId) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}/retry`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        if (showAlert) {
          showAlert(data.success ? 'E-mail de convite enviado com sucesso' : 'Falha ao enviar e-mail de convite',
                    data.success ? 'success' : 'error');
        }
        fetchInvitations();
      }
    } catch (e) {
      if (showAlert) showAlert('Error retrying invitation', 'error');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (showConfirm) {
      showConfirm(
        'Tem certeza de que deseja excluir este convite?',
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('Convite excluído', 'success');
              fetchInvitations();
            }
          } catch (e) {
            if (showAlert) showAlert('Erro ao excluir convite', 'error');
          }
        },
        'Excluir Convite',
        'Excluir',
        'Cancelar'
      );
    }
  };

  if (userRole !== 'owner') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">Acesso Negado</h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">Você não tem permissão para gerenciar usuários.</p>
          <button onClick={onBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            Voltar ao Painel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="w-full bg-card dark:bg-card-dark border-b border-border dark:border-border-dark">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5"/>
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">Contas</h1>
              <p className="text-sm text-text-secondary dark:text-text-muted-dark">Gerencie contas que podem criar e gerenciar cartões</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateUserModal(true)}
              className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-full text-sm font-bold flex items-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark"
            >
              <Plus className="w-4 h-4" /> Nova Conta
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-text-secondary dark:text-text-muted-dark">Carregando usuários...</div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">Contas cadastradas</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Contas com acesso para criar e gerenciar cartões</p>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {users.length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    Nenhum usuário encontrado
                  </div>
                ) : (
                  users.map((user) => {
                    const isCurrentUser = user.id === currentUserId;
                    return (
                      <div key={user.id} className="p-6 flex items-center justify-between hover:bg-surface dark:hover:bg-surface-dark/50 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                              <User className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                            </div>
                            <div>
                              <div className="font-medium text-text-primary dark:text-text-primary-dark flex items-center gap-2">
                                {user.email}
                                {isCurrentUser && (
                                  <span className="text-xs text-text-muted dark:text-text-muted-dark">(Você)</span>
                                )}
                              </div>
                              <div className="text-sm text-text-muted dark:text-text-muted-dark">
                                {user.role === 'owner' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                                    Proprietário
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">
                                    Membro
                                  </span>
                                )}
                                <span className="ml-2">Entrou em {new Date(user.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {isCurrentUser ? (
                            <span className="text-xs text-text-muted dark:text-text-muted-dark italic">Não é possível modificar você mesmo</span>
                          ) : editingUserId === user.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={user.role}
                                onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                                className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                              >
                                <option value="member">Membro</option>
                                <option value="owner">Proprietário</option>
                              </select>
                              <button
                                onClick={() => setEditingUserId(null)}
                                className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingUserId(user.id)}
                                className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                              >
                                <Edit3 className="w-3 h-3" /> Alterar Cargo
                              </button>
                              <button
                                onClick={() => handleRemoveUser(user.id, user.email)}
                                className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" /> Remover
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pending Invitations Section */}
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">Convites Pendentes</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Convites enviados mas ainda não aceitos</p>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    Nenhum convite pendente
                  </div>
                ) : (
                  invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).map(inv => (
                    <div key={inv.id} className="p-6 flex items-center justify-between hover:bg-surface dark:hover:bg-surface-dark/50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-primary-dark">
                              {inv.email}
                            </div>
                            <div className="text-sm text-text-muted dark:text-text-muted-dark flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${
                                inv.status === 'sent' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                inv.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                inv.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                inv.status === 'expired' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300' :
                                'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              }`}>
                                {inv.status === 'sent' && 'Enviado'}
                                {inv.status === 'failed' && 'Falhou'}
                                {inv.status === 'pending' && 'Pendente'}
                                {inv.status === 'expired' && 'Expirado'}
                              </span>
                              {inv.role === 'owner' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                                  Proprietário
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">
                                  Membro
                                </span>
                              )}
                              {inv.invited_by_email && (
                                <span className="text-xs">convidado por {inv.invited_by_email}</span>
                              )}
                              <span className="text-xs">• {new Date(inv.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(inv.status === 'failed' || inv.status === 'pending') && (
                          <button
                            onClick={() => handleRetryInvitation(inv.id)}
                            className="px-3 py-1.5 text-sm bg-action dark:bg-action-dark text-white rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center gap-1"
                            title="Tentar novamente o envio do e-mail"
                          >
                            <RefreshCw className="w-3 h-3" /> Tentar novamente
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteInvitation(inv.id)}
                          className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                          title="Excluir convite"
                        >
                          <Trash2 className="w-3 h-3" /> Excluir
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Criar Novo Usuário</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">E-mail</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Senha</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Cargo</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="member">Membro</option>
                  <option value="owner">Proprietário</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUser({ email: '', password: '', role: 'member' });
                }}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : isSuccess ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Criando...' : 'Criar Usuário'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function InvitationAcceptance({ apiCall, showAlert, API_ENDPOINT }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    // Don't fetch if invitation has already been accepted
    if (hasAccepted) return;

    const fetchInvitation = async () => {
      try {
        const res = await apiCall(`${API_ENDPOINT}/invitations/${token}`, { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setInvitation(data);
          setError(null);
        } else {
          const errorData = await res.json().catch(() => ({}));
          // If already accepted, treat as success and redirect
          if (errorData.error && errorData.error.includes('already been accepted')) {
            if (showAlert) showAlert('Bem-vindo! Sua conta foi criada.', 'success');
            setTimeout(() => navigate('/'), 500);
          } else {
            setError(errorData.error || 'Convite não encontrado ou expirado');
            setInvitation(null);
          }
        }
      } catch (e) {
        setError('Erro ao carregar convite');
        setInvitation(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    }
  }, [token, apiCall, API_ENDPOINT, hasAccepted, showAlert, navigate]);

  const validatePasswords = () => {
    setPasswordError(null);

    if (!password) {
      setPasswordError('Senha é obrigatória');
      return false;
    }

    if (password.length < 8) {
      setPasswordError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/invitations/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        const data = await res.json();
        // Set flag immediately to prevent re-fetching during redirect window
        setHasAccepted(true);
        if (showAlert) showAlert('Bem-vindo! Sua conta foi criada.', 'success');
        // Redirect to dashboard
        setTimeout(() => navigate('/'), 1000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setPasswordError(errorData.error || 'Falha ao aceitar convite');
      }
    } catch (e) {
      setPasswordError('Erro ao aceitar convite');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-action dark:text-action-dark mx-auto mb-4" />
          <p className="text-text-primary dark:text-text-primary-dark">Carregando convite...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
        <div className="max-w-md w-full bg-card dark:bg-card-dark rounded-card shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Convite Inválido</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">{error || 'Este link de convite não é válido ou expirou.'}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-medium hover:bg-action-hover dark:hover:bg-action-hover-dark"
          >
            Ir para Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
      <div className="max-w-md w-full">
        <div className="bg-card dark:bg-card-dark rounded-card shadow-lg p-8 mb-4">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Entrar em {invitation.organisationName}</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
            Você foi convidado a entrar como <span className="font-semibold capitalize">{invitation.role}</span>
          </p>

          <form onSubmit={handleAcceptInvitation} className="space-y-4">
            {/* Email display */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">E-mail</label>
              <input
                type="email"
                value={invitation.email}
                disabled
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark"
              />
            </div>

            {/* Password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder="Digite a senha (mín. 8 caracteres)"
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Confirm password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">Confirmar Senha</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder="Confirme a senha"
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Error message */}
            {passwordError && (
              <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-input text-red-700 dark:text-red-200 text-sm">
                {passwordError}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Criando Conta...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Aceitar Convite
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-text-muted dark:text-text-muted-dark text-center mt-4">
            Ao aceitar este convite, você concorda em entrar na organização
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsView({ settings, setSettings, onBack, onSave, apiCall, showAlert, showConfirm, availableThemes = [], setAvailableThemes, csrfToken = '' }) {
  // Initialize local settings with extracted base colors from existing data
  const initializeColorData = (colors) => {
    return colors.map(color => {
      let hexBase, baseColor, colorType;
      
      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');
      
      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }
      
      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);
      
      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;
      
      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const [localSettings, setLocalSettings] = useState({
    ...settings,
    theme_colors: initializeColorData(settings.theme_colors || []),
    theme_variant: settings.theme_variant || 'default',
    // Initialize override toggles - default to true if undefined, but preserve false
    allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
    allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
    allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
    allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true
  });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOrganisationNameOpen, setIsOrganisationNameOpen] = useState(false);
  const [isCustomizationControlsOpen, setIsCustomizationControlsOpen] = useState(false);
  const [isThemeColorsOpen, setIsThemeColorsOpen] = useState(false);
  const [isAppThemesOpen, setIsAppThemesOpen] = useState(false);
  const [isCustomThemesOpen, setIsCustomThemesOpen] = useState(false);
  const [isSmtpOpen, setIsSmtpOpen] = useState(false);
  // SMTP local state (password field is always empty on load; only sent if user types a new one)
  const [smtpFields, setSmtpFields] = useState({
    smtp_host: settings.smtp_host || '',
    smtp_port: settings.smtp_port || '587',
    smtp_secure: settings.smtp_secure || false,
    smtp_user: settings.smtp_user || '',
    smtp_password: '',
    smtp_from: settings.smtp_from || '',
  });
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(settings.smtp_password_set || false);

  const applyThemePreset = (variant) => {
    setEditingColorIndex(null);
    const preset = THEME_PRESETS?.[variant];
    setLocalSettings(prev => ({
      ...prev,
      theme_variant: preset ? variant : 'custom',
      theme_colors: preset ? initializeColorData(preset) : prev.theme_colors
    }));
  };

  // Re-initialize when settings prop changes
  useEffect(() => {
    setLocalSettings({
      ...settings,
      theme_colors: initializeColorData(settings.theme_colors || []),
      theme_variant: settings.theme_variant || localSettings.theme_variant || 'swiish',
      // Initialize override toggles - default to true if undefined, but preserve false
      allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
      allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
      allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
      allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true
    });
  }, [settings]);

  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  useEffect(() => {
    const variant = localSettings.theme_variant || settings.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [localSettings.theme_variant, settings.theme_variant]);

  const handleSave = async () => {
    setIsSaving(true);
    const startTime = Date.now();
    try {
      // Save hex-only color structure
      const colorsToSave = localSettings.theme_colors.map(color => {
        return {
          name: color.name,
          colorType: color.colorType || 'standard',
          baseColor: color.baseColor || null, // null for custom, Tailwind name for standard
          hexBase: color.hexBase || '#4f46e5',
          hexSecondary: color.hexSecondary || color.hexBase || '#4f46e5',
          gradientStyle: color.gradientStyle || `linear-gradient(135deg, ${color.hexBase || '#4f46e5'}, ${color.hexSecondary || color.hexBase || '#4f46e5'})`,
          buttonStyle: color.buttonStyle || color.hexBase || '#4f46e5',
          linkStyle: color.linkStyle || color.hexBase || '#4f46e5',
          textStyle: color.textStyle || color.hexBase || '#4f46e5'
        };
      });

      const res = await apiCall(`${API_ENDPOINT}/admin/settings`, {
        method: 'POST',
        body: JSON.stringify({
          default_organisation: localSettings.default_organisation,
          theme_colors: colorsToSave,
          theme_variant: localSettings.theme_variant || 'swiish',
          allow_theme_customisation: Boolean(localSettings.allow_theme_customisation),
          allow_image_customisation: Boolean(localSettings.allow_image_customisation),
          allow_links_customisation: Boolean(localSettings.allow_links_customisation),
          allow_privacy_customisation: Boolean(localSettings.allow_privacy_customisation),
          smtp_host: smtpFields.smtp_host,
          smtp_port: smtpFields.smtp_port,
          smtp_secure: smtpFields.smtp_secure,
          smtp_user: smtpFields.smtp_user,
          ...(smtpFields.smtp_password ? { smtp_password: smtpFields.smtp_password } : {}),
          smtp_from: smtpFields.smtp_from,
        })
      });

      // Ensure at least 500ms has passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 500) {
        await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
      }

      if (res.ok) {
        // Refetch from server to get the latest saved data
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        await onSave();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Save failed:', errorData);
        if (showAlert) showAlert('Falha ao salvar configurações', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Erro ao salvar configurações', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const addColor = () => {
    const baseColor = 'indigo';
    const hexBase = getTailwindColorHex(baseColor, 600);
    const complementary = getComplementaryColor(baseColor);
    const hexSecondary = getTailwindColorHex(complementary, 600);
    
    const newColor = {
      name: `color${localSettings.theme_colors.length + 1}`,
      colorType: 'standard',
      baseColor: baseColor,
      hexBase: hexBase,
      hexSecondary: hexSecondary,
      gradientStyle: `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`,
      buttonStyle: hexBase,
      linkStyle: hexBase,
      textStyle: hexBase
    };
    setLocalSettings(prev => {
      const newColors = [...prev.theme_colors, newColor];
      setEditingColorIndex(newColors.length - 1);
      return {
        ...prev,
        theme_colors: newColors,
        theme_variant: 'custom'
      };
    });
  };

  const updateColor = (colorIndex, field, value) => {
    setLocalSettings(prev => {
      const updated = prev.theme_colors.map((c, idx) => {
        if (idx === colorIndex) {
          const updatedColor = { ...c, [field]: value };
          const currentColorType = updatedColor.colorType || c.colorType || 'standard';
          
          // Handle colorType changes
          if (field === 'colorType') {
            if (value === 'standard' && !updatedColor.baseColor) {
              // Switching to standard - set default baseColor if missing
              updatedColor.baseColor = c.baseColor || 'indigo';
              updatedColor.hexBase = c.hexBase || getTailwindColorHex(updatedColor.baseColor, 600);
            } else if (value === 'custom') {
              // Switching to custom - clear baseColor
              updatedColor.baseColor = null;
            }
          }
          
          // For standard colors: when baseColor changes, update hexBase
          if (currentColorType === 'standard' && field === 'baseColor') {
            updatedColor.hexBase = getTailwindColorHex(value, 600);
          }
          
          // For custom colors: when hexBase changes, ensure colorType is custom
          if (field === 'hexBase') {
            if (currentColorType !== 'custom') {
              updatedColor.colorType = 'custom';
              updatedColor.baseColor = null;
            }
          }
          
          // Handle hexBase
          let hexBase = updatedColor.hexBase || c.hexBase;
          let baseColor = updatedColor.baseColor || c.baseColor;
          
          // If hexBase is missing, generate it
          if (!hexBase) {
            if (baseColor) {
              hexBase = getTailwindColorHex(baseColor, 600);
              updatedColor.hexBase = hexBase;
            } else {
              hexBase = '#4f46e5'; // Default
              updatedColor.hexBase = hexBase;
            }
          }
          
          // Handle hexSecondary
          let hexSecondary;
          
          if (currentColorType === 'standard') {
            // Standard colors always use auto-complementary
            const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
            hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
            updatedColor.hexSecondary = hexSecondary;
          } else if (currentColorType === 'custom') {
            // Custom colors: allow manual setting
            if (field === 'hexSecondary') {
              // User is manually setting hexSecondary
              hexSecondary = value || null;
              updatedColor.hexSecondary = hexSecondary;
            } else if (field === 'baseColor' || field === 'hexBase' || field === 'colorType') {
              // When baseColor/hexBase changes, only auto-generate if hexSecondary is not manually set
              const existingHexSecondary = c.hexSecondary;
              if (!existingHexSecondary || existingHexSecondary === '') {
                // Auto-generate complementary
                const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
                hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
                updatedColor.hexSecondary = hexSecondary;
              } else {
                // Keep existing manual value
                hexSecondary = existingHexSecondary;
                updatedColor.hexSecondary = hexSecondary;
              }
            } else {
              // Keep existing value
              hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
              updatedColor.hexSecondary = hexSecondary;
            }
          } else {
            // Fallback
            hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
            updatedColor.hexSecondary = hexSecondary;
          }
          
          // Always regenerate inline styles when relevant fields change
          if (field === 'baseColor' || field === 'colorType' || field === 'hexBase' || field === 'hexSecondary') {
            const finalHexSecondary = hexSecondary || hexBase;
            updatedColor.gradientStyle = `linear-gradient(135deg, ${hexBase}, ${finalHexSecondary})`;
            updatedColor.buttonStyle = hexBase;
            updatedColor.linkStyle = hexBase;
            updatedColor.textStyle = hexBase;
          }
          
          return updatedColor;
        }
        return c;
      });
      return { ...prev, theme_colors: updated, theme_variant: 'custom' };
    });
  };

  const removeColor = (colorIndex) => {
    if (localSettings.theme_colors.length <= 1) {
      if (showAlert) showAlert('Você deve ter pelo menos uma cor', 'error');
      return;
    }
    const colorName = localSettings.theme_colors[colorIndex]?.name;
    if (showConfirm) {
      showConfirm(
        `Excluir cor "${colorName}"?`,
        () => {
          setLocalSettings(prev => ({
            ...prev,
            theme_colors: prev.theme_colors.filter((c, idx) => idx !== colorIndex),
            theme_variant: 'custom'
          }));
          if (editingColorIndex === colorIndex) {
            setEditingColorIndex(null);
          } else if (editingColorIndex !== null && editingColorIndex > colorIndex) {
            // Adjust index if we deleted a color before the one being edited
            setEditingColorIndex(editingColorIndex - 1);
          }
        },
        'Excluir Cor',
        'Excluir',
        'Cancelar'
      );
    }
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 bg-card dark:bg-card-dark border-r border-border dark:border-border-dark h-auto lg:h-screen overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-border-subtle dark:border-border-dark flex items-center justify-between bg-card dark:bg-card-dark sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5"/>
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">Configurações da Organização</h1>
            </div>
          </div>
          <button 
            onClick={handleSave} 
            disabled={isSaving}
            className="px-5 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : isSuccess ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>

        <div className="flex-1 p-6 space-y-6">
          <div className="space-y-6">
            {/* Organisation Name Section */}
            <div>
              <button
                onClick={() => setIsOrganisationNameOpen(!isOrganisationNameOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Nome da Organização</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Este nome da organização será aplicado a todos os cartões. Os usuários não podem alterar isso.</p>
                </div>
                {isOrganisationNameOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isOrganisationNameOpen && (
                <div className="mt-4 p-4 bg-surface dark:bg-card-dark/50 rounded-input">
                  <input
                    type="text"
                    value={localSettings.default_organisation || ''}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, default_organisation: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    placeholder="Nome da Organização"
                  />
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Organization Override Toggles */}
            <div className="space-y-6">
              <button
                onClick={() => setIsCustomizationControlsOpen(!isCustomizationControlsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Controles de Personalização do Usuário</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Controle o que os usuários da sua organização podem personalizar em seus cartões.</p>
                </div>
                {isCustomizationControlsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isCustomizationControlsOpen && (
                <div className="mt-4 space-y-4">
                  {/* Theme Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Personalização do Tema</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Controle se os usuários podem escolher cores do tema para seus cartões.</p>
                </div>
                <Toggle
                  label="Permitir que usuários escolham cores do tema"
                  description="Quando habilitado, os usuários podem selecionar da paleta de cores do tema da organização. Quando desabilitado, os cartões usarão a primeira cor da paleta."
                  checked={localSettings.allow_theme_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_theme_customisation: checked }))}
                />
              </div>

              {/* Image Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Personalização de Imagens</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Controle se os usuários podem fazer upload de avatares e banners personalizados.</p>
                </div>
                <Toggle
                  label="Permitir que usuários façam upload de avatares e banners personalizados"
                  description="Quando habilitado, os usuários podem fazer upload de suas próprias imagens. Quando desabilitado, os uploads de imagens serão bloqueados."
                  checked={localSettings.allow_image_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_image_customisation: checked }))}
                />
              </div>

              {/* Links Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Personalização de Links</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Controle se os usuários podem adicionar links personalizados aos seus cartões.</p>
                </div>
                <Toggle
                  label="Permitir que usuários adicionem links personalizados"
                  description="Quando habilitado, os usuários podem adicionar links personalizados. Quando desabilitado, a seção de links será bloqueada."
                  checked={localSettings.allow_links_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_links_customisation: checked }))}
                />
              </div>

              {/* Privacy Settings Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Configurações de Privacidade</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Controle se os usuários podem alterar as configurações de privacidade nos seus cartões.</p>
                </div>
                <Toggle
                  label="Permitir que usuários alterem configurações de privacidade"
                  description="Quando habilitado, os usuários podem modificar opções de privacidade (exigir interação, ofuscação, bloquear robôs). Quando desabilitado, as configurações de privacidade serão bloqueadas."
                  checked={localSettings.allow_privacy_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_privacy_customisation: checked }))}
                />
              </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* App Themes */}
            <div>
              <button
                onClick={() => setIsAppThemesOpen(!isAppThemesOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Temas do App</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Escolha a aparência da interface do app (fundos, elementos, botões).</p>
                </div>
                {isAppThemesOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isAppThemesOpen && (
                <div className="mt-4 space-y-3">
                  {[
                    { id: 'swiish', title: 'ANRVA', desc: 'Tema ANRVA Presença Digital com cores e texturas.' },
                    { id: 'minimal', title: 'Minimal', desc: 'Preto/branco, poucos cinzas, sem texturas, acentos apenas de borda.' },
                  ].map(opt => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-3 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark transition-colors cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="app-theme"
                        className="mt-1"
                        value={opt.id}
                        checked={(localSettings.theme_variant || 'swiish') === opt.id}
                        onChange={(e) => applyThemePreset(e.target.value)}
                      />
                      <div>
                        <div className="font-medium text-text-primary dark:text-text-primary-dark">{opt.title}</div>
                        <p className="text-sm text-text-secondary dark:text-text-muted-dark">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Custom Themes Upload */}
            <div>
              <button
                onClick={() => setIsCustomThemesOpen(!isCustomThemesOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Temas Personalizados</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Faça upload de um arquivo .zip com um tema customizado para os cartões.</p>
                </div>
                {isCustomThemesOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              {isCustomThemesOpen && (
                <div className="mt-4 space-y-4">
                  {(availableThemes.filter(t => t.id !== 'swiish' && t.id !== 'minimal')).length > 0 && (
                    <div className="space-y-2">
                      {availableThemes.filter(t => t.id !== 'swiish' && t.id !== 'minimal').map(t => (
                        <div key={t.id} className="flex items-center justify-between p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark">
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1">
                              {(t.swatchColors || []).map((c,i) => (
                                <span key={i} className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: c }} />
                              ))}
                            </div>
                            <span className="text-sm font-medium text-text-primary dark:text-text-primary-dark">{t.displayName}</span>
                            <code className="text-xs text-text-muted dark:text-text-muted-dark bg-surface dark:bg-main-dark px-1.5 py-0.5 rounded">{t.id}</code>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Remover o tema "${t.displayName}"?`)) return;
                              try {
                                const r = await apiCall(`${API_ENDPOINT}/admin/themes/${t.id}`, { method: 'DELETE', headers: { 'x-csrf-token': csrfToken } });
                                if (r.ok) {
                                  delete RUNTIME_THEMES[t.id];
                                  setAvailableThemes(prev => prev.filter(x => x.id !== t.id));
                                  showAlert('Tema removido com sucesso.', 'success');
                                }
                              } catch {}
                            }}
                            className="p-1.5 rounded text-error hover:bg-error-bg dark:hover:bg-error-bg-dark transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mb-3">
                      Envie um arquivo <code className="bg-surface dark:bg-main-dark px-1 rounded">.zip</code> contendo um <code className="bg-surface dark:bg-main-dark px-1 rounded">theme.json</code> no formato ANRVA.
                    </p>
                    <label className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-border dark:border-border-dark rounded-input cursor-pointer hover:bg-surface dark:hover:bg-surface-dark transition-colors">
                      <Upload className="w-8 h-8 text-text-muted dark:text-text-muted-dark" />
                      <span className="text-sm text-text-secondary dark:text-text-muted-dark">Clique ou arraste um <strong>.zip</strong> aqui</span>
                      <input
                        type="file"
                        accept=".zip"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('theme', file);
                          try {
                            const r = await fetch(`${API_ENDPOINT}/admin/themes/upload`, {
                              method: 'POST',
                              headers: { 'x-csrf-token': csrfToken },
                              credentials: 'include',
                              body: fd,
                            });
                            const json = await r.json();
                            if (r.ok) {
                              RUNTIME_THEMES[json.theme.id] = json.theme;
                              setAvailableThemes(prev => {
                                const filtered = prev.filter(x => x.id !== json.theme.id);
                                return [...filtered, { id: json.theme.id, displayName: json.theme.displayName || json.theme.name, swatchColors: json.theme.swatchColors || [] }];
                              });
                              showAlert(`Tema "${json.theme.displayName || json.theme.name}" instalado com sucesso!`, 'success');
                            } else {
                              showAlert(json.error || 'Erro ao instalar tema.', 'error');
                            }
                          } catch {
                            showAlert('Falha no upload. Verifique o arquivo e tente novamente.', 'error');
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Profile Colors */}
            <div>
              <button
                onClick={() => setIsThemeColorsOpen(!isThemeColorsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Cores do Perfil</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Gerencie a paleta de cores disponível para os cartões dos usuários.</p>
                </div>
                {isThemeColorsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isThemeColorsOpen && (
                <div className="mt-4 space-y-4">
                  <div className="flex justify-end">
                      <button 
                        onClick={addColor}
                        className="text-sm font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/30 rounded-badge hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Adicionar Cor
                      </button>
                    </div>

              <div className="space-y-4">
                {localSettings.theme_colors?.map((color, index) => (
                  <div key={index} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                    {editingColorIndex === index ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-text-primary dark:text-text-primary-dark">Editando: {color.name}</h4>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingColorIndex(null)}
                              className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark text-text-primary dark:text-text-primary-dark"
                            >
                              Concluído
                            </button>
                            <button
                              onClick={() => removeColor(index)}
                              className="px-3 py-1 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark border border-error-border dark:border-error-border-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark"
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                        
                        <Input
                          label="Nome da Cor"
                          value={color.name}
                          onChange={(v) => updateColor(index, 'name', v)}
                        />

                        <div>
                          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Tipo de Cor</label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateColor(index, 'colorType', 'standard')}
                              className={`flex-1 px-4 py-2 rounded-button border transition-all ${
                                (color.colorType || 'standard') === 'standard'
                                  ? 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-500 dark:border-cyan-400 text-cyan-700 dark:text-cyan-300 font-medium'
                                  : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                              }`}
                            >
                              Cores Padrão
                            </button>
                            <button
                              onClick={() => updateColor(index, 'colorType', 'custom')}
                              className={`flex-1 px-4 py-2 rounded-button border transition-all ${
                                color.colorType === 'custom'
                                  ? 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-500 dark:border-cyan-400 text-cyan-700 dark:text-cyan-300 font-medium'
                                  : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                              }`}
                            >
                              Cores Personalizadas
                            </button>
                          </div>
                        </div>

                        {(color.colorType || 'standard') === 'standard' ? (
                          <>
                            <ColorSelector
                              label="Cor Base"
                              selectedColor={color.baseColor || 'indigo'}
                              onSelect={(selected) => updateColor(index, 'baseColor', selected)}
                            />
                            <p className="text-xs text-text-muted dark:text-text-muted-dark">Secundária: Auto (complementar)</p>
                          </>
                        ) : (
                          <>
                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Cor Base</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={color.hexBase || '#4f46e5'}
                                  onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                  className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={color.hexBase || '#4f46e5'}
                                  onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                  placeholder="#4f46e5"
                                  className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                />
                                <EyedropperButton onPick={picked => updateColor(index, "hexBase", picked)} title="Capturar cor base" />
                              </div>
                            </div>

                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Cor Secundária (opcional)</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={color.hexSecondary || color.hexBase || '#7c3aed'}
                                  onChange={(e) => updateColor(index, 'hexSecondary', e.target.value)}
                                  className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={color.hexSecondary || ''}
                                  onChange={(e) => updateColor(index, 'hexSecondary', e.target.value || null)}
                                  placeholder="Deixe em branco para auto (complementar)"
                                  className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                />
                                <EyedropperButton onPick={picked => updateColor(index, "hexSecondary", picked)} title="Capturar cor secundária" />
                              </div>
                              <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Deixe em branco para usar a cor auto (complementar)</p>
                            </div>
                          </>
                        )}

                        {/* Preview of generated gradient */}
                        <div>
                          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Prévia</label>
                          <div 
                            className="w-full h-16 rounded-container overflow-hidden"
                            style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                          />
                        </div>

                        {/* Collapsible advanced view */}
                        <details className="text-sm">
                          <summary className="cursor-pointer text-text-secondary dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark font-medium mb-2">
                            Avançado: Estilos Gerados
                          </summary>
                          <div className="bg-card dark:bg-surface-dark p-3 rounded-container border border-border dark:border-border-dark space-y-2 text-xs font-mono">
                            <div><span className="text-text-muted dark:text-text-muted-dark">Estilo Gradiente:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.gradientStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Estilo Botão:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.buttonStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Estilo Link:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.linkStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Estilo Texto:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.textStyle || 'N/A'}</span></div>
                          </div>
                        </details>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-full"
                            style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                          />
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-primary-dark">
                              {color.baseColor ? (color.baseColor.charAt(0).toUpperCase() + color.baseColor.slice(1)) : color.name}
                            </div>
                            <div className="text-xs text-text-muted dark:text-text-muted-dark">
                              {color.baseColor ? 'Padrão' : 'Personalizado'} (Secundária: Auto)
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingColorIndex(index)}
                          className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1 text-text-primary dark:text-text-primary-dark"
                        >
                          <Edit3 className="w-3 h-3" /> Editar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* SMTP Configuration */}
            <div>
              <button
                onClick={() => setIsSmtpOpen(!isSmtpOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">E-mail (SMTP)</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">
                    Servidor de envio para convites e redefinição de senha.
                    {smtpPasswordSet && <span className="ml-1 text-xs text-green-600 dark:text-green-400">● Senha salva</span>}
                  </p>
                </div>
                {isSmtpOpen ? <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark flex-shrink-0" />}
              </button>
              {isSmtpOpen && (
                <div className="mt-4 p-4 bg-surface dark:bg-card-dark/50 rounded-input space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-text-secondary dark:text-text-muted-dark mb-1 block">Host SMTP</label>
                      <input type="text" value={smtpFields.smtp_host} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_host: e.target.value }))}
                        className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                        placeholder="smtp.gmail.com" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-text-secondary dark:text-text-muted-dark mb-1 block">Porta</label>
                      <input type="text" value={smtpFields.smtp_port} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_port: e.target.value }))}
                        className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                        placeholder="587" />
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={smtpFields.smtp_secure} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_secure: e.target.checked }))} className="sr-only peer" />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-action dark:peer-checked:bg-action-dark"></div>
                        <span className="ml-2 text-xs text-text-secondary dark:text-text-muted-dark">TLS / SSL</span>
                      </label>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-text-secondary dark:text-text-muted-dark mb-1 block">Usuário / E-mail SMTP</label>
                      <input type="text" value={smtpFields.smtp_user} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_user: e.target.value }))}
                        className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                        placeholder="noreply@suaempresa.com.br" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-text-secondary dark:text-text-muted-dark mb-1 block">
                        Senha {smtpPasswordSet && <span className="text-green-600 dark:text-green-400">(já salva — deixe em branco para manter)</span>}
                      </label>
                      <input type="password" value={smtpFields.smtp_password} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_password: e.target.value }))}
                        className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                        placeholder={smtpPasswordSet ? '••••••••' : 'Senha SMTP'} autoComplete="new-password" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-text-secondary dark:text-text-muted-dark mb-1 block">Remetente (From)</label>
                      <input type="text" value={smtpFields.smtp_from} onChange={(e) => setSmtpFields(p => ({ ...p, smtp_from: e.target.value }))}
                        className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                        placeholder='ANRVA Presença Digital <noreply@suaempresa.com.br>' />
                    </div>
                  </div>
                  <p className="text-xs text-text-muted dark:text-text-muted-dark">Entra em vigor imediatamente ao salvar, sem reiniciar o servidor.</p>
                </div>
              )}
            </div>

          </div>
        </div>
        <AnrvaFooter />
      </div>

      <div className="hidden lg:flex w-1/2 bg-main dark:bg-main-dark items-center justify-center p-10">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full border border-border dark:border-border-dark relative z-10 isolate">
          <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Prévia</h3>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-2">Organização Padrão:</div>
              <div className="font-medium text-text-primary dark:text-text-primary-dark">{localSettings.default_organisation || 'Não definido'}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-3">Efeitos de Cor:</div>
              {(() => {
                const previewColor = editingColorIndex !== null && localSettings.theme_colors?.[editingColorIndex]
                  ? localSettings.theme_colors[editingColorIndex]
                  : localSettings.theme_colors?.[0];
                
                return (
                  <div className="space-y-3">
                    {/* Text example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Texto</div>
                      <div 
                        className="text-lg font-medium"
                        style={{ color: previewColor?.textStyle || '#4f46e5' }}
                      >
                        Texto de Exemplo
                      </div>
                    </div>
                    
                    {/* Link example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Link</div>
                      <a 
                        href="#" 
                        className="inline-block px-4 py-2 rounded-input border border-border dark:border-border-dark transition-all hover:opacity-80"
                        style={{ color: previewColor?.linkStyle || '#4f46e5' }}
                        onClick={(e) => e.preventDefault()}
                      >
                        Link de Exemplo
                      </a>
                    </div>
                    
                    {/* Button example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Botão</div>
                      <button 
                        className="px-4 py-2 rounded-full font-medium text-white shadow-md transition-transform active:scale-[0.98]"
                        style={{ backgroundColor: previewColor?.buttonStyle || '#4f46e5' }}
                      >
                        Botão de Exemplo
                      </button>
                    </div>
                    
                    {/* Gradient background example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Fundo Gradiente</div>
                      <div 
                        className="w-full h-12 rounded-container overflow-hidden"
                        style={{ background: previewColor?.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
